import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { toast } from '../lib/toast';
import { errorBus } from '../lib/errorBus';
import InspectorPanel from '../components/InspectorPanel';
import BomPanel from '../components/BomPanel';
import ErrorBoundary from '../components/ErrorBoundary';
import TerritoryBusinessCase, { type ShowModes } from '../components/TerritoryBusinessCase';
import ExpansionStudyPanel, { type ExpansionZone } from '../components/ExpansionStudyPanel';
import type { BomResult } from '../types/bom';
import { useSegmentDraw } from '../hooks/useSegmentDraw';
import { usePolygonDraw } from '../hooks/usePolygonDraw';
import { usePolygonEdit } from '../hooks/usePolygonEdit';
import { nodeCirclePaint, segmentPaint } from '../lib/plant-styles';
import { BASEMAPS, type BasemapId } from '../lib/map-basemaps';
import { DEFAULT_SPECS, type PlantSpecs } from '../components/DesignPanel';
import type {
  BuildingFeatureCollection, EditorMode, NodeType, Placement,
  Plant, PlantGraph, Project, SegmentType, SelectedItem,
} from '../types/plant';

const WV_CENTER: [number, number] = [-80.45, 38.6];

type Tab = 'manual' | 'layers';

// Build stamp visible in the status bar so you can confirm you have the
// latest bundle (bumped on each deploy).
const BUILD_STAMP = 'milestone-build 0.5.0';

/** Fetch a static cache JSON, falling back to API endpoint on 404/error.
 * Static file is served by nginx directly (no Node, no PG round-trip);
 * API endpoint is the original /api/bead/* route used as fallback when
 * the cache file is missing (e.g., after a clean rebuild).
 */
async function fetchLayer(staticUrl: string, apiUrl: string): Promise<any> {
  try {
    const r = await fetch(staticUrl, { credentials: 'omit' });
    if (r.ok) {
      const ct = r.headers.get('content-type') || '';
      if (ct.includes('application/json')) return await r.json();
    }
  } catch { /* fall through to API path */ }
  // 2026-06-04 retry API fallback up to 3 times on transient network failure.
  const BACKOFF = [200, 600, 1800];
  for (let i = 0; i <= 3; i++) {
    try {
      const r2 = await fetch(apiUrl, { credentials: 'include' });
      if (r2.ok) return await r2.json();
      if (![502, 503, 504, 408].includes(r2.status) || i >= 3) return null;
    } catch (e: any) {
      if (e?.name === 'AbortError' || i >= 3) return null;
    }
    await new Promise((res) => setTimeout(res, BACKOFF[i]));
  }
  return null;
}

export default function MapPageWrapped() {
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[FiberForge] MapPage mounted', BUILD_STAMP);
  }, []);
  return (
    <ErrorBoundary>
      <MapPageInner />
    </ErrorBoundary>
  );
}

function MapPageInner() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const qc = useQueryClient();
  const nav = useNavigate();

  const [tab, setTab] = useState<Tab>('manual');
  const [basemap, setBasemap] = useState<BasemapId>('satellite');
  const [mapReady, setMapReady] = useState(false);
  const [styleVersion, setStyleVersion] = useState(0);  // bumps after basemap swap — triggers all data-push effects to re-run
  const [showBuildings, setShowBuildings] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [selected, setSelected] = useState<SelectedItem>(null);
  const [banner, setBanner] = useState<{ kind: 'error' | 'info' | 'success'; text: string } | null>(null);
  const [generatingDrops, setGeneratingDrops] = useState(false);
  const [lastDropResult, setLastDropResult] = useState<{ created: number; skipped: number; considered: number } | null>(null);

  // Manual-mode state
  const [mode, setMode] = useState<EditorMode>('select');
  const [pendingNodeType, setPendingNodeType] = useState<NodeType>('handhole');
  const [pendingSegmentType, setPendingSegmentType] = useState<SegmentType>('distribution');
  const [pendingPlacement, setPendingPlacement] = useState<Placement>('buried');

  // Design-mode state
  const [specs, setSpecs] = useState<PlantSpecs>(DEFAULT_SPECS);
  const [polygonMode, setPolygonMode] = useState(false);
  const importInputRef = useRef<HTMLInputElement | null>(null);
  const [importingBoundary, setImportingBoundary] = useState(false);
  const [importedLocations, setImportedLocations] = useState<{ name: string; folder: string; lng: number; lat: number }[]>(() => { try { return JSON.parse(localStorage.getItem('ff.importedLocations') || '[]'); } catch { return []; } });
  const [ringMode, setRingMode] = useState<'idle' | 'pick'>('idle');
  const [ringDesign, setRingDesign] = useState<{ coords: number[][]; hub: [number, number] | null; miles: number; straightMiles: number; sites: number; orderedSites: any[]; routed?: boolean } | null>(null);
  const [ringCostPerMile, setRingCostPerMile] = useState(45000);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<number[]>([]);
  const selectModeRef = useRef(false);
  const fitSigRef = useRef('');
  const [routingProgress, setRoutingProgress] = useState<{ done: number; total: number } | null>(null);
  const ringRunRef = useRef(0);
  const [polygonRing, setPolygonRing] = useState<[number, number][] | null>(null);
  const [placingOlt, setPlacingOlt] = useState(false);
  const [oltLngLat, setOltLngLat] = useState<[number, number] | null>(null);
  const [designBusy, setDesignBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [designStatus, setDesignStatus] = useState<string | null>(null);
  const [animatingDesign, setAnimatingDesign] = useState(false);  // progressive segment reveal after auto-design

  // Draw-segment state (manual)
  const [drawFromNode, setDrawFromNode] = useState<{ id: string; lng: number; lat: number } | null>(null);
  const pendingToNodeRef = useRef<{ to_node_id: string; from_node_id: string } | null>(null);

  const { data: projects = [] } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: () => api.get('/projects'),
  });
  useEffect(() => {
    if (!selectedProjectId && projects[0]) setSelectedProjectId(projects[0].id);
  }, [projects, selectedProjectId]);

  const { data: plants = [] } = useQuery<Plant[]>({
    queryKey: ['plants', selectedProjectId],
    queryFn: () => selectedProjectId ? api.get(`/plants/project/${selectedProjectId}`) : Promise.resolve([]),
    enabled: !!selectedProjectId,
  });
  const plant = plants[0] ?? null;

  // New projects don't have a plant yet — auto-create on first select.
  useEffect(() => {
    if (!selectedProjectId) return;
    if (plants.length > 0) return;
    api.post('/plants/ensure', { project_id: selectedProjectId })
      .then(() => qc.invalidateQueries({ queryKey: ['plants', selectedProjectId] }))
      .catch((e) => toast.error('Could not create plant: ' + (e as Error).message));
  }, [selectedProjectId, plants.length, qc]);

  const { data: graph, refetch: refetchGraph } = useQuery<PlantGraph>({
    queryKey: ['plant-graph', plant?.id],
    queryFn: () => plant
      ? api.get(`/plants/${plant.id}/graph`)
      : Promise.resolve({ nodes:{type:'FeatureCollection',features:[]}, segments:{type:'FeatureCollection',features:[]}, drops:{type:'FeatureCollection',features:[]}, stats:{node_count:0,segment_count:0,drop_count:0,feeder_m:0,distribution_m:0,drop_m:0} } as any),
    enabled: !!plant,
  });

  // Passings buffer + BSL dots. Only loads when plant has segments.
  const { data: passings, refetch: refetchPassings } = useQuery<{ buffer: any; passings: any; buffer_ft: number; count: number }>({
    queryKey: ['plant-passings', plant?.id],
    queryFn: () => plant ? api.get(`/plants/${plant.id}/passings`) : Promise.reject(),
    enabled: !!plant && !!graph && graph.stats.segment_count > 0,
    staleTime: 60_000,
  });

  // BEAD eligible BSL ids (per project) — used to color dots.
  const { data: beadEligible } = useQuery<{ location_ids: string[] }>({
    queryKey: ['bead-eligible', selectedProjectId],
    queryFn: () => selectedProjectId
      ? api.get(`/bead/project/${selectedProjectId}/eligible-bsls`)
      : Promise.resolve({ location_ids: [] }),
    enabled: !!selectedProjectId,
  });

  // BOM + economics — recomputes on demand.
  const { data: bom, refetch: refetchBom } = useQuery<BomResult>({
    queryKey: ['bom', plant?.id],
    queryFn: () => plant ? api.get(`/bom/plant/${plant.id}`) : Promise.reject(),
    enabled: !!plant && !!graph && graph.stats.segment_count > 0,
    staleTime: 30_000,
  });

  // ---- Expansion Studies (Layer 3 / private-capex zones) ----
  const [expansionMode, setExpansionMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editRingCommitted, setEditRingCommitted] = useState<[number, number][]>([]);
  const [previewPassings, setPreviewPassings] = useState<number | null>(null);
  const [savedNeedsReanalyze, setSavedNeedsReanalyze] = useState<boolean>(false);
  const [staleSince, setStaleSince] = useState<number | null>(null);
  const [selectedZoneIds, setSelectedZoneIds] = useState<string[]>([]);
  const [layerVis, setLayerVis] = useState<Record<string, boolean>>({
    // 2026-05-27: defaults flipped to false for plant + buildings.
    // The Builder map is for BEAD compliance / territory analysis, not
    // plant design. Existing plant nodes (OLTs etc) and building footprints
    // were rendering by default even when the user is focused on a
    // territory polygon hundreds of miles away from the plant data,
    // creating "stale data" confusion. User can re-enable via the new
    // checkboxes in the controls panel.
    buildings: false,
    towers: true,
    inactive_zones: true,
    passings: true,
    plant: false,
    backbone: true,
    bead_awards: false,
  });
  const [beadProvider, setBeadProvider] = useState<string>('Aristotle');
  const [beadState, setBeadState] = useState<string>('TX');
  const [activeZoneId, setActiveZoneIdRaw] = useState<string | null>(null);
  // Scope the cluster picker to one territory plan at a time. 'all' = show
  // every plan's clusters. Default: the plan of the current active zone or the
  // most-recent plan for this project.
  const [activeTerritoryPlanId, setActiveTerritoryPlanIdRaw] = useState<string | 'all' | null>(null);
  const setActiveTerritoryPlanId = useCallback((id: string | 'all' | null) => {
    try {
      const pid = (window as any).__currentProjectId || null;
      if (pid) {
        if (id) localStorage.setItem(`ff.activeTerritoryPlanId.${pid}`, id);
        else localStorage.removeItem(`ff.activeTerritoryPlanId.${pid}`);
      }
    } catch {}
    setActiveTerritoryPlanIdRaw(id);
  }, []);
  // Wrapper persists every change
  const setActiveZoneId = useCallback((id: string | null) => {
    try {
      const pid = (window as any).__currentProjectId || null;
      if (pid) {
        if (id) localStorage.setItem(`ff.activeZoneId.${pid}`, id);
        else localStorage.removeItem(`ff.activeZoneId.${pid}`);
      }
    } catch {}
    setActiveZoneIdRaw(id);
  }, []);
  const [selectedBackhaulType, setSelectedBackhaulType] = useState<string | null>(null);
  const [wirelessHops, setWirelessHops] = useState<any[] | null>(null);

  // ==== Territory plan state + queries (merged from TerritoryPlanPage) ====
  const [terrDrawMode, setTerrDrawMode] = useState<boolean>(false);
  const [terrGrossMargin, setTerrGrossMargin] = useState<number>(0.72);
  const [terrBuildIndices, setTerrBuildIndices] = useState<number[]>([]);
  const [terrShowModes, setTerrShowModes] = useState<ShowModes>({ fiber_build: true, wireless_ptp: true, ride_along: true });
  const [showVbProjects, setShowVbProjects] = useState<boolean>(true);
  const [showBeadProjects, setShowBeadProjects] = useState<boolean>(false);
  const [showRdofLocations, setShowRdofLocations] = useState<boolean>(false);
  // 2026-06-01: FCC ASR tower overlay (bbox-scoped to viewport)
  const [showTowers, setShowTowers] = useState<boolean>(false);
  const [showSoil, setShowSoil] = useState<boolean>(false);
  const [soilLoading, setSoilLoading] = useState<boolean>(false);
  const [mergeModal, setMergeModal] = useState<{ open: boolean; name: string }>({ open: false, name: '' });
  const [headendPlacingMode, setHeadendPlacingMode] = useState<boolean>(false);
  const [headendSuggestion, setHeadendSuggestion] = useState<{ lng: number; lat: number; distance_km: number } | null>(null);
  const [terrAnalysisRunning, setTerrAnalysisRunning] = useState<boolean>(false);
  // User-selected plan id; overrides the default "most recent" active plan.
  // Lets the operator switch between scenarios in the Scenarios panel.
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  // Deep-link: if the URL has ?planId=X (from Plans of Record), jump to it
  // once the plan list is loaded.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qPlanId = params.get('planId');
    if (!qPlanId) return;
    (async () => {
      try {
        const plan: any = await api.get(`/territory/plans/${qPlanId}`);
        if (plan?.project_id && plan.project_id !== selectedProjectId) {
          setSelectedProjectId(plan.project_id);
        }
        setSelectedPlanId(qPlanId);
      } catch (e: any) {
        // Plan was deleted or isn't accessible — surface it and clean the URL
        toast.error('That plan no longer exists. It may have been deleted.');
        const u = new URL(window.location.href);
        u.searchParams.delete('planId');
        window.history.replaceState({}, '', u.toString());
        // Also invalidate the committed-plans cache so the user sees truth
        qc.invalidateQueries({ queryKey: ['committed-plans'] });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Territory polygon edit mode — lets the user drag vertices of the active
  // plan's polygon and either save in place or branch to a new scenario.
  const [territoryEditMode, setTerritoryEditMode] = useState<boolean>(false);
  // Multi-select for side-by-side scenario comparison
  const [compareSelection, setCompareSelection] = useState<Set<string>>(new Set());

  const { data: terrPlans = [] } = useQuery<any[]>({
    queryKey: ['territory-plans', selectedProjectId],
    queryFn: () => selectedProjectId ? api.get(`/territory/project/${selectedProjectId}`) : Promise.resolve([]),
    enabled: !!selectedProjectId,
  });
  // Active plan = user-selected (respects deep-link to a committed plan),
  // else the most recent non-rejected / non-failed / non-committed plan.
  // Committed plans live in Plans of Record — they don't auto-pick back
  // into the Territory workspace.
  const activePlan = useMemo(() => {
    if (!terrPlans.length) return null;
    if (selectedPlanId) {
      const m = terrPlans.find((p: any) => p.id === selectedPlanId);
      if (m) return m;
    }
    const viable = terrPlans.filter((p: any) =>
      p.status !== 'rejected' && p.status !== 'failed' && p.status !== 'committed'
    );
    return viable[0] ?? null;
  }, [terrPlans, selectedPlanId]);
  const { data: terrPlan, refetch: refetchTerrPlan } = useQuery<any>({
    queryKey: ['territory-plan', activePlan?.id],
    queryFn: () => activePlan?.id ? api.get(`/territory/plans/${activePlan.id}`) : Promise.reject(),
    enabled: !!activePlan?.id,
    refetchInterval: (q) => {
      const d: any = q.state.data;
      const phase = d?.progress?.phase;
      // 2026-05-30 Tier-2: 5s during analyze (was 2s). pg_stat_statements
      // showed 158 expansion-zones + 160 terrPlans polls in a 7-min build
      // adding ~30s of PG load AND competing with per-cluster queries for
      // connections. 5s polling is still snappy in the UI and cuts the
      // overhead by ~60%.
      return (terrAnalysisRunning
              || d?.status === 'analyzing'
              || d?.status === 'clustering'
              || phase === 'computing_backbone')
        ? 5000 : false;
    },
  });

  // 2026-05-27: full-plant snapshot for the active plan. Loads every
  // segment/node/drop across all this plan's expansion_zones in one shot
  // so the Builder map can render a committed Plan of Record as a true
  // frozen snapshot (polygon + clusters + backbone + FTTH + nodes + drops)
  // without the user having to click into each cluster individually.
  // Only fires when plan is complete or committed — pointless before then.
  const { data: planFullPlant } = useQuery<{
    segments: any; nodes: any; drops: any;
    stats: { segment_count: number; node_count: number; drop_count: number };
  }>({
    queryKey: ['plan-full-plant', activePlan?.id, terrPlan?.status],
    queryFn: () => activePlan?.id
      ? api.get(`/territory/plans/${activePlan.id}/full-plant`)
      : Promise.reject(),
    enabled: !!activePlan?.id
      && (terrPlan?.status === 'complete' || terrPlan?.status === 'committed'),
    staleTime: 60_000,
  });
  // 2026-05-30: push fatal banner the moment a plan flips to 'failed'.
  // Stays on screen until user dismisses — so a failure that happens while
  // they're AFK is impossible to miss when they come back.
  // 2026-06-05: ALSO auto-dismiss banner when plan recovers (status flips
  // off 'failed' via re-analyze) — otherwise the red banner stays forever
  // even after the plan successfully completes.
  useEffect(() => {
    if (!terrPlan) return;
    if (terrPlan.status !== 'failed') {
      // Recovery path: clear any stale failure banner for this plan.
      errorBus.dismissByKey(`plan-status::${terrPlan.id}`);
      return;
    }
    const prog = (terrPlan as any).progress ?? {};
    const reason = prog.reason
      ?? `Analyze aborted at ${prog.phase ?? 'unknown phase'} (${prog.done ?? '?'}/${prog.total ?? '?'} clusters done)`;
    const lastCluster = prog.current ? ` Last cluster: ${prog.current}.` : '';
    errorBus.push({
      severity: 'fatal',
      title: `Plan "${terrPlan.name}" FAILED — ${reason}`,
      detail: `Plan ID: ${terrPlan.id}\nProgress: ${JSON.stringify(prog, null, 2)}${lastCluster}\n\nCheck the API logs for the underlying crash. Use the ✕ on the plan in the Scenarios panel to clear it once you've investigated.`,
      source: 'plan-status',
      dedupeKey: `plan-status::${terrPlan.id}`,
    });
  }, [terrPlan?.id, terrPlan?.status]);

  // 2026-06-02 Push a warning banner when the active plan is paused waiting
  // for GeoHub to recover. Backend auto-resumes on recovery (server.ts
  // onGeohubUp callback); the banner just makes it visible so users don't
  // wonder why their build is sitting at 70% for an hour.
  useEffect(() => {
    if (!terrPlan) return;
    const prog = (terrPlan as any).progress ?? {};
    if (prog.phase !== 'paused_geohub_down') {
      // Recovery path: clear any stale pause banner for this plan.
      errorBus.dismissByKey(`plan-paused::${terrPlan.id}`);
      return;
    }
    const pausedAt = prog.paused_at ? new Date(prog.paused_at).toLocaleString() : 'unknown';
    errorBus.push({
      severity: 'warning',
      title: 'Plan "' + terrPlan.name + '" PAUSED \u2014 GeoHub PG unreachable',
      detail: 'Build paused at ' + pausedAt + '. The server will automatically resume the analyze when the GeoHub connection comes back. No action required \u2014 but if it stays paused for >5 min, check that the GeoHub database is online.\n\nProgress snapshot: ' + JSON.stringify(prog, null, 2),
      source: 'plan-paused',
      dedupeKey: 'plan-paused::' + terrPlan.id,
    });
  }, [terrPlan?.id, terrPlan?.status, (terrPlan as any)?.progress?.phase]);

  // Single effect that handles BOTH plan-switch and status-change. When
  // the active plan changes (id or status), seed buildIndices to all
  // clusters if it's a complete plan, otherwise clear them. This prevents
  // both (a) stale indices leaking across plans (the "40 of 9 zones" bug)
  // and (b) empty indices wiping the map when switching between two
  // already-complete plans (the "$679k / 0 of 43 zones" bug).
  useEffect(() => {
    const len = terrPlan?.summary?.per_cluster?.length ?? 0;
    if (terrPlan?.status === 'complete' && len > 0) {
      setTerrBuildIndices(Array.from({ length: len }, (_, i) => i));
      setTerrAnalysisRunning(false);
      qc.invalidateQueries({ queryKey: ['territory-plans'] });
    } else if (terrPlan?.status === 'analyzing' || terrPlan?.status === 'clustering') {
      setTerrAnalysisRunning(true);
    } else if (!terrPlan) {
      setTerrBuildIndices([]);
    }
  }, [terrPlan?.id, terrPlan?.status, terrPlan?.summary?.per_cluster?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const createTerrMut = useMutation({
    mutationFn: (polygon: [number, number][]) => {
      const ring = polygon[0][0] === polygon[polygon.length - 1][0] ? polygon : [...polygon, polygon[0]];
      return api.post('/territory/plans', { project_id: selectedProjectId, name: `Territory ${terrPlans.length + 1}`  /* will be auto-renamed by backend after polygon analysis */, polygon: ring });
    },
    onSuccess: (res: any) => {
      toast.success(`Detected ${res.cluster_count} zones`);
      qc.invalidateQueries({ queryKey: ['territory-plans'] });
    },
    onError: (e: any) => toast.error(`Territory create failed: ${e?.message ?? e}`),
  });

  // Import a territory boundary from a KML/KMZ/GeoJSON file (e.g. Google My Maps export) -> create a territory.
  const handleImportBoundary = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!selectedProjectId) { toast.error('Select a project first'); return; }
    setImportingBoundary(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/territory/import-boundary', { method: 'POST', body: fd, credentials: 'include' });
      if (!res.ok) { const j = await res.json().catch(() => ({} as any)); throw new Error((j as any).error || res.statusText); }
      const data = await res.json();
      if (data.kind === 'locations' || (Array.isArray(data.points) && data.points.length && !data.ring)) {
        setImportedLocations(data.points);
        try { localStorage.setItem('ff.importedLocations', JSON.stringify(data.points)); } catch { /* */ }
        const fl = (data.folders || []).map((f: any) => `${f.name}`).join(' · ');
        toast.success(`Dropped ${data.pointCount} locations on the map (${fl}).`);
      } else if (data.ring && data.ring.length >= 4) {
        toast.info(`Imported boundary from "${data.name}" — building territory…`);
        createTerrMut.mutate(data.ring as [number, number][]);
      } else {
        throw new Error('No usable points or boundary found in file');
      }
    } catch (err: any) {
      toast.error('Import failed: ' + (err?.message || err));
    } finally {
      setImportingBoundary(false);
    }
  };

  // Render imported facility pins — colored by folder; click to inspect, or tap to select in select-mode.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const locs = importedLocations;
    const selSet = new Set(selectedKeys);
    const fc: any = { type: 'FeatureCollection', features: locs.map((p, i) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: [p.lng, p.lat] }, properties: { name: p.name, folder: p.folder, idx: i, selected: selSet.has(i) } })) };
    const folders = Array.from(new Set(locs.map((p) => p.folder || '(none)')));
    const palette = ['#ef4444', '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];
    const colorExpr: any = ['match', ['get', 'folder']];
    folders.forEach((f, i) => { colorExpr.push(f, palette[i % palette.length]); });
    colorExpr.push('#64748b');
    if (!map.getSource('imported-locations')) {
      map.addSource('imported-locations', { type: 'geojson', data: fc } as any);
      map.addLayer({ id: 'imported-locations', type: 'circle', source: 'imported-locations', paint: { 'circle-radius': ['case', ['get', 'selected'], 9, 6], 'circle-color': locs.length ? colorExpr : '#64748b', 'circle-stroke-width': ['case', ['get', 'selected'], 3, 1.5], 'circle-stroke-color': ['case', ['get', 'selected'], '#fde047', '#ffffff'] } as any });
      map.addLayer({ id: 'imported-locations-label', type: 'symbol', source: 'imported-locations', minzoom: 12, layout: { 'text-field': ['get', 'name'], 'text-size': 10, 'text-offset': [0, 1.1], 'text-anchor': 'top', 'text-optional': true }, paint: { 'text-color': '#0f172a', 'text-halo-color': '#ffffff', 'text-halo-width': 1.5 } });
      map.on('mouseenter', 'imported-locations', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'imported-locations', () => { map.getCanvas().style.cursor = ''; });
      map.on('click', 'imported-locations', (e: any) => {
        const f = e.features?.[0]; if (!f) return;
        if (selectModeRef.current) {
          const idx = f.properties.idx as number;
          setSelectedKeys((prev) => prev.includes(idx) ? prev.filter((x) => x !== idx) : [...prev, idx]);
        } else {
          new maplibregl.Popup({ closeButton: true }).setLngLat(e.lngLat).setHTML(`<div style="font:13px system-ui;max-width:220px"><b>${f.properties.name}</b><br/><span style="color:#64748b">${f.properties.folder || ''}</span></div>`).addTo(map);
        }
      });
    } else {
      (map.getSource('imported-locations') as maplibregl.GeoJSONSource).setData(fc);
      if (locs.length) map.setPaintProperty('imported-locations', 'circle-color', colorExpr);
    }
    const sig = locs.length + ':' + (locs[0] ? `${locs[0].lng},${locs[0].lat}` : '');
    if (locs.length && sig !== fitSigRef.current) {
      fitSigRef.current = sig;
      try { const b = new maplibregl.LngLatBounds(); locs.forEach((p) => b.extend([p.lng, p.lat] as [number, number])); if (!b.isEmpty()) map.fitBounds(b, { padding: 90, maxZoom: 13, duration: 600 }); } catch { /* */ }
    }
  }, [importedLocations, mapReady, selectedKeys]);

  // ---- Fiber RING design: protected (dual-fed) loop through imported facilities ----
  const computeRing = (pts: { lng: number; lat: number; name: string; folder: string }[], origin?: [number, number]) => {
    const R = 3958.8, toR = (d: number) => (d * Math.PI) / 180;
    const hav = (a: number[], b: number[]) => { const dLat = toR(b[1] - a[1]), dLng = toR(b[0] - a[0]); const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a[1])) * Math.cos(toR(b[1])) * Math.sin(dLng / 2) ** 2; return 2 * R * Math.asin(Math.sqrt(s)); };
    const ptCoords = pts.map((p) => [p.lng, p.lat]);
    const nodes: number[][] = origin ? [origin, ...ptCoords] : ptCoords;
    const n = nodes.length;
    if (n < 3) return null;
    const used = new Array(n).fill(false); let order = [0]; used[0] = true;
    for (let k = 1; k < n; k++) { const last = order[order.length - 1]; let best = -1, bd = Infinity; for (let j = 0; j < n; j++) { if (used[j]) continue; const d = hav(nodes[last], nodes[j]); if (d < bd) { bd = d; best = j; } } order.push(best); used[best] = true; }
    const ringLen = (o: number[]) => { let s = 0; for (let i = 0; i < o.length; i++) s += hav(nodes[o[i]], nodes[o[(i + 1) % o.length]]); return s; };
    let improved = true, pass = 0;
    while (improved && pass < 20) { improved = false; pass++; for (let i = 1; i < n - 1; i++) { for (let k = i + 1; k < n; k++) { const cand = order.slice(0, i).concat(order.slice(i, k + 1).reverse(), order.slice(k + 1)); if (ringLen(cand) < ringLen(order) - 1e-9) { order = cand; improved = true; } } } }
    const coords = order.map((i) => nodes[i]); coords.push(nodes[order[0]]);
    const straight = ringLen(order);
    const orderedSites = origin ? order.slice(1).map((i) => pts[i - 1]) : order.map((i) => pts[i]);
    return { coords, hub: origin || null, miles: straight * 1.3, straightMiles: straight, sites: pts.length, orderedSites };
  };

  // Snap a ring's straight legs onto the real GeoHub road network (pgRouting), stitched into one path.
  const haversineM = (a: number[], b: number[]) => { const R = 6371000, toR = (d: number) => (d * Math.PI) / 180; const dLat = toR(b[1] - a[1]), dLng = toR(b[0] - a[0]); const s = Math.sin(dLat / 2) ** 2 + Math.cos(toR(a[1])) * Math.cos(toR(b[1])) * Math.sin(dLng / 2) ** 2; return 2 * R * Math.asin(Math.sqrt(s)); };
  const routeLeg = async (a: number[], b: number[]) => {
    const straightM = haversineM(a, b);
    const corridor = Math.min(5000, Math.max(800, Math.round(straightM * 0.4)));
    try {
      const res = await fetch('/api/map/route-along-roads', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ from: { lng: a[0], lat: a[1] }, to: { lng: b[0], lat: b[1] }, corridor_m: corridor }) });
      if (res.ok) { const r = await res.json(); if (r?.geometry?.coordinates?.length >= 2) return { coords: r.geometry.coordinates as number[][], meters: Number(r.length_m) || straightM, ok: true }; }
    } catch { /* fall through to straight */ }
    return { coords: [a, b], meters: straightM, ok: false };
  };
  const snapRingToRoads = async (d: { coords: number[][] } | null) => {
    if (!d) return;
    const myRun = ++ringRunRef.current;
    const nodes = d.coords;
    const legs: number[][][] = [];
    for (let i = 0; i < nodes.length - 1; i++) legs.push([nodes[i], nodes[i + 1]]);
    if (!legs.length) return;
    setRoutingProgress({ done: 0, total: legs.length });
    const results: { coords: number[][]; meters: number; ok: boolean }[] = new Array(legs.length);
    let done = 0, idx = 0, failed = 0;
    const worker = async () => { while (idx < legs.length) { if (ringRunRef.current !== myRun) return; const my = idx++; const r = await routeLeg(legs[my][0], legs[my][1]); if (ringRunRef.current !== myRun) return; results[my] = r; if (!r.ok) failed++; done++; setRoutingProgress({ done, total: legs.length }); } };
    await Promise.all(Array.from({ length: Math.min(3, legs.length) }, () => worker()));
    if (ringRunRef.current !== myRun) return;
    let coords: number[][] = [], meters = 0;
    results.forEach((r, i) => { if (!r) return; if (i === 0) coords.push(...r.coords); else coords.push(...r.coords.slice(1)); meters += r.meters; });
    setRoutingProgress(null);
    setRingDesign((prev) => (prev ? { ...prev, coords, miles: meters / 1609.34, routed: true } : prev));
    if (failed) toast.warning(`${failed} of ${legs.length} legs had no road path (kept straight) — likely outside loaded road data.`);
    else toast.success('Ring routed along roads.');
  };
  const finalizeRing = (d: any) => { setRingDesign(d); const legs = d.coords.length - 1; if (legs > 30) toast.info(`Routing ${legs} legs along roads \u2014 this can take a minute.`); snapRingToRoads(d); };

  useEffect(() => {
    const map = mapRef.current;
    if (!map || ringMode !== 'pick') return;
    map.getCanvas().style.cursor = 'crosshair';
    const handler = (e: any) => {
      const origin: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      const d = computeRing(importedLocations, origin);
      if (!d) { toast.error('Need at least 2 imported locations to ring'); setRingMode('idle'); return; }
      finalizeRing(d);
      setRingMode('idle');
      toast.success(`Protected ring: ${d.miles.toFixed(1)} fiber-mi across ${d.sites} sites`);
    };
    map.once('click', handler);
    return () => { map.off('click', handler); if (mapRef.current) mapRef.current.getCanvas().style.cursor = ''; };
  }, [ringMode, importedLocations]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const fc: any = (ringDesign && ringDesign.routed)
      ? { type: 'FeatureCollection', features: [ { type: 'Feature', geometry: { type: 'LineString', coordinates: ringDesign.coords }, properties: {} }, ...(ringDesign.hub ? [{ type: 'Feature', geometry: { type: 'Point', coordinates: ringDesign.hub }, properties: { hub: true } }] : []) ] }
      : { type: 'FeatureCollection', features: [] };
    if (!map.getSource('fiber-ring')) {
      map.addSource('fiber-ring', { type: 'geojson', data: fc } as any);
      map.addLayer({ id: 'fiber-ring-line', type: 'line', source: 'fiber-ring', filter: ['==', ['geometry-type'], 'LineString'], paint: { 'line-color': '#f59e0b', 'line-width': 3 } } as any, map.getLayer('imported-locations') ? 'imported-locations' : undefined);
      map.addLayer({ id: 'fiber-ring-hub', type: 'circle', source: 'fiber-ring', filter: ['==', ['get', 'hub'], true], paint: { 'circle-radius': 9, 'circle-color': '#111827', 'circle-stroke-color': '#f59e0b', 'circle-stroke-width': 3 } } as any);
    } else {
      (map.getSource('fiber-ring') as maplibregl.GeoJSONSource).setData(fc);
    }
    if (map.getLayer('fiber-ring-line')) {
      map.setPaintProperty('fiber-ring-line', 'line-color', ringDesign?.routed ? '#f59e0b' : '#9ca3af');
      map.setPaintProperty('fiber-ring-line', 'line-width', ringDesign?.routed ? 3.5 : 2);
    }
  }, [ringDesign, mapReady, routingProgress]);

  useEffect(() => { selectModeRef.current = selectMode; }, [selectMode]);

  const regenBackboneMut = useMutation({
    mutationFn: async (overrideIndices?: number[]) => {
      if (!activePlan?.id) throw new Error('no active plan');
      // If TerritoryBusinessCase computed effective indices (union of
      // private-checked and BEAD-checked), use those; else fall back to
      // the basic buildIndices.
      const indices = overrideIndices && overrideIndices.length > 0 ? overrideIndices : terrBuildIndices;
      return api.post(`/territory/plans/${activePlan.id}/recompute-backbone`, {
        include_cluster_indices: indices,
      });
    },
    onSuccess: () => {
      toast.success('Backhaul regenerated');
      qc.invalidateQueries({ queryKey: ['territory-plan', activePlan?.id] });
    },
    onError: (e: any) => toast.error(`Backhaul failed: ${e?.message ?? e}`),
  });

  const renamePlanMut = useMutation({
    mutationFn: (args: { planId: string; name: string }) =>
      api.patch(`/territory/plans/${args.planId}`, { name: args.name }),
    onSuccess: () => {
      toast.success('Renamed');
      qc.invalidateQueries({ queryKey: ['territory-plans'] });
    },
    onError: (e: any) => toast.error(`Rename failed: ${e?.message ?? e}`),
  });

  const commitPlanMut = useMutation({
    mutationFn: (planId: string) => api.post(`/territory/plans/${planId}/commit`, {}),
    onSuccess: () => {
      // Keep the plan selected so the user can keep inspecting / adding
      // scenarios around it. Committed plans are normally hidden from
      // the Scenarios list, but the (p.id === selectedPlanId) bypass
      // keeps the just-committed one visible.
      toast.success('Committed. Added to Plans of Record.');
      // Exit polygon-edit mode — the polygon is locked once committed
      // (backend also enforces this).
      setTerritoryEditMode(false);
      qc.invalidateQueries({ queryKey: ['territory-plans'] });
      qc.invalidateQueries({ queryKey: ['committed-plans'] });
    },
    onError: (e: any) => toast.error(`Commit failed: ${e?.message ?? e}`),
  });

  // Promote a plan's design straight into As-Built (no Vetro) + auto-detect permits.
  const promoteAsBuiltMut = useMutation({
    mutationFn: (planId: string) => api.post(`/as-built/promote-from-plan`, { plan_id: planId }),
    onSuccess: (data: any) => {
      toast.success(`Promoted to As-Built — ${data?.applied?.created ?? 0} segments, ${data?.permits_auto_detected ?? 0} permits auto-detected.`);
      qc.invalidateQueries({ queryKey: ['territory-plans'] });
    },
    onError: (e: any) => toast.error(`Promote failed: ${e?.message ?? e}`),
  });

  // 2026-05-27: terminate a stuck analyze. Backend sets status='failed'
  // with progress.aborted=true. UI moves on; if the background worker
  // somehow finishes later it'll just overwrite back to 'complete'.
  const abortPlanMut = useMutation({
    mutationFn: (planId: string) => api.post(`/territory/plans/${planId}/abort`, {}),
    onSuccess: (_data, planId) => {
      toast.success('Analysis terminated.');
      qc.invalidateQueries({ queryKey: ['territory-plans'] });
      qc.invalidateQueries({ queryKey: ['territory-plan', planId] });
    },
    onError: (e: any) => toast.error('Could not terminate: ' + (e?.message ?? e)),
  });

  const uncommitPlanMut = useMutation({
    mutationFn: (planId: string) => api.post(`/territory/plans/${planId}/uncommit`, {}),
    onSuccess: () => {
      toast.success('Uncommitted');
      qc.invalidateQueries({ queryKey: ['territory-plans'] });
      qc.invalidateQueries({ queryKey: ['committed-plans'] });
    },
    onError: (e: any) => toast.error(`Uncommit failed: ${e?.message ?? e}`),
  });

  const cloneTerrMut = useMutation({
    mutationFn: async (planId: string) => {
      const r: any = await api.post(`/territory/plans/${planId}/clone`, {});
      // If the server did a deep copy (source was complete), the clone is
      // already analyzed — skip re-triggering /analyze.
      if (r?.plan_id && !r?.deep) {
        fetch(`/api/territory/plans/${r.plan_id}/analyze`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: '{}',
        }).catch(() => {});
      }
      return r;
    },
    onSuccess: (res: any) => {
      if (res?.deep) {
        toast.success(`Scenario created: ${res.name ?? 'clone'} (instant copy, no re-analyze needed)`);
      } else {
        toast.success(`Scenario created: ${res.name ?? 'clone'} — analyzing...`);
        setTerrAnalysisRunning(true);
      }
      setSelectedPlanId(res.plan_id);
      qc.invalidateQueries({ queryKey: ['territory-plans'] });
    },
    onError: (e: any) => toast.error(`Clone failed: ${e?.message ?? e}`),
  });

  const expandPolygonMut = useMutation({
    mutationFn: async (miles: number) => {
      if (!activePlan?.id) throw new Error('no active plan');
      // Fetch the source plan's polygon
      const planData: any = await api.get(`/territory/plans/${activePlan.id}`);
      const tg = planData?.territory_geojson;
      let ring: number[][] = [];
      if (tg?.type === 'Polygon') ring = tg.coordinates?.[0] ?? [];
      else if (tg?.type === 'MultiPolygon') ring = tg.coordinates?.[0]?.[0] ?? [];
      if (ring.length < 4) throw new Error('no polygon to expand');
      // Buffer outward from centroid by `miles` (rough deg-per-mile conversion
      // compensated for latitude)
      const meanLat = ring.reduce((a, p) => a + p[1], 0) / ring.length;
      const dPerMile = 1 / 69;
      const dxFactor = dPerMile / Math.cos(meanLat * Math.PI / 180);
      const dyFactor = dPerMile;
      const cx = ring.reduce((a, p) => a + p[0], 0) / ring.length;
      const cy = ring.reduce((a, p) => a + p[1], 0) / ring.length;
      const expanded: [number, number][] = ring.map((pt) => {
        const vx = pt[0] - cx;
        const vy = pt[1] - cy;
        const len = Math.sqrt(vx * vx + vy * vy);
        if (len < 1e-9) return [pt[0], pt[1]];
        const nx = vx / len;
        const ny = vy / len;
        return [pt[0] + nx * dxFactor * miles, pt[1] + ny * dyFactor * miles];
      });
      if (expanded[0][0] !== expanded[expanded.length - 1][0] || expanded[0][1] !== expanded[expanded.length - 1][1]) {
        expanded.push(expanded[0]);
      }

      // CLONE the source plan first so the original stays untouched. Name
      // the clone "<base> (+Nmi)" so it's obvious what it is in the list.
      const baseName = String(planData?.name ?? 'Territory').replace(/\s*\(\+\d+mi\)\s*$/, '');
      const clone: any = await api.post(
        `/territory/plans/${activePlan.id}/clone`,
        { name: `${baseName} (+${miles}mi)` }
      );
      const newId = clone?.plan_id;
      if (!newId) throw new Error('clone failed');

      // Overwrite the clone's polygon with the expanded ring, then analyze.
      await api.patch(`/territory/plans/${newId}/polygon`, { polygon: expanded });
      fetch(`/api/territory/plans/${newId}/analyze`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: '{}',
      }).catch(() => {});
      return { plan_id: newId, miles };
    },
    onSuccess: (res: any) => {
      toast.success(`Scenario +${res.miles}mi created — analyzing... (original preserved)`);
      setSelectedPlanId(res.plan_id);
      setTerrAnalysisRunning(true);
      qc.invalidateQueries({ queryKey: ['territory-plans'] });
    },
    onError: (e: any) => toast.error(`Expand failed: ${e?.message ?? e}`),
  });

  const clearAllScenariosMut = useMutation({
    mutationFn: async () => {
      // Delete every non-committed plan in the current project. Committed
      // plans are locked by the backend (409) and by this filter.
      const targets = (terrPlans as any[]).filter((p) => p.status !== 'committed');
      for (const p of targets) {
        try {
          await api.del(`/territory/plans/${p.id}`);
        } catch (e) {
          // Keep going — a single failure shouldn't abort the whole sweep
          // (e.g. someone committed it mid-sweep)
          console.warn('Clear scenarios: failed to delete', p.id, e);
        }
      }
      return { deleted: targets.length };
    },
    onSuccess: (res: any) => {
      toast.success(`Cleared ${res.deleted} scenario${res.deleted === 1 ? '' : 's'}${' '}(committed plans preserved)`);
      setSelectedPlanId(null);
      setTerrBuildIndices([]);
      setTerritoryEditMode(false);
      setCompareSelection(new Set());
      qc.invalidateQueries({ queryKey: ['territory-plans'] });
      qc.invalidateQueries({ queryKey: ['committed-plans'] });
    },
    onError: (e: any) => toast.error(`Clear all failed: ${e?.message ?? e}`),
  });

  // Hard reset — nuke EVERY zone and EVERY plan (including committed) for
  // the current project. Uses the project-level reset-zones endpoint with
  // force=true. Distinct from 'Clear all' which preserves committed plans.
  const hardResetMut = useMutation({
    mutationFn: () => {
      if (!selectedProjectId) throw new Error('no project selected');
      return api.post(`/projects/${selectedProjectId}/reset-zones?force=true`, {});
    },
    onSuccess: (res: any) => {
      toast.success(
        `Project reset · ${res.plans_deleted ?? 0} plans + ${res.zones_deleted ?? 0} zones deleted`
      );
      setSelectedPlanId(null);
      setActiveZoneId(null);
      setTerrBuildIndices([]);
      setTerritoryEditMode(false);
      setCompareSelection(new Set());
      qc.invalidateQueries({ queryKey: ['territory-plans'] });
      qc.invalidateQueries({ queryKey: ['committed-plans'] });
      qc.invalidateQueries({ queryKey: ['expansion-zones'] });
    },
    onError: (e: any) => toast.error(`Hard reset failed: ${e?.message ?? e}`),
  });

  const clearDraftsMut = useMutation({
    mutationFn: () => api.post('/territory/plans/clear-drafts', {}),
    onSuccess: (res: any) => {
      const n = Number(res?.deleted ?? 0);
      toast.success(n > 0 ? `Cleared ${n} orphan draft zone${n === 1 ? '' : 's'}` : 'No orphan drafts to clear');
      qc.invalidateQueries({ queryKey: ['territory-plans'] });
      qc.invalidateQueries({ queryKey: ['expansion-zones'] });
    },
    onError: (e: any) => toast.error(`Clear drafts failed: ${e?.message ?? e}`),
  });

  // Extract the active plan's outer ring for the polygon editor.
  const terrEditInitialRing = useMemo<[number, number][]>(() => {
    const tg: any = (terrPlan as any)?.territory_geojson;
    if (!tg) return [];
    if (tg.type === 'Polygon') return (tg.coordinates?.[0] ?? []) as [number, number][];
    if (tg.type === 'MultiPolygon') return (tg.coordinates?.[0]?.[0] ?? []) as [number, number][];
    return [];
  }, [terrPlan]);

  const terrPolyEdit = usePolygonEdit(mapRef, {
    active: territoryEditMode,
    initialRing: terrEditInitialRing,
    layerId: 'territory-edit-verts',
  });

  // Push edited polygon + vertex points into the territory-edit map source.
  // If the usePolygonEdit hook hasn't yet picked up the initial ring (e.g.
  // terrPlan detail loaded after the user clicked Stretch), fall back to the
  // ring extracted directly from territory_geojson so handles still render.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource('territory-edit') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    if (!territoryEditMode) {
      src.setData({ type: 'FeatureCollection', features: [] } as any);
      return;
    }
    const hookRing = terrPolyEdit.ring;
    const ring = hookRing.length >= 3 ? hookRing : terrEditInitialRing;
    if (ring.length < 3) {
      src.setData({ type: 'FeatureCollection', features: [] } as any);
      return;
    }
    const closed = ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
      ? ring : [...ring, ring[0]];
    const features: any[] = [
      { type: 'Feature', geometry: { type: 'Polygon', coordinates: [closed] }, properties: {} },
    ];
    // Skip the duplicated-closure vertex so the user doesn't see two handles
    // stacked on the same spot.
    const uniqVerts = ring.slice(0, ring.length - (closed.length > ring.length ? 0 : 1));
    for (let i = 0; i < uniqVerts.length; i++) {
      features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: uniqVerts[i] }, properties: { idx: i } });
    }
    src.setData({ type: 'FeatureCollection', features } as any);
  }, [territoryEditMode, terrPolyEdit.ring, terrEditInitialRing, mapReady]);

  const saveTerritoryEditMut = useMutation({
    mutationFn: async (args: { asScenario: boolean }) => {
      if (!activePlan?.id) throw new Error('no active plan');
      const ring = terrPolyEdit.ring;
      if (ring.length < 4) throw new Error('ring has fewer than 4 vertices');
      // Ensure closed ring
      const closed = ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
        ? ring : [...ring, ring[0]];
      let targetPlanId = activePlan.id;
      if (args.asScenario) {
        const clone: any = await api.post(`/territory/plans/${activePlan.id}/clone`, { name: `${String((terrPlan as any)?.name ?? 'Territory')} (edited)` });
        if (!clone?.plan_id) throw new Error('clone failed');
        targetPlanId = clone.plan_id;
      }
      await api.patch(`/territory/plans/${targetPlanId}/polygon`, { polygon: closed });
      // Kick off analyze
      fetch(`/api/territory/plans/${targetPlanId}/analyze`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: '{}',
      }).catch(() => {});
      return { plan_id: targetPlanId, asScenario: args.asScenario };
    },
    onSuccess: (res: any) => {
      setTerritoryEditMode(false);
      setTerrAnalysisRunning(true);
      if (res.asScenario) {
        toast.success('Edited polygon saved as new scenario — analyzing...');
        setSelectedPlanId(res.plan_id);
      } else {
        toast.success('Polygon saved — re-analyzing...');
      }
      qc.invalidateQueries({ queryKey: ['territory-plans'] });
      qc.invalidateQueries({ queryKey: ['territory-plan', res.plan_id] });
    },
    onError: (e: any) => toast.error(`Save failed: ${e?.message ?? e}`),
  });

  const deleteTerrMut = useMutation({
    mutationFn: (planId: string) => api.del(`/territory/plans/${planId}`),
    onSuccess: (_data, planId) => {
      toast.info('Territory deleted');
      // 2026-05-30: cache fix. The deleted plan id may still be the
      // selectedPlanId, in which case activePlan keeps pointing at a row
      // that no longer exists, terrPlan re-queries, gets 404, and the map
      // shows stale data until manual refresh. Reset selection + invalidate
      // the singular plan + full-plant caches so the map clears properly.
      if (selectedPlanId === planId) {
        setSelectedPlanId(null);
        setActiveTerritoryPlanId(null);
      }
      qc.invalidateQueries({ queryKey: ['territory-plans'] });
      qc.invalidateQueries({ queryKey: ['territory-plans', selectedProjectId] });
      qc.invalidateQueries({ queryKey: ['territory-plan', planId] });
      qc.invalidateQueries({ queryKey: ['plan-full-plant', planId] });
      qc.invalidateQueries({ queryKey: ['plan-rdof-summary', planId] });
      qc.invalidateQueries({ queryKey: ['committed-plans'] });
      qc.invalidateQueries({ queryKey: ['expansion-zones', selectedProjectId] });
      qc.invalidateQueries({ queryKey: ['zone-failures', selectedProjectId] });
      // 2026-05-30: also invalidate plant-graph + bom so the segments/nodes
      // we just cascade-deleted on the backend immediately clear from the
      // map (no manual refresh needed).
      if (plant?.id) {
        qc.invalidateQueries({ queryKey: ['plant-graph', plant.id] });
        qc.invalidateQueries({ queryKey: ['plant-passings', plant.id] });
        qc.invalidateQueries({ queryKey: ['bom', plant.id] });
      }
      setTerrBuildIndices([]);
    },
  });
  // 2026-05-30: clear all expansion_zone_failures audit rows for a project.
  const clearFailuresMut = useMutation({
    mutationFn: (projectId: string) => api.del(`/expansion/failures?project_id=${projectId}`),
    onSuccess: (data: any) => {
      toast.success(`Cleared ${data?.deleted ?? 0} failure record(s).`);
      qc.invalidateQueries({ queryKey: ['zone-failures', selectedProjectId] });
    },
    onError: (e: any) => toast.error('Could not clear failures: ' + (e?.message ?? e)),
  });

  const startTerrAnalysis = useCallback((analysisMode?: 'full' | 'bead_only' | 'bolt_on') => {
    if (!activePlan?.id) return;
    setTerrAnalysisRunning(true);
    // Don't clear the running flag via setTimeout — the useEffect on
    // terrPlan.status will clear it when the plan flips to 'complete'.
    // Clearing early races with the refetch and lets the Analyze
    // button flash back in.
    const invalAll = () => {
      qc.invalidateQueries({ queryKey: ['territory-plan', activePlan.id] });
      qc.invalidateQueries({ queryKey: ['territory-plans', selectedProjectId] });
      qc.invalidateQueries({ queryKey: ['expansion-zones', selectedProjectId] });
    };
    fetch(`/api/territory/plans/${activePlan.id}/analyze`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ mode: analysisMode ?? 'full' }) })
      .then(async (res) => {
        // Backend now returns 202 Accepted immediately and runs the
        // job in a detached task. We announce the start here; the 3s
        // poll below catches the status transition to 'complete'.
        if (res.status === 202) toast.info('Analysis started — updating every 3s', 6000);
        else if (res.ok) toast.success('Territory analysis complete');
      })
      .catch(() => {
        // Fetch-abort swallow. Real failures surface via the poller.
      })
      .finally(() => { invalAll(); });
    // While analysis is running, poll every 3s so cluster polygons + backbone appear as they finish.
    const pollId = window.setInterval(() => {
      invalAll();
      refetchTerrPlan();
    }, 3000);
    // Auto-stop polling after 30 min safety net (big plans like CO full-state can run long)
    window.setTimeout(() => window.clearInterval(pollId), 30 * 60 * 1000);
    setTimeout(() => refetchTerrPlan(), 2000);
  }, [activePlan?.id, refetchTerrPlan, qc]);

  const { data: expansionZones = [] } = useQuery<ExpansionZone[]>({
    queryKey: ['expansion-zones', selectedProjectId],
    queryFn: () => selectedProjectId
      ? api.get(`/expansion/project/${selectedProjectId}`)
      : Promise.resolve([]),
    enabled: !!selectedProjectId,
    // 2026-05-30 Tier-2: cache for 5s during analyze (no-op when not
    // analyzing — invalidations from mutations still fire immediately).
    staleTime: 5_000,
  });

  // Audit log of deleted failed/rejected/zombie zones for this project.
  const { data: zoneFailures = [] } = useQuery<Array<{
    id: string; original_zone_id: string; name: string; zone_type: string | null;
    failure_kind: string; reason: string | null;
    partial_analysis: any | null;
    zone_geojson: any | null;
    zone_created_at: string; failed_at: string;
  }>>({
    queryKey: ['zone-failures', selectedProjectId],
    queryFn: () => selectedProjectId
      ? api.get(`/expansion/failures?project_id=${selectedProjectId}&limit=50`)
      : Promise.resolve([]),
    enabled: !!selectedProjectId,
    refetchInterval: 30_000,
  });

  const [failuresOpen, setFailuresOpen] = useState(false);

  const { data: activeZone } = useQuery<ExpansionZone>({
    queryKey: ['expansion-zone', activeZoneId],
    queryFn: () => activeZoneId ? api.get(`/expansion/zones/${activeZoneId}`) : Promise.reject(),
    enabled: !!activeZoneId,
    refetchInterval: (q) => {
      // Poll briefly while design is running (status stays 'draft' until analyze completes).
      const d = q.state.data as ExpansionZone | undefined;
      return d && d.status === 'draft' ? 2000 : false;
    },
  });

  const { data: activeZoneDesign } = useQuery<{ segments: any; nodes: any }>({
    queryKey: ['expansion-design', activeZoneId],
    queryFn: () => activeZoneId ? api.get(`/expansion/zones/${activeZoneId}/design`) : Promise.reject(),
    enabled: !!activeZoneId && !!activeZone && activeZone.status !== 'draft',
  });

  const { data: activeZonePassings } = useQuery<any>({
    queryKey: ['expansion-passings', activeZoneId],
    queryFn: () => activeZoneId ? api.get(`/expansion/zones/${activeZoneId}/passings`) : Promise.reject(),
    enabled: !!activeZoneId && !!activeZone && activeZone.status !== 'draft',
  });

  // Buildings (viewport)
  const [bbox, setBbox] = useState<string | null>(null);
  const { data: buildings } = useQuery<BuildingFeatureCollection>({
    queryKey: ['buildings', bbox, plant?.id],
    queryFn: () => bbox && plant
      ? api.get(`/buildings?bbox=${bbox}&plant_id=${plant.id}&limit=3000`)
      : Promise.resolve({ type: 'FeatureCollection', features: [] } as BuildingFeatureCollection),
    enabled: !!plant && !!bbox && showBuildings,
    staleTime: 30_000,
  });

  // Towers (FCC ASR) — visible at zoom >= 10, fetched per-viewport like buildings.
  const { data: towers } = useQuery<any>({
    queryKey: ['towers', bbox],
    queryFn: () => bbox ? api.get(`/map/towers?bbox=${bbox}&limit=500`) : Promise.resolve({ type: 'FeatureCollection', features: [] }),
    enabled: !!bbox,
    staleTime: 60_000,
  });

  // ---- Map init ----
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: BASEMAPS[basemap].style,
      center: WV_CENTER,
      zoom: 7,
      hash: false,
    });
    map.addControl(new maplibregl.NavigationControl({}), 'top-right');
    map.addControl(new maplibregl.ScaleControl({ unit: 'imperial', maxWidth: 120 }));
    map.on('load', () => {
      addAppLayers(map);
      const syncBbox = () => {
        const b = map.getBounds();
        setBbox(`${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`);
      };
      map.on('moveend', syncBbox);
      syncBbox();
      mapRef.current = map;
      setMapReady(true);
    });
    return () => { map.remove(); mapRef.current = null; setMapReady(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Swap basemap by rebuilding style + re-adding app layers
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    map.setStyle(BASEMAPS[basemap].style);
    map.once('styledata', () => {
      addAppLayers(map);
      // Repush current data (these effects don't re-fire on basemap swap,
      // so we push directly here for what we have locally).
      if (graph) {
        (map.getSource('segments') as maplibregl.GeoJSONSource)?.setData(graph.segments as any);
        (map.getSource('nodes')    as maplibregl.GeoJSONSource)?.setData(graph.nodes as any);
        (map.getSource('drops')    as maplibregl.GeoJSONSource)?.setData(graph.drops as any);
      }
      if (buildings) (map.getSource('buildings') as maplibregl.GeoJSONSource)?.setData(buildings as any);
      // Bump styleVersion — the effect at styleVersion then invalidates every
      // other query (passings, towers, zones, expansion-design) so their
      // data-push effects fire too and re-hydrate the freshly re-added sources.
      setStyleVersion((v) => v + 1);
    });
  }, [basemap]);

  // Push data to map on changes — depends on mapReady so a cached graph
  // response that arrives before map.on('load') still lands.
  // Skipped during animatingDesign — the animation effect owns the source
  // temporarily and reveals segments progressively.
  useEffect(() => {
    if (!mapReady) return;
    if (animatingDesign) return;
    const map = mapRef.current;
    if (!map || !map.getSource('segments')) return;
    (map.getSource('segments') as maplibregl.GeoJSONSource).setData(graph?.segments ?? ({ type: 'FeatureCollection', features: [] } as any));
    (map.getSource('nodes')    as maplibregl.GeoJSONSource).setData(graph?.nodes    ?? ({ type: 'FeatureCollection', features: [] } as any));
    (map.getSource('drops')    as maplibregl.GeoJSONSource).setData(graph?.drops    ?? ({ type: 'FeatureCollection', features: [] } as any));
    if (graph && (graph.nodes.features.length || graph.segments.features.length)) {
      const b = new maplibregl.LngLatBounds();
      for (const f of graph.nodes.features) {
        const c = f.geometry?.coordinates;
        if (Array.isArray(c) && typeof c[0] === 'number') b.extend(c as [number, number]);
      }
      for (const f of graph.segments.features) {
        const cs = f.geometry?.coordinates;
        if (Array.isArray(cs)) for (const c of cs) if (Array.isArray(c) && typeof c[0] === 'number') b.extend(c as [number, number]);
      }
      if (!b.isEmpty()) map.fitBounds(b, { padding: 80, duration: 0, maxZoom: 17 });
    }
  }, [graph, mapReady, animatingDesign]);

  // 2026-05-27: clear plant-graph sources the instant the active plant id
  // changes (project switch or deselect) so stale nodes/segments/drops from
  // the previous plant don't linger on the map while the new graph loads.
  // The black-with-orange-outline OLT markers were appearing in a county
  // far from the active project because of this gap.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    const empty = { type: 'FeatureCollection', features: [] } as any;
    for (const id of ['nodes', 'segments', 'drops', 'passings-buffer',
                      'passing-dots', 'olt-marker', 'polygon',
                      'headend-src', 'gateway-src', 'pops-src',
                      'clusters', 'backbone', 'ftth',
                      'plan-segments', 'plan-nodes', 'plan-drops',
                      'bead-bsl-source', 'rdof-bsl-source', 'towers-source']) {
      const s = map.getSource(id) as maplibregl.GeoJSONSource | undefined;
      if (s) s.setData(empty);
    }
  }, [plant?.id, mapReady]);

  // ---- Auto-design animation: progressive reveal of segments + nodes ----
  // When `animatingDesign` becomes true (right after auto-design returns),
  // we clear the segments source, then reveal them in batches over ~3s,
  // sorted by auto_label which approximates Dijkstra/insertion order.
  useEffect(() => {
    if (!animatingDesign || !mapReady || !graph) return;
    const map = mapRef.current;
    if (!map) return;

    const segs = [...graph.segments.features].sort((a, b) =>
      String(a.properties?.auto_label ?? '').localeCompare(
        String(b.properties?.auto_label ?? ''), undefined, { numeric: true }
      )
    );
    const nodes = graph.nodes.features;

    // Auto-fit to bounds first
    const bounds = new maplibregl.LngLatBounds();
    for (const f of segs) {
      const cs = f.geometry?.coordinates;
      if (Array.isArray(cs)) for (const c of cs) if (Array.isArray(c) && typeof c[0] === 'number') bounds.extend(c as [number, number]);
    }
    if (!bounds.isEmpty()) map.fitBounds(bounds, { padding: 80, duration: 600, maxZoom: 17 });

    // Start empty
    const segSrc = map.getSource('segments') as maplibregl.GeoJSONSource;
    const nodeSrc = map.getSource('nodes')   as maplibregl.GeoJSONSource;
    segSrc.setData({ type: 'FeatureCollection', features: [] } as any);
    nodeSrc.setData({ type: 'FeatureCollection', features: [] } as any);
    // Pop the OLT in immediately so the audience sees the "ignition point"
    const olts = nodes.filter((n) => n.properties?.type === 'olt' || n.properties?.type === 'headend');
    if (olts.length) nodeSrc.setData({ type: 'FeatureCollection', features: olts } as any);

    const total = segs.length;
    const targetMs = 2800;       // ~3 s feels live, not slow
    const tickMs = 30;
    const perTick = Math.max(1, Math.ceil(total / (targetMs / tickMs)));
    let i = 0;
    const handle = setInterval(() => {
      i = Math.min(total, i + perTick);
      segSrc.setData({
        type: 'FeatureCollection',
        features: segs.slice(0, i),
      } as any);
      if (i >= total) {
        clearInterval(handle);
        // Final state: full graph
        segSrc.setData(graph.segments as any);
        nodeSrc.setData(graph.nodes as any);
        setAnimatingDesign(false);
      }
    }, tickMs);
    return () => clearInterval(handle);
  }, [animatingDesign, mapReady, graph]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getSource('buildings')) return;
    if (showBuildings && buildings) (map.getSource('buildings') as maplibregl.GeoJSONSource).setData(buildings as any);
    for (const id of ['buildings-fill','buildings-stroke'] as const) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', showBuildings ? 'visible' : 'none');
    }
  }, [buildings, showBuildings]);

  // Towers → map
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource('towers') as maplibregl.GeoJSONSource | undefined;
    if (src && towers) src.setData(towers);
  }, [towers, mapReady]);

  // Passings data → map. Tag each BSL with `eligible:true|false` so the
  // dot layer can color it (Layer 1 green vs Layer 2 amber).
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map || !map.getSource('passings-buffer')) return;
    (map.getSource('passings-buffer') as maplibregl.GeoJSONSource).setData(passings?.buffer ?? ({ type: 'FeatureCollection', features: [] } as any));

    const eligibleSet = new Set((beadEligible?.location_ids ?? []).map(String));
    const dots = passings?.passings ?? { type: 'FeatureCollection', features: [] };
    const tagged = {
      type: 'FeatureCollection' as const,
      features: (dots.features ?? []).map((f: any) => ({
        ...f,
        properties: {
          ...(f.properties ?? {}),
          eligible: eligibleSet.has(String(f.properties?.location_id ?? f.id)),
        },
      })),
    };
    (map.getSource('passing-dots') as maplibregl.GeoJSONSource).setData(tagged);
  }, [passings, beadEligible, mapReady]);

  // ---- Polygon draw hook (for design) ----
  const polyDraw = usePolygonDraw(mapRef, {
    active: polygonMode,
    onChange: (ring) => {
      const map = mapRef.current;
      if (!map || !map.getSource('polygon')) return;
      const features: any[] = [];
      if (ring.length >= 3) {
        features.push({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[...ring, ring[0]]] }, properties: {} });
      }
      if (ring.length >= 2) {
        features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: ring }, properties: {} });
      }
      for (const c of ring) features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: {} });
      (map.getSource('polygon') as maplibregl.GeoJSONSource).setData({ type: 'FeatureCollection', features });
    },
    onFinish: (closed) => {
      setPolygonRing(closed);
      setPolygonMode(false);
    },
  });

  // ---- Expansion-zone boundary EDIT (vertex-drag on an existing zone) ----
  const editInitialRing: [number, number][] = (() => {
    const g = (activeZone as any)?.zone_geojson;
    if (!g) return [];
    // zone_geom is MultiPolygon; take first ring of first polygon.
    const coords = g.type === 'MultiPolygon' ? g.coordinates?.[0]?.[0]
                 : g.type === 'Polygon'       ? g.coordinates?.[0]
                 : null;
    return Array.isArray(coords) ? coords as [number, number][] : [];
  })();
  const polyEdit = usePolygonEdit(mapRef, {
    active: editMode,
    initialRing: editInitialRing,
    onChange: (ring) => {
      const map = mapRef.current;
      if (!map || !map.getSource('expansion-edit')) return;
      const polyFeature = ring.length >= 4
        ? [{ type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: {} }]
        : [];
      const vertFeatures = ring.slice(0, -1).map((c, i) => ({
        type: 'Feature' as const,
        id: i,
        geometry: { type: 'Point' as const, coordinates: c },
        properties: { idx: i },
      }));
      (map.getSource('expansion-edit') as maplibregl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: [...polyFeature, ...vertFeatures] as any,
      });
    },
  });
  const mergeMut = useMutation({
    mutationFn: async () => {
      if (selectedZoneIds.length < 2) throw new Error('Select 2+ zones to merge');
      const [primary_zone_id, ...secondary_zone_ids] = selectedZoneIds;
      return api.post('/expansion/zones/merge', {
        primary_zone_id,
        secondary_zone_ids,
        buffer_m: 30,
      });
    },
    onSuccess: (z: any) => {
      toast.success(`Merged ${selectedZoneIds.length} zones → ${z.name}`);
      setSelectedZoneIds([]);
      qc.invalidateQueries({ queryKey: ['expansion-zones', selectedProjectId] });
      setActiveZoneId(z.id);
    },
    onError: (e: any) => toast.error('Merge failed: ' + (e?.message ?? e)),
  });

  // Debounced live preview of passings while dragging vertices.
  useEffect(() => {
    if (!editMode) { setPreviewPassings(null); return; }
    if (polyEdit.ring.length < 4) { setPreviewPassings(null); return; }
    const ring = polyEdit.ring;
    const t = setTimeout(() => {
      // Keep the ring closed for the backend
      const closed = ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1] ? ring : [...ring, ring[0]];
      api.post('/expansion/preview-passings', { polygon: closed })
        .then((r: any) => { if (typeof r?.passings === 'number') setPreviewPassings(r.passings); })
        .catch(() => {});
    }, 400); // debouncedPreview
    return () => clearTimeout(t);
  }, [editMode, polyEdit.ring]);

  // Auto-recover effect: whenever the current zone is in a "stale" state
  // (user edited shape or kicked off analyze), poll the zone every 5s and
  // react-query invalidate once the server reports status='analyzed' with an
  // updated_at newer than when we marked it stale.
  //
  // This handles: (a) HTTP request timed out from the browser's side but the
  // server finished, (b) user closed+reopened the tab mid-analyze, (c) any
  // case where the mutation's onSuccess never fires.
  const [analyzePollStart, setAnalyzePollStart] = useState<number | null>(null);
  useEffect(() => {
    if (!activeZoneId || staleSince === null) return;
    const timer = setInterval(() => {
      api.get(`/expansion/zones/${activeZoneId}`).then((z: any) => {
        const updated = new Date(z.updated_at).getTime();
        if (z.status === 'analyzed' && updated >= staleSince) {
          // Done — clear all stale/pending flags
          setAnalyzePollStart(null);
          setSavedNeedsReanalyze(false);
          setStaleSince(null);
          qc.invalidateQueries({ queryKey: ['expansion-zone', activeZoneId] });
          qc.invalidateQueries({ queryKey: ['expansion-zones', selectedProjectId] });
          qc.invalidateQueries({ queryKey: ['expansion-design', activeZoneId] });
          qc.invalidateQueries({ queryKey: ['expansion-passings', activeZoneId] });
          toast.success('Pricing refreshed');
        }
      }).catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [activeZoneId, staleSince, qc, selectedProjectId]);

  const analyzeMut = useMutation({
    mutationFn: () => {
      const now = Date.now();
      setAnalyzePollStart(now);
      setStaleSince(now);
      return api.post(`/expansion/zones/${activeZoneId}/analyze`, {});
    },
    onSuccess: () => {
      setAnalyzePollStart(null);
      setSavedNeedsReanalyze(false);
      setStaleSince(null);
      toast.success('Re-analyzed. Pricing refreshed.');
      qc.invalidateQueries({ queryKey: ['expansion-zone', activeZoneId] });
      qc.invalidateQueries({ queryKey: ['expansion-zones', selectedProjectId] });
      qc.invalidateQueries({ queryKey: ['expansion-design', activeZoneId] });
      qc.invalidateQueries({ queryKey: ['expansion-passings', activeZoneId] });
    },
    onError: (e: any) => {
      // HTTP timed out or dropped — DON'T clear staleSince; the auto-recover
      // effect below will catch the completion via direct zone polling.
      console.warn('[analyze] mutation errored; waiting for poll to detect completion', e);
    },
  });

  const saveEditMut = useMutation({
    mutationFn: async (ring: [number, number][]) => {
      if (!activeZoneId) throw new Error('no active zone');
      const closed = ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1] ? ring : [...ring, ring[0]];
      return api.patch(`/expansion/zones/${activeZoneId}/geometry`, { polygon: closed });
    },
    onSuccess: () => {
      toast.success('Shape saved. Pricing is stale until you re-analyze.');
      setEditMode(false);
      setSavedNeedsReanalyze(true);
      setStaleSince(Date.now());
      qc.invalidateQueries({ queryKey: ['expansion-zone', activeZoneId] });
      qc.invalidateQueries({ queryKey: ['expansion-zones', selectedProjectId] });
    },
    onError: (e: any) => toast.error('Save failed: ' + (e?.message ?? e)),
  });

  // ---- Territory polygon draw (for creating a new Territory Plan) ----
  const territoryDraw = usePolygonDraw(mapRef, {
    active: terrDrawMode,
    onChange: (ring) => {
      const map = mapRef.current;
      if (!map || !map.getSource('territory-draft')) return;
      const features: any[] = [];
      if (ring.length >= 3) features.push({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[...ring, ring[0]]] }, properties: {} });
      if (ring.length >= 2) features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: ring }, properties: {} });
      for (const c of ring) features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: {} });
      (map.getSource('territory-draft') as maplibregl.GeoJSONSource).setData({ type: 'FeatureCollection', features });
    },
    onFinish: (closed) => {
      setTerrDrawMode(false);
      if (closed.length >= 4) createTerrMut.mutate(closed);
      // Clear the draft source
      const map = mapRef.current;
      if (map) {
        const src = map.getSource('territory-draft') as maplibregl.GeoJSONSource | undefined;
        if (src) src.setData({ type: 'FeatureCollection', features: [] });
      }
    },
  });

  // ---- Expansion-zone polygon draw (separate hook instance, separate source) ----
  const expansionDraw = usePolygonDraw(mapRef, {
    active: expansionMode,
    onChange: (ring) => {
      const map = mapRef.current;
      if (!map || !map.getSource('expansion-draft')) return;
      const features: any[] = [];
      if (ring.length >= 3) {
        features.push({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [[...ring, ring[0]]] }, properties: {} });
      }
      if (ring.length >= 2) {
        features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: ring }, properties: {} });
      }
      for (const c of ring) features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: {} });
      (map.getSource('expansion-draft') as maplibregl.GeoJSONSource).setData({ type: 'FeatureCollection', features });
    },
    onFinish: async (closed) => {
      setExpansionMode(false);
      if (!selectedProjectId || !plant || closed.length < 4) return;
      // Ensure the ring is closed for ST_GeomFromText
      const ring = closed[0][0] === closed[closed.length - 1][0] && closed[0][1] === closed[closed.length - 1][1]
        ? closed
        : [...closed, closed[0]];
      try {
        const nextIdx = expansionZones.filter((z) => z.status !== 'rejected').length + 1;
        const created = await api.post('/expansion/zones', {
          project_id: selectedProjectId,
          plant_id: plant.id,
          name: `Zone ${nextIdx}`,
          zone_type: 'contiguous',
          selection_mode: 'polygon',
          polygon: ring,
        });
        toast.success(`Zone drafted — click 'Run analysis' to design the extension.`);
        // Reset per-zone flags so the fresh-draft analyze panel shows.
        setEditMode(false);
        setSavedNeedsReanalyze(false);
        setStaleSince(null);
        setAnalyzePollStart(null);
        // Optimistically inject the new zone into the cached list BEFORE
        // setActiveZoneId fires. Otherwise the 'scrub orphan' effect runs
        // against the stale list, doesn't find the new id, and nullifies
        // activeZoneId before the refetch completes.
        qc.setQueryData<any[]>(['expansion-zones', selectedProjectId], (old) =>
          Array.isArray(old) ? [...old, created] : [created]
        );
        setActiveZoneId(created.id);
        qc.invalidateQueries({ queryKey: ['expansion-zones', selectedProjectId] });
      } catch (e) {
        toast.error('Could not create zone: ' + (e as Error).message);
      }
    },
  });

  // ---- Segment draw hook (for manual) ----
  const segDraw = useSegmentDraw(mapRef, {
    active: mode === 'draw_segment' && tab === 'manual',
    onChange: (coords) => {
      const map = mapRef.current;
      if (!map || !map.getSource('draft')) return;
      const features: any[] = [];
      if (coords.length >= 2) features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: {} });
      for (const c of coords) features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: c }, properties: {} });
      (map.getSource('draft') as maplibregl.GeoJSONSource).setData({ type: 'FeatureCollection', features });
    },
    onFinish: async (coords) => {
      if (!plant || coords.length < 2) return;
      const pending = pendingToNodeRef.current;
      pendingToNodeRef.current = null;
      try {
        const created = await api.post('/segments', {
          plant_id: plant.id,
          type: pendingSegmentType,
          placement: pendingPlacement,
          geom_planned: { type: 'LineString', coordinates: coords },
          from_node_id: pending?.from_node_id ?? drawFromNode?.id ?? undefined,
        });
        if (pending?.to_node_id) {
          await api.patch(`/segments/${created.id}`, { to_node_id: pending.to_node_id });
        }
        setDrawFromNode(null);
        refetchGraph();
      } catch (e) {
        toast.error('Save segment failed: ' + (e as Error).message);
      }
    },
  });

  // ---- Master click handler (defensively wrapped) ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const onClick = async (e: maplibregl.MapMouseEvent) => {
      try {
        // Safe queryRenderedFeatures — only pass layers that exist.
        const wantedLayers = ['nodes','segments','drops','buildings-fill','towers-dot']
          .filter((id) => map.getLayer(id));
        const hits = wantedLayers.length ? map.queryRenderedFeatures(e.point, { layers: wantedLayers }) : [];
        const nodeHit  = hits.find((h) => h.layer.id === 'nodes');
        const segHit   = hits.find((h) => h.layer.id === 'segments');
        const dropHit  = hits.find((h) => h.layer.id === 'drops');
        const bldHit   = hits.find((h) => h.layer.id === 'buildings-fill');
        const towerHit = hits.find((h) => h.layer.id === 'towers-dot');
        const zoneHit  = hits.find((h) => h.layer.id === 'expansion-all-zones-fill' || h.layer.id === 'expansion-polygon-fill');

        // ---- DESIGN MODE: placing OLT overrides other clicks ----
        if (placingOlt && plant) {
          setOltLngLat([e.lngLat.lng, e.lngLat.lat]);
          setPlacingOlt(false);
          return;
        }

        // Polygon draw: click appends a vertex.
        if (polygonMode) {
          polyDraw.appendVertex([e.lngLat.lng, e.lngLat.lat]);
          return;
        }

        // Expansion-zone draw: click appends a vertex to the expansion polygon.
        if (expansionMode) {
          expansionDraw.appendVertex([e.lngLat.lng, e.lngLat.lat]);
          return;
        }

        // Territory draw: click appends a vertex to the territory polygon.
        if (terrDrawMode) {
          territoryDraw.appendVertex([e.lngLat.lng, e.lngLat.lat]);
          return;
        }

        // ---- MANUAL: place node ----
        if (tab === 'manual' && mode === 'place_node' && plant) {
          await api.post('/nodes', {
            plant_id: plant.id,
            type: pendingNodeType,
            geom: { type: 'Point', coordinates: [e.lngLat.lng, e.lngLat.lat] },
          });
          refetchGraph();
          return;
        }

        // ---- MANUAL: draw segment ----
        if (tab === 'manual' && mode === 'draw_segment') {
          if (nodeHit) {
            const nid = nodeHit.id as string;
            const coords = (nodeHit.geometry as any)?.coordinates as [number, number] | undefined;
            if (!coords) return;
            if (!drawFromNode) {
              setDrawFromNode({ id: nid, lng: coords[0], lat: coords[1] });
              segDraw.seedWithNode(coords);
            } else if (drawFromNode.id !== nid) {
              const fromId = drawFromNode.id;
              setDrawFromNode(null);
              pendingToNodeRef.current = { to_node_id: nid, from_node_id: fromId };
              segDraw.endAtNode(coords);
            }
            return;
          }
          // Free vertex (no node required — user wanted simpler UX)
          segDraw.appendVertex([e.lngLat.lng, e.lngLat.lat]);
          return;
        }

        // ---- SELECT mode (default) ----
        // If click lands on an INACTIVE zone polygon, always activate that
        // zone (even if there's a node underneath) — user is clearly trying to
        // switch clusters. Within the already-active zone, precise features
        // (node/segment/drop) win as normal.
        const inactiveZoneHit = (zoneHit && (zoneHit.properties as any)?.zone_id && (zoneHit.properties as any)?.zone_id !== activeZoneId)
          ? zoneHit : null;
        if (inactiveZoneHit) {
          const zid = (inactiveZoneHit.properties as any).zone_id as string;
          setActiveZoneId(zid);
          setSelected(null);
        }
        else if (nodeHit)  setSelected({ kind: 'node',     feature: nodeHit as any });
        else if (segHit)   setSelected({ kind: 'segment',  feature: segHit as any });
        else if (dropHit)  setSelected({ kind: 'drop',     feature: dropHit as any });
        else if (towerHit) setSelected({ kind: 'tower',    feature: towerHit as any });
        else if (bldHit)   setSelected({ kind: 'building', feature: bldHit as any });
        else               setSelected(null);
      } catch (err) {
        // Never let a click throw — we swallow and surface as a banner so the
        // map stays alive.
        // eslint-disable-next-line no-console
        console.error('click handler error', err);
        toast.error('Click failed: ' + (err as Error).message);
      }
    };

    map.on('click', onClick);
    return () => { map.off('click', onClick); };
  }, [tab, mode, plant, pendingNodeType, drawFromNode, segDraw, polygonMode, expansionMode, placingOlt, refetchGraph, activeZoneId]);

  // Cursor hints
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const cursor =
      placingOlt ? 'crosshair' :
      polygonMode ? 'crosshair' :
      expansionMode ? 'crosshair' :
      (tab === 'manual' && mode === 'place_node') ? 'crosshair' :
      (tab === 'manual' && mode === 'draw_segment') ? 'crosshair' :
      '';
    map.getCanvas().style.cursor = cursor;
  }, [placingOlt, polygonMode, expansionMode, tab, mode]);

  // Double-click: finish the polygon (design) or segment (manual) or expansion zone.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const onDbl = (e: maplibregl.MapMouseEvent) => {
      if (polygonMode) {
        e.preventDefault();
        polyDraw.finish();
        return;
      }
      if (expansionMode) {
        e.preventDefault();
        expansionDraw.finish();
        return;
      }
      if (terrDrawMode) {
        e.preventDefault();
        territoryDraw.finish();
        return;
      }
      if (tab === 'manual' && mode === 'draw_segment') {
        e.preventDefault();
        segDraw.finish();
        return;
      }
    };
    map.on('dblclick', onDbl);
    return () => { map.off('dblclick', onDbl); };
  }, [polygonMode, expansionMode, tab, mode, polyDraw, segDraw, expansionDraw]);

  // Keyboard shortcuts:
  //  Esc      — cancel pending action
  //  Del/Back — delete the currently-selected segment or node
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPlacingOlt(false);
        setPolygonMode(false);
        setDrawFromNode(null);
        setBanner(null);
        segDraw.clear();
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected) {
        // Don't interfere with typing in inputs.
        const t = e.target as HTMLElement;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        e.preventDefault();
        handleDelete();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segDraw, selected]);

  // Render OLT marker source when oltLngLat changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getSource('olt-marker')) return;
    (map.getSource('olt-marker') as maplibregl.GeoJSONSource).setData({
      type: 'FeatureCollection',
      features: oltLngLat
        ? [{ type: 'Feature', geometry: { type: 'Point', coordinates: oltLngLat }, properties: { label: 'OLT' } }]
        : [],
    });
  }, [oltLngLat]);

  // Selected highlight filters
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const nid = selected?.kind === 'node' ? selected.feature.id : '';
    const sid = selected?.kind === 'segment' ? selected.feature.id : '';
    if (map.getLayer('nodes-selected'))    map.setFilter('nodes-selected',    ['==', ['id'], nid as any] as any);
    if (map.getLayer('segments-selected')) map.setFilter('segments-selected', ['==', ['id'], sid as any] as any);
  }, [selected]);

  // Clear polygon visual when the ring is cleared
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getSource('polygon')) return;
    if (polygonRing) {
      (map.getSource('polygon') as maplibregl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry: { type: 'Polygon', coordinates: [polygonRing] }, properties: {} }],
      });
    } else if (!polygonMode) {
      (map.getSource('polygon') as maplibregl.GeoJSONSource).setData({ type: 'FeatureCollection', features: [] });
    }
  }, [polygonRing, polygonMode]);

  // Render the active expansion zone's polygon into the expansion-polygon source
  // (this stays up after expansionMode finishes so the user keeps seeing the zone).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !map.getSource('expansion-polygon')) return;
    // Draft draw lives in a separate source now (expansion-draft), so the
    // expansionMode early-return is no longer needed — the saved active-zone
    // polygon stays visible while the user draws a new one.
    if (activeZone?.zone_geojson) {
      const g = activeZone.zone_geojson;
      (map.getSource('expansion-polygon') as maplibregl.GeoJSONSource).setData({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry: g, properties: {} }],
      });
    } else {
      (map.getSource('expansion-polygon') as maplibregl.GeoJSONSource).setData({
        type: 'FeatureCollection', features: [],
      });
    }
  }, [activeZone, expansionMode, mapReady]);

  // Clear the draft source when we exit expansion mode (cancel or finish).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (expansionMode) return;
    const src = map.getSource('expansion-draft') as maplibregl.GeoJSONSource | undefined;
    if (src) src.setData({ type: 'FeatureCollection', features: [] });
  }, [expansionMode, mapReady]);

  // After basemap swap, reinvalidate every query whose data gets pushed to
  // a maplibre source so the data-push effects fire again against the
  // freshly-re-added sources.
  useEffect(() => {
    if (styleVersion === 0) return;  // initial mount, not a swap
    qc.invalidateQueries({ queryKey: ['plant-graph'] });
    qc.invalidateQueries({ queryKey: ['plant-passings'] });
    qc.invalidateQueries({ queryKey: ['buildings'] });
    qc.invalidateQueries({ queryKey: ['towers'] });
    qc.invalidateQueries({ queryKey: ['expansion-zones'] });
    qc.invalidateQueries({ queryKey: ['expansion-zone'] });
    qc.invalidateQueries({ queryKey: ['expansion-design'] });
    qc.invalidateQueries({ queryKey: ['expansion-passings'] });
  }, [styleVersion, qc]);

  // Apply layer-visibility toggles (Layers panel).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const applyLayerVis = (id: string, visible: boolean) => {
      if (!map.getLayer(id)) return;
      map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
    };
    applyLayerVis('buildings-fill', layerVis.buildings);
    applyLayerVis('buildings-line', layerVis.buildings);
    applyLayerVis('towers-dot', layerVis.towers);
    applyLayerVis('expansion-all-zones-fill', layerVis.inactive_zones);
    applyLayerVis('expansion-all-zones-outline', layerVis.inactive_zones);
    applyLayerVis('expansion-all-zones-label', layerVis.inactive_zones);
    applyLayerVis('expansion-dots', layerVis.passings);
    applyLayerVis('expansion-segments', layerVis.plant);
    applyLayerVis('expansion-segments-casing', layerVis.plant);
    applyLayerVis('expansion-nodes', layerVis.plant);
    applyLayerVis('segments', layerVis.plant);
    applyLayerVis('segments-casing', layerVis.plant);
    applyLayerVis('nodes', layerVis.plant);
    applyLayerVis('bead-awards-circles', layerVis.bead_awards);
    applyLayerVis('bead-awards-counts', layerVis.bead_awards);
  }, [layerVis, mapReady]);

  // --- BEAD Awards overlay: pull from GeoHub when toggle is on ---
  const beadOverlayQuery = useQuery<any>({
    queryKey: ['bead-awards-overlay', beadState, beadProvider],
    queryFn: () => api.get(`/bead/awards-overlay?state=${encodeURIComponent(beadState)}&provider=${encodeURIComponent(beadProvider)}`),
    // 2026-05-27: hard-disabled. The bead-awards-circles overlay has no
    // checkbox in the visible map controls but a stale layerVis.bead_awards
    // value (localStorage, etc.) was keeping it on. To restore, change back
    // to `enabled: layerVis.bead_awards` AND add a checkbox.
    enabled: false,
    staleTime: 60_000,
  });

  // Wire BEAD Awards FeatureCollection into the map
  useEffect(() => {
    if (!mapReady) return;
    const m = mapRef.current; if (!m) return;
    const fc = beadOverlayQuery.data;
    const SRC_ID = 'bead-awards-src';
    try {
      if (fc && fc.type === 'FeatureCollection') {
        if (m.getSource(SRC_ID)) {
          (m.getSource(SRC_ID) as any).setData(fc);
        } else {
          m.addSource(SRC_ID, { type: 'geojson', data: fc });
          m.addLayer({
            id: 'bead-awards-circles',
            type: 'circle',
            source: SRC_ID,
            paint: {
              'circle-radius': [
                'interpolate', ['linear'], ['get', 'bsl_count'],
                1, 4, 50, 9, 200, 14, 500, 20,
              ],
              'circle-color': '#f59e0b',
              'circle-opacity': 0.55,
              'circle-stroke-color': '#b45309',
              'circle-stroke-width': 1,
            },
          });
          m.addLayer({
            id: 'bead-awards-counts',
            type: 'symbol',
            source: SRC_ID,
            layout: {
              'text-field': ['to-string', ['get', 'bsl_count']],
              'text-size': 11,
              'text-allow-overlap': false,
            },
            paint: {
              'text-color': '#1f2937',
              'text-halo-color': '#fffbeb',
              'text-halo-width': 1.5,
            },
            filter: ['>=', ['get', 'bsl_count'], 5],
          });
          // Click for popup with project info
          m.on('click', 'bead-awards-circles', (e: any) => {
            const f = e.features?.[0]; if (!f) return;
            const p = f.properties || {};
            const html = `<div style="font:12px system-ui;padding:4px 6px;max-width:280px">
              <div style="font-weight:700;margin-bottom:3px">${p.providers ?? 'Unknown'}</div>
              <div style="color:#475569">${p.projects ?? ''}</div>
              <div style="margin-top:6px;color:#0f172a"><b>${p.bsl_count}</b> awarded BSLs in this hex</div>
              <div style="font-size:10px;color:#94a3b8;margin-top:4px">tech: ${p.technology ?? '—'}</div>
            </div>`;
            new (window as any).maplibregl.Popup({ closeButton: true })
              .setLngLat(e.lngLat).setHTML(html).addTo(m);
          });
          m.on('mouseenter', 'bead-awards-circles', () => { m.getCanvas().style.cursor = 'pointer'; });
          m.on('mouseleave', 'bead-awards-circles', () => { m.getCanvas().style.cursor = ''; });
        }
      } else if (!fc && m.getSource(SRC_ID)) {
        // remove if data cleared
        try { m.removeLayer('bead-awards-circles'); } catch {}
        try { m.removeLayer('bead-awards-counts'); } catch {}
        try { m.removeSource(SRC_ID); } catch {}
      }
    } catch (err) {
      console.error('[bead-overlay]', err);
    }
  }, [beadOverlayQuery.data, mapReady]);

  // Rehydrate per-project UI state (activeZoneId + activeTerritoryPlanId)
  // whenever the selected project changes. Sets a window-level "current
  // project id" that our scoped setters read.
  useEffect(() => {
    (window as any).__currentProjectId = selectedProjectId || null;
    if (!selectedProjectId) {
      setActiveZoneIdRaw(null);
      setActiveTerritoryPlanIdRaw(null);
      return;
    }
    try {
      const t = localStorage.getItem(`ff.activeTerritoryPlanId.${selectedProjectId}`);
      setActiveTerritoryPlanIdRaw((t as any) || null);
      // Only rehydrate activeZoneId if we don't have a territory plan. In territory
      // flow, the default view is the whole-territory business case, not a zone drill-in.
      // User can click a cluster on the map to drill in.
      setActiveZoneIdRaw(null);
    } catch {
      setActiveZoneIdRaw(null);
      setActiveTerritoryPlanIdRaw(null);
    }
  }, [selectedProjectId]);

  // Scrub a stored activeZoneId that no longer exists in this project's zones
  // (e.g. the zone was deleted) so we don't 404-loop forever.
  useEffect(() => {
    if (!activeZoneId || !expansionZones) return;
    const exists = expansionZones.some((z) => z.id === activeZoneId);
    if (!exists) {
      setActiveZoneIdRaw(null);
      try {
        const pid = (window as any).__currentProjectId;
        if (pid) localStorage.removeItem(`ff.activeZoneId.${pid}`);
      } catch {}
    }
  }, [activeZoneId, expansionZones]);

  // Default the plan filter to 'all' on first load so no clusters are hidden.
  // User can narrow via the selector chip.
  useEffect(() => {
    if (activeTerritoryPlanId !== null) return;
    setActiveTerritoryPlanId('all');
  }, [activeTerritoryPlanId, setActiveTerritoryPlanId]);

  // Fly the map to the active zone whenever activeZoneId changes (one-time per id).
  const flownToZoneIdRef = useRef<string | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !activeZoneId) return;
    if (flownToZoneIdRef.current === activeZoneId) return;
    const g = (activeZone as any)?.zone_geojson;
    if (!g) return;  // wait for query to load
    try {
      const b = new maplibregl.LngLatBounds();
      const collect = (c: any) => {
        if (typeof c[0] === 'number') b.extend(c as [number, number]);
        else for (const cc of c) collect(cc);
      };
      collect(g.coordinates);
      if (!b.isEmpty()) {
        map.fitBounds(b, { padding: 80, duration: 800, maxZoom: 15 });
        flownToZoneIdRef.current = activeZoneId;
      }
    } catch {}
  }, [activeZone, activeZoneId, mapReady]);

  // Render the active territory polygon when the plan loads.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource('territory-poly') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    const g = terrPlan?.territory_geojson;
    if (g && terrPlan.status !== 'rejected') {
      src.setData({ type: 'FeatureCollection', features: [{ type: 'Feature', geometry: g, properties: {} }] } as any);
      // First-load fit: if this plan id wasn't fit yet, fit the map to the polygon bounds.
      try {
        const planId = (terrPlan as any)?.id;
        if (planId && (window as any).__terrFitPlanId !== planId) {
          (window as any).__terrFitPlanId = planId;
          const b = new maplibregl.LngLatBounds();
          const coords = g.type === 'Polygon' ? g.coordinates?.[0] ?? [] : g.coordinates?.[0]?.[0] ?? [];
          for (const c of coords) if (Array.isArray(c) && typeof c[0] === 'number') b.extend(c as [number, number]);
          if (!b.isEmpty()) map.fitBounds(b, { padding: 60, duration: 700, maxZoom: 13 });
        }
      } catch {}
    } else {
      src.setData({ type: 'FeatureCollection', features: [] } as any);
    }
  }, [terrPlan, mapReady, styleVersion]);


  // Fade the active territory's polygon when the user is drawing a NEW
  // territory, so it's visually out of the way without disappearing.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const fillOp  = terrDrawMode ? 0.02 : 0.06;
    const lineOp  = terrDrawMode ? 0.25 : 1.0;
    try {
      if (map.getLayer('territory-poly-fill'))    map.setPaintProperty('territory-poly-fill',    'fill-opacity',    fillOp);
      if (map.getLayer('territory-poly-outline')) map.setPaintProperty('territory-poly-outline', 'line-opacity',    lineOp);
    } catch {}
  }, [terrDrawMode, mapReady, styleVersion]);

    // Head-end marker: populate from terrPlan.headend_geojson
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource('headend-src') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    const he = (terrPlan as any)?.headend_geojson;
    if (he?.coordinates) {
      src.setData({ type: 'FeatureCollection', features: [{ type: 'Feature', geometry: he, properties: {} }] } as any);
    } else {
      src.setData({ type: 'FeatureCollection', features: [] } as any);
    }
  }, [terrPlan, mapReady, styleVersion]);

  // POP markers: populate from expansionZones' pop_geojson (fetched per-zone via /expansion/zones/:id response)
  // Since the list endpoint may not include pop_geojson, derive from cluster centroid as fallback.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource('pops-src') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    if (!terrPlan) { src.setData({ type: 'FeatureCollection', features: [] } as any); return; }
    const clusters = (terrPlan as any).clusters ?? [];
    const features = clusters.map((c: any, i: number) => ({
      type: 'Feature' as const,
      geometry: { type: 'Point', coordinates: c.pop_point || c.centroid },
      properties: { name: c.name ?? `Cluster ${i + 1}`, zone_id: c.zone_id, cluster_index: i },
    }));
    src.setData({ type: 'FeatureCollection', features } as any);
  }, [terrPlan, mapReady]);

  // Gateway-tower markers: populate from backbone edges' tower coords.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource('gateway-src') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    if (!terrPlan) { src.setData({ type: 'FeatureCollection', features: [] } as any); return; }
    const seen = new Set<string>();
    const features: any[] = [];
    for (const e of ((terrPlan as any).backbone?.edges ?? [])) {
      for (const seg of (e.hybrid_result?.segments ?? [])) {
        for (const t of [seg.tower_a, seg.tower_b]) {
          if (!t || !t.lng || !t.lat) continue;
          const k = `${t.id}`;
          if (seen.has(k)) continue;
          seen.add(k);
          features.push({
            type: 'Feature', geometry: { type: 'Point', coordinates: [t.lng, t.lat] },
            properties: { id: t.id, city: t.city ?? '', reg: t.reg ?? '' },
          });
        }
      }
    }
    src.setData({ type: 'FeatureCollection', features } as any);
  }, [terrPlan, mapReady]);

  // Head-end placing mode: click map to drop pin
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !headendPlacingMode || !activePlan?.id) return;
    const onClick = async (e: maplibregl.MapMouseEvent) => {
      const { lng, lat } = e.lngLat;
      try {
        await api.patch(`/territory/plans/${activePlan.id}/headend`, { point: [lng, lat] });
        setHeadendPlacingMode(false);
        setHeadendSuggestion(null);
        qc.invalidateQueries({ queryKey: ['territory-plan', activePlan.id] });
        toast.success('Head-end placed');
      } catch (err: any) {
        toast.error(`Place failed: ${err?.message ?? err}`);
      }
    };
    map.on('click', onClick);
    map.getCanvas().style.cursor = 'crosshair';
    return () => {
      map.off('click', onClick);
      map.getCanvas().style.cursor = '';
    };
  }, [headendPlacingMode, mapReady, activePlan?.id, qc]);

  // Fetch head-end suggestion when entering placement mode (once per territory)
  useEffect(() => {
    if (!headendPlacingMode || !activePlan?.id) return;
    let cancelled = false;
    api.get(`/territory/plans/${activePlan.id}/suggest-headend`).then((r: any) => {
      if (cancelled) return;
      if (r?.suggested) setHeadendSuggestion(r.suggested);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [headendPlacingMode, activePlan?.id]);

  // the operator BEAD projects overlay: lazy-fetch when toggled on
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const bslSrc = map.getSource('bead-bsl-source') as maplibregl.GeoJSONSource | undefined;
    if (!bslSrc) return;
    const vis = showVbProjects ? 'visible' : 'none';
    for (const lid of ['bead-bsl-dot']) {
      if (map.getLayer(lid)) map.setLayoutProperty(lid, 'visibility', vis);
    }
    if (!showVbProjects) return;
    let cancelled = false;
    // Project-polygon fetch removed — actual BSL dot data replaces it.
    // Keep the bead-bsl-source populated when the toggle is on.
    if (bslSrc) {
      // Use React Query so flipping the toggle off and back on within a
      // session reuses cached data instead of refetching 9K dots over the wire.
      qc.fetchQuery({
        queryKey: ['bead-locations-all'],
        queryFn: () => fetchLayer('/cache/bead-locations.json?v=arlams1', '/api/bead/locations-all'),
        staleTime: 30 * 60 * 1000,
        gcTime: 60 * 60 * 1000,
      }).then((fc: any) => { if (!cancelled && fc) bslSrc.setData(fc); }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [showVbProjects, mapReady, styleVersion]);

  // the operator RDOF overlay: lazy-fetch when toggled on (mirrors BEAD pattern)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const rdofSrc = map.getSource('rdof-bsl-source') as maplibregl.GeoJSONSource | undefined;
    if (!rdofSrc) return;
    const vis = showRdofLocations ? 'visible' : 'none';
    for (const lid of ['rdof-bsl-dot']) {
      if (map.getLayer(lid)) map.setLayoutProperty(lid, 'visibility', vis);
    }
    if (!showRdofLocations) return;
    let cancelled = false;
    // Use React Query so flipping the toggle off and back on within a session
    // reuses cached data instead of refetching 10K dots over the wire.
    qc.fetchQuery({
      queryKey: ['rdof-locations-all'],
      queryFn: () => fetchLayer('/cache/rdof-locations.json?v=arlams1', '/api/bead/rdof-locations-all'),
      staleTime: 30 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
    }).then((fc: any) => { if (!cancelled && fc) rdofSrc.setData(fc); }).catch(() => {});
    return () => { cancelled = true; };
  }, [showRdofLocations, mapReady, styleVersion]);

  // 2026-06-01 Towers overlay (FCC ASR via GeoHub). Bbox-scoped, so refetches
  // when the user pans the map. Distinctive orange marker.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource('towers-source') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    const vis = showTowers ? 'visible' : 'none';
    for (const lid of ['towers-dot', 'towers-label']) {
      if (map.getLayer(lid)) map.setLayoutProperty(lid, 'visibility', vis);
    }
    if (!showTowers || !bbox) return;
    let cancelled = false;
    qc.fetchQuery({
      queryKey: ['towers', bbox],
      queryFn: async () => {
        const r = await fetch(`/api/map/towers?bbox=${encodeURIComponent(bbox)}&limit=2000`, { credentials: 'include' });
        return r.ok ? await r.json() : null;
      },
      staleTime: 5 * 60 * 1000,
    }).then((fc: any) => { if (!cancelled && fc) src.setData(fc); }).catch(() => {});
    return () => { cancelled = true; };
  }, [showTowers, bbox, mapReady, styleVersion, qc]);

  // BEAD Project Areas overlay: shows each BEAD project boundary as a
  // filled colored polygon with label. Lazy-fetch on first enable.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    // Ensure source + layers exist (idempotent)
    if (!map.getSource('bead-projects-source')) {
      map.addSource('bead-projects-source', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
      // Color expression: hash project_code to a palette.
      const PALETTE = [
        '#ef4444','#f59e0b','#eab308','#10b981','#14b8a6','#06b6d4',
        '#3b82f6','#6366f1','#8b5cf6','#a855f7','#d946ef','#ec4899',
        '#f43f5e','#84cc16','#0ea5e9','#22c55e','#fb923c','#c026d3',
        '#fbbf24','#fde047','#34d399','#0284c7','#7c3aed','#c084fc',
      ];
      const colorExpr: any = ['match', ['get', 'project_code']];
      // This is populated dynamically when data loads; placeholder first.
      map.addLayer({
        id: 'bead-projects-fill', type: 'fill', source: 'bead-projects-source',
        paint: { 'fill-color': '#8b5cf6', 'fill-opacity': 0.20 },
      } as any);
      map.addLayer({
        id: 'bead-projects-outline', type: 'line', source: 'bead-projects-source',
        paint: { 'line-color': '#8b5cf6', 'line-width': 1.5, 'line-opacity': 0.7 },
      } as any);
      map.addLayer({
        id: 'bead-projects-label', type: 'symbol', source: 'bead-projects-source',
        layout: {
          'text-field': ['format',
            ['get', 'project_name'], { 'font-scale': 1 },
            '\n', {},
            ['get', 'project_code'], { 'font-scale': 0.75 },
          ] as any,
          'text-size': 12,
          'text-anchor': 'center',
          'text-allow-overlap': false,
        },
        paint: { 'text-color': '#fff', 'text-halo-color': '#0b1220', 'text-halo-width': 2.5 },
      } as any);
      // After data loads we'll rebuild the fill color expression.
      void PALETTE; void colorExpr;
    }

    const vis = showBeadProjects ? 'visible' : 'none';
    for (const lid of ['bead-projects-fill','bead-projects-outline','bead-projects-label']) {
      if (map.getLayer(lid)) map.setLayoutProperty(lid, 'visibility', vis);
    }
    if (!showBeadProjects) return;

    let cancelled = false;
    fetch('/api/bead/project-boundaries', { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((gj) => {
        if (cancelled || !gj) return;
        // Build per-project color expression from returned codes
        const codes = Array.from(new Set((gj.features ?? []).map((f: any) => f.properties?.project_code).filter(Boolean))) as string[];
        const palette = ['#ef4444','#f59e0b','#eab308','#10b981','#14b8a6','#06b6d4','#3b82f6','#6366f1','#8b5cf6','#a855f7','#d946ef','#ec4899','#f43f5e','#84cc16','#0ea5e9','#22c55e','#fb923c','#c026d3','#fbbf24','#fde047','#34d399','#0284c7','#7c3aed','#c084fc'];
        const matchArr: any[] = ['match', ['get', 'project_code']];
        codes.forEach((code, i) => { matchArr.push(code, palette[i % palette.length]); });
        matchArr.push('#8b5cf6');
        try {
          map.setPaintProperty('bead-projects-fill', 'fill-color', matchArr as any);
          map.setPaintProperty('bead-projects-outline', 'line-color', matchArr as any);
        } catch {}
        const src = map.getSource('bead-projects-source') as maplibregl.GeoJSONSource | undefined;
        if (src) src.setData(gj);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [showBeadProjects, mapReady, styleVersion]);

  // BEAD Project Areas: click a polygon to spin up a Territory Plan from
  // its geometry (same flow as Finish-territory after a hand-draw).
  // Latest mutation + project id stashed in refs so the click handler
  // doesn't need to be re-bound on every render.
  const createTerrMutRef = useRef(createTerrMut);
  const selectedProjectIdRef = useRef(selectedProjectId);
  useEffect(() => { createTerrMutRef.current = createTerrMut; }, [createTerrMut]);
  useEffect(() => { selectedProjectIdRef.current = selectedProjectId; }, [selectedProjectId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (!map.getLayer('bead-projects-fill')) return;

    const onClick = (e: any) => {
      if (!showBeadProjects) return;
      const f = e.features?.[0]; if (!f) return;
      const projId = selectedProjectIdRef.current;
      if (!projId) {
        toast.info('Select a project first to attach the new territory.');
        return;
      }
      const projName = f.properties?.project_name ?? f.properties?.project_code ?? 'this BEAD area';
      // Pull the outer ring(s) from Polygon or MultiPolygon geometry.
      const geom = f.geometry;
      let ring: [number, number][] | null = null;
      if (geom?.type === 'Polygon' && Array.isArray(geom.coordinates?.[0])) {
        ring = geom.coordinates[0] as [number, number][];
      } else if (geom?.type === 'MultiPolygon' && Array.isArray(geom.coordinates?.[0]?.[0])) {
        // Pick the largest sub-polygon by simple bbox area heuristic.
        const polys = geom.coordinates as number[][][][];
        let best = polys[0]?.[0] ?? null;
        let bestArea = 0;
        for (const poly of polys) {
          const r = poly[0]; if (!r) continue;
          let minX=Infinity, minY=Infinity, maxX=-Infinity, maxY=-Infinity;
          for (const c of r) {
            if (c[0] < minX) minX = c[0]; if (c[1] < minY) minY = c[1];
            if (c[0] > maxX) maxX = c[0]; if (c[1] > maxY) maxY = c[1];
          }
          const a = (maxX - minX) * (maxY - minY);
          if (a > bestArea) { bestArea = a; best = r; }
        }
        ring = best as [number, number][] | null;
      }
      if (!ring || ring.length < 4) {
        toast.error('Could not read polygon geometry from BEAD area.');
        return;
      }
      const ok = confirm(
        `Create a new Territory Plan from BEAD area "${projName}" and run analyze?\n\n` +
        `(${ring.length - 1} vertices · same flow as drawing it by hand.)`
      );
      if (!ok) return;
      createTerrMutRef.current.mutate(ring);
    };

    const onEnter = () => { if (showBeadProjects) map.getCanvas().style.cursor = 'pointer'; };
    const onLeave = () => { map.getCanvas().style.cursor = ''; };

    map.on('click', 'bead-projects-fill', onClick);
    map.on('mouseenter', 'bead-projects-fill', onEnter);
    map.on('mouseleave', 'bead-projects-fill', onLeave);
    return () => {
      map.off('click', 'bead-projects-fill', onClick);
      map.off('mouseenter', 'bead-projects-fill', onEnter);
      map.off('mouseleave', 'bead-projects-fill', onLeave);
    };
  }, [showBeadProjects, mapReady, styleVersion]);

  // Soil overlay: SSURGO polygons clipped to active territory geometry,
  // colored by classified construction method. Only fetches when both the
  // checkbox is on AND we have an active territory plan to clip against.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    if (!map.getSource('soil-source')) {
      map.addSource('soil-source', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
      map.addLayer({
        id: 'soil-fill', type: 'fill', source: 'soil-source',
        paint: {
          'fill-color': ['match', ['get', 'method'],
            'plow',             '#3b82f6',
            'trench',           '#22c55e',
            'rock_saw',         '#f59e0b',
            'directional_bore', '#ef4444',
            '#94a3b8',
          ],
          'fill-opacity': 0.35,
        },
      } as any);
      map.addLayer({
        id: 'soil-outline', type: 'line', source: 'soil-source',
        paint: { 'line-color': '#0f172a', 'line-width': 0.4, 'line-opacity': 0.4 },
      } as any);
      // Click popup with soil-unit detail.
      map.on('click', 'soil-fill', (e: any) => {
        const f = e.features?.[0]; if (!f) return;
        const p = f.properties || {};
        const methodLabel: Record<string, string> = {
          plow: 'Plow', trench: 'Trench', rock_saw: 'Rock saw', directional_bore: 'Directional bore',
        };
        const html = `
          <div style="font-family: system-ui; font-size: 12px; max-width: 240px;">
            <div style="font-weight: 700; margin-bottom: 4px;">${p.muname ?? '(unnamed)'}</div>
            <div style="color: #64748b; font-size: 11px;">mukey ${p.mukey}</div>
            <div style="margin-top: 6px;">Method: <strong style="color: ${
              p.method === 'plow' ? '#3b82f6'
              : p.method === 'trench' ? '#22c55e'
              : p.method === 'rock_saw' ? '#f59e0b'
              : p.method === 'directional_bore' ? '#ef4444' : '#64748b'
            }">${methodLabel[p.method] ?? p.method ?? '—'}</strong></div>
            ${p.drainage ? `<div style="color: #475569; font-size: 11px;">Drainage: ${p.drainage}</div>` : ''}
            ${p.hydro_group ? `<div style="color: #475569; font-size: 11px;">Hydro group: ${p.hydro_group}</div>` : ''}
            ${p.slope_pct != null ? `<div style="color: #475569; font-size: 11px;">Slope: ${Number(p.slope_pct).toFixed(1)}%</div>` : ''}
            ${p.bedrock_in != null ? `<div style="color: #475569; font-size: 11px;">Bedrock: ${Number(p.bedrock_in).toFixed(0)} in</div>` : ''}
            ${p.hydric_pct != null ? `<div style="color: #475569; font-size: 11px;">Hydric: ${Number(p.hydric_pct).toFixed(0)}%</div>` : ''}
          </div>`;
        new (maplibregl as any).Popup({ closeButton: true, maxWidth: '260px' })
          .setLngLat(e.lngLat).setHTML(html).addTo(map);
      });
      map.on('mouseenter', 'soil-fill', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'soil-fill', () => { map.getCanvas().style.cursor = ''; });
    }

    const vis = (showSoil && activePlan?.id) ? 'visible' : 'none';
    for (const lid of ['soil-fill','soil-outline']) {
      if (map.getLayer(lid)) map.setLayoutProperty(lid, 'visibility', vis);
    }
    if (!showSoil || !activePlan?.id) return;

    let cancelled = false;
    setSoilLoading(true);
    fetch(`/api/geohub-data/soil-polygons?territory_plan_id=${activePlan.id}`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((gj) => {
        if (cancelled || !gj) return;
        const src = map.getSource('soil-source') as maplibregl.GeoJSONSource | undefined;
        if (src) src.setData(gj);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setSoilLoading(false); });
    return () => { cancelled = true; };
  }, [showSoil, activePlan?.id, mapReady, styleVersion]);

  // Populate the all-zones overlay (every zone except the active one).
  // Zones from committed plans are hidden unless that plan is the one the
  // user currently has selected — mirrors the Scenarios panel filter
  // (p.status !== 'committed' || p.id === selectedPlanId) so that clearing
  // all scenarios actually removes the purple polygons from the map.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource('expansion-all-zones') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    // 2026-05-27: tightened. Was only hiding 'committed' non-selected plans,
    // which let zones from 'complete' sibling plans (e.g. Phillips SA + Phillips
    // SA ext) leak through and render as phantom polygons. Now: only show zones
    // that either belong to the selected plan or have no parent plan.
    const features = (expansionZones || [])
      .filter((z) => z.id !== activeZoneId && (z as any).zone_geojson)
      .filter((z) => {
        const tpid = (z as any).territory_plan_id;
        if (!tpid) return true;                    // free-floating zone, always show
        return tpid === selectedPlanId;            // otherwise must match selection
      })
      .map((z) => ({
        type: 'Feature' as const,
        id: z.id as any,
        geometry: (z as any).zone_geojson,
        properties: { zone_id: z.id, name: z.name, status: z.status },
      }));
    src.setData({ type: 'FeatureCollection', features } as any);
  }, [expansionZones, activeZoneId, mapReady, terrPlans, selectedPlanId]);

  // Populate cluster centroid markers from terrPlan
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource('clusters') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    if (!terrPlan) { src.setData({ type: 'FeatureCollection', features: [] } as any); return; }
    const clusters = (terrPlan as any).clusters ?? [];
    const pc = (terrPlan as any).summary?.per_cluster ?? [];
    const buildSet = new Set(terrBuildIndices);
    const features = clusters.map((c: any, i: number) => ({
      type: 'Feature' as const,
      geometry: { type: 'Point', coordinates: c.centroid },
      properties: {
        passings: c.passings,
        label: (() => {
          const pb = pc[i]?.payback_months;
          const pbStr = pb == null || !isFinite(pb) ? '' : pb < 12 ? ` · ${pb.toFixed(1)}mo` : ` · ${(pb / 12).toFixed(1)}yr`;
          return `#${i + 1} · ${(c.passings ?? 0).toLocaleString()}${pbStr}`;
        })(),
        payback: pc[i]?.payback_months ?? 999,
        selected: buildSet.has(i),
      },
    }));
    src.setData({ type: 'FeatureCollection', features } as any);
  }, [terrPlan, terrBuildIndices, mapReady]);

  // 2026-05-27: push full-plant snapshot into the dedicated plan-* sources.
  // Renders the entire saved fiber design for the active plan in one go.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;
    const empty = { type: 'FeatureCollection', features: [] } as any;
    const segSrc  = map.getSource('plan-segments') as maplibregl.GeoJSONSource | undefined;
    const nodeSrc = map.getSource('plan-nodes')    as maplibregl.GeoJSONSource | undefined;
    const dropSrc = map.getSource('plan-drops')    as maplibregl.GeoJSONSource | undefined;
    if (segSrc)  segSrc.setData(planFullPlant?.segments ?? empty);
    if (nodeSrc) nodeSrc.setData(planFullPlant?.nodes    ?? empty);
    if (dropSrc) dropSrc.setData(planFullPlant?.drops    ?? empty);
  }, [planFullPlant, mapReady, activePlan?.id]);

  // Populate backbone lines from terrPlan.backbone.edges (colored by mode)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource('backbone') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    if (!terrPlan) { src.setData({ type: 'FeatureCollection', features: [] } as any); return; }
    // Build a coord-keyed map so overlapping fiber segments (shared trunk)
    // render ONCE with a 'load' count = number of edges using it. Line width
    // scales with load so the trunk near the head-end gets visibly thicker.
    type AggKey = string;
    const fiberAgg = new Map<AggKey, { coords: [number, number][]; mode: string; load: number }>();
    const wirelessAgg = new Map<AggKey, { coords: [number, number][]; mode: string; load: number }>();
    const fallbackFeats: any[] = [];
    const keyCoords = (cs: [number, number][]): AggKey => {
      // Canonical key: round to 5 decimals (~1 m) + sort endpoints so
      // reverse-direction duplicates collapse too.
      const r = (v: number) => Math.round(v * 100000) / 100000;
      const first = cs[0], last = cs[cs.length - 1];
      const a = `${r(first[0])},${r(first[1])}`;
      const b = `${r(last[0])},${r(last[1])}`;
      return a < b ? `${a}|${b}` : `${b}|${a}`;
    };
    for (const e of ((terrPlan as any).backbone?.edges ?? [])) {
      const segs = e.hybrid_result?.segments ?? [];
      let anySeg = false;
      for (const seg of segs) {
        if (!(terrShowModes as any)[seg.mode]) { anySeg = true; continue; }
        if (!seg.coords || seg.coords.length < 2) continue;
        if (seg.mode === 'wireless_ptp' && seg.tower_a && seg.tower_b) {
          const a = seg.tower_a.id, b = seg.tower_b.id;
          const key = a < b ? `tower:${a}|${b}` : `tower:${b}|${a}`;
          const prev = wirelessAgg.get(key);
          if (prev) prev.load += 1;
          else wirelessAgg.set(key, { coords: seg.coords, mode: seg.mode, load: 1 });
        } else {
          const k = keyCoords(seg.coords);
          const prev = fiberAgg.get(k);
          if (prev) prev.load += 1;
          else fiberAgg.set(k, { coords: seg.coords, mode: seg.mode, load: 1 });
        }
        anySeg = true;
      }
      if (!anySeg && e.from_centroid && e.to_centroid) {
        fallbackFeats.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: [e.from_centroid, e.to_centroid] }, properties: { mode: 'unknown', load: 1 } });
      }
    }
    const bbFeatures: any[] = [
      ...fallbackFeats,
      ...[...fiberAgg.values()].map((v) => ({ type: 'Feature', geometry: { type: 'LineString', coordinates: v.coords }, properties: { mode: v.mode, load: v.load } })),
      ...[...wirelessAgg.values()].map((v) => ({ type: 'Feature', geometry: { type: 'LineString', coordinates: v.coords }, properties: { mode: v.mode, load: v.load } })),
    ];
    src.setData({ type: 'FeatureCollection', features: bbFeatures } as any);
  }, [terrPlan, terrShowModes, mapReady]);

  // Combined FTTH overlay: fetch each selected cluster's design and merge segments
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const src = map.getSource('ftth') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    if (!terrPlan) { src.setData({ type: 'FeatureCollection', features: [] } as any); return; }
    const clusters = (terrPlan as any).clusters ?? [];
    const buildSet = new Set(terrBuildIndices);
    const showAll = (terrPlan as any).status !== 'complete';
    const zoneIds: string[] = clusters
      .map((c: any, i: number) => ((showAll || buildSet.has(i)) && c.zone_id ? c.zone_id : null))
      .filter(Boolean) as string[];
    if (zoneIds.length === 0) { src.setData({ type: 'FeatureCollection', features: [] } as any); return; }
    let cancelled = false;
    Promise.all(zoneIds.map((id) => api.get(`/expansion/zones/${id}/design`).catch(() => null)))
      .then((results) => {
        if (cancelled) return;
        const features: any[] = [];
        for (const r of results as any[]) {
          if (!r) continue;
          for (const f of r.segments?.features ?? []) features.push(f);
        }
        src.setData({ type: 'FeatureCollection', features } as any);
      });
    return () => { cancelled = true; };
  }, [terrPlan?.id, (terrPlan as any)?.status, terrBuildIndices.join(','), mapReady]);


  // Render active zone's extension plant (segments + nodes) + passings split dots.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const segSrc = map.getSource('expansion-segments') as maplibregl.GeoJSONSource | undefined;
    const nodeSrc = map.getSource('expansion-nodes') as maplibregl.GeoJSONSource | undefined;
    const dotSrc = map.getSource('expansion-dots') as maplibregl.GeoJSONSource | undefined;

    if (segSrc)  segSrc.setData(activeZoneDesign?.segments ?? { type: 'FeatureCollection', features: [] });
    if (nodeSrc) nodeSrc.setData(activeZoneDesign?.nodes ?? { type: 'FeatureCollection', features: [] });

    // Passings dots
    if (dotSrc) dotSrc.setData(activeZonePassings ?? { type: 'FeatureCollection', features: [] });

    // Backhaul path toggle: when wireless is selected, hide the fiber
    // feeder_trunk but ALWAYS keep distribution (in-zone FTTH) visible.
    // When lease or fiber is selected, show everything.
    if (map.getLayer('expansion-segments')) {
      const hideBackhaulFiber = selectedBackhaulType === 'wireless_ptp';
      map.setFilter('expansion-segments',
        hideBackhaulFiber ? ['!=', ['get', 'type'], 'feeder_trunk'] : null);
      map.setFilter('expansion-segments-casing',
        hideBackhaulFiber ? ['!=', ['get', 'type'], 'feeder_trunk'] : null);
      // Force visibility on (might have been toggled off previously)
      map.setLayoutProperty('expansion-segments', 'visibility', 'visible');
      map.setLayoutProperty('expansion-segments-casing', 'visibility', 'visible');
    }

    // Fit to extension on first analyze
    if (activeZoneDesign?.segments?.features?.length) {
      try {
        const b = new maplibregl.LngLatBounds();
        for (const f of activeZoneDesign.segments.features) {
          const coords = (f.geometry as any)?.coordinates ?? [];
          for (const c of coords) if (Array.isArray(c) && typeof c[0] === 'number') b.extend(c as [number, number]);
        }
        if (!b.isEmpty()) map.fitBounds(b, { padding: 80, duration: 600, maxZoom: 15 });
      } catch { /* ignore */ }
    }
  }, [activeZoneDesign, activeZonePassings, selectedBackhaulType, mapReady]);

  // Render wireless hop lines when wireless backhaul is selected.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    // Add source/layer if not present
    if (!map.getSource('wireless-hops')) {
      map.addSource('wireless-hops', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
      map.addLayer({
        id: 'wireless-hops-line', type: 'line', source: 'wireless-hops',
        paint: {
          'line-color': '#dc2626',
          'line-width': 3,
          'line-dasharray': [6, 3],
          'line-opacity': 0.9,
        },
        layout: { 'line-cap': 'round' },
      });
      map.addLayer({
        id: 'wireless-hops-towers', type: 'circle', source: 'wireless-hops',
        filter: ['==', '$type', 'Point'] as any,
        paint: {
          'circle-radius': 8,
          'circle-color': '#dc2626',
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 3,
        },
      });
    }

    const src = map.getSource('wireless-hops') as maplibregl.GeoJSONSource;
    if (selectedBackhaulType === 'wireless_ptp' && wirelessHops && wirelessHops.length > 0) {
      const features: any[] = [];
      for (const hop of wirelessHops) {
        features.push({
          type: 'Feature',
          geometry: { type: 'LineString', coordinates: [[hop.from.lng, hop.from.lat], [hop.to.lng, hop.to.lat]] },
          properties: { radio: hop.radio?.name ?? '', throughput: hop.throughput_mbps, verdict: hop.verdict },
        });
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [hop.from.lng, hop.from.lat] },
          properties: { city: hop.from.city, reg: hop.from.reg, agl_m: hop.from.agl_m },
        });
        features.push({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [hop.to.lng, hop.to.lat] },
          properties: { city: hop.to.city, reg: hop.to.reg, agl_m: hop.to.agl_m },
        });
      }
      src.setData({ type: 'FeatureCollection', features });
    } else {
      src.setData({ type: 'FeatureCollection', features: [] });
    }
  }, [selectedBackhaulType, wirelessHops, mapReady]);

  // ---- Auto-design ----
  async function runAutoDesign() {
    // Loud diagnostics: make every failed precondition visible instead of
    // the silent return we had before. Shows a banner regardless so you can
    // at least tell the click fired.
    // eslint-disable-next-line no-console
    console.log('[runAutoDesign] fired', {
      hasPlant: !!plant, plantId: plant?.id,
      hasPolygon: !!polygonRing, polygonVertices: polygonRing?.length ?? 0,
      hasOlt: !!oltLngLat, olt: oltLngLat,
    });
    if (!plant) {
      toast.error('Plant is still loading. Wait a second and try again.');
      return;
    }
    if (!polygonRing || polygonRing.length < 4) {
      toast.warning('No polygon drawn yet. Use Draw Polygon → click 3+ points → Finish polygon.');
      return;
    }
    if (!oltLngLat) {
      toast.warning('No OLT placed yet. Click Place OLT then click the map where the OLT sits.');
      return;
    }
    setDesignBusy(true);
    setDesignStatus('Contacting backend…');
    toast.info('Auto-design running. Pulling road network and running shortest-path…', 8000);
    try {
      const res = await api.post('/auto-design/run', {
        plant_id: plant.id,
        polygon: polygonRing,
        olt: { lng: oltLngLat[0], lat: oltLngLat[1] },
        specs,
      });
      const feetTotal = Math.round((res.total_m ?? 0) * 3.28084).toLocaleString();
      setDesignStatus(
        `${res.segments_created} segments, ${res.nodes_created} nodes · ${feetTotal} ft · ${res.passings ?? 0} passings @ ${res.passing_buffer_ft ?? 300} ft buffer`
      );
      toast.success(`Auto-design complete: ${feetTotal} ft, ${res.passings ?? 0} passings.`);
      // Refetch graph THEN trigger animation. We await refetchGraph so the
      // animation effect sees the new feature data.
      await refetchGraph();
      setAnimatingDesign(true);
      refetchPassings();
      refetchBom();
      qc.invalidateQueries({ queryKey: ['buildings'] });
    } catch (e) {
      const msg = (e as Error).message;
      setDesignStatus('Failed: ' + msg);
      toast.error('Auto-design failed: ' + msg);
    } finally {
      setDesignBusy(false);
    }
  }

  function clearDesign() {
    setPolygonRing(null);
    setOltLngLat(null);
    setPlacingOlt(false);
    setPolygonMode(false);
    setDesignStatus(null);
    setBanner(null);
  }

  async function promoteToBuilt() {
    if (!plant) return;
    if (!confirm('Promote every PLANNED element in this plant to BUILT? Elements will then appear on the Built page.')) return;
    try {
      const r = await api.post(`/plants/${plant.id}/promote`, {});
      toast.success(`Promoted: ${r.segments_promoted} segments, ${r.nodes_promoted} nodes.`);
      refetchGraph();
    } catch (e) {
      toast.error('Promote failed: ' + (e as Error).message);
    }
  }

  async function resetPlant() {
    if (!plant) return;
    if (!confirm('Delete every node, segment, and drop in this plant? This cannot be undone.')) return;
    setResetBusy(true);
    try {
      await api.post(`/plants/${plant.id}/clear`, {});
      clearDesign();
      toast.success('Plant cleared.');
      refetchGraph();
    } catch (e) {
      toast.error('Clear failed: ' + (e as Error).message);
    } finally {
      setResetBusy(false);
    }
  }

  // ---- Manual actions ----
  async function handleDelete() {
    if (!selected) return;
    // MapLibre's queryRenderedFeatures sometimes returns feature.id as
    // undefined even when the source GeoJSON had "id". We now also stash
    // the id into properties on the backend, so fall back to that.
    const fid = (selected.feature as any).id ?? (selected.feature.properties as any)?.id;
    if (!fid) {
      toast.error('Could not determine ID of selected item.');
      return;
    }
    try {
      if (selected.kind === 'node')    await api.del(`/nodes/${fid}`);
      if (selected.kind === 'segment') await api.del(`/segments/${fid}`);
      setSelected(null);
      refetchGraph();
    } catch (e) {
      toast.error('Delete failed: ' + (e as Error).message);
    }
  }

  async function handleGenerateDrops(buffer_m: number, max_drop_m: number) {
    if (!plant || !selected || selected.kind !== 'segment') return;
    setGeneratingDrops(true);
    setLastDropResult(null);
    try {
      const res = await api.post('/auto-design/generate-drops', {
        plant_id: plant.id,
        segment_id: selected.feature.id,
        buffer_m, max_drop_m,
        placement: 'buried',
      });
      setLastDropResult({ created: res.created, skipped: res.skipped, considered: res.considered ?? 0 });
      toast.success(`Generated ${res.created} drops of ${res.considered ?? 0} nearby buildings.`);
      refetchGraph();
    } catch (e) {
      toast.error('Drop generation failed: ' + (e as Error).message);
    } finally {
      setGeneratingDrops(false);
    }
  }

  const statText = useMemo(() => {
    if (polygonMode)  return `Polygon: ${polyDraw.ring.length} pts. Double-click to close.`;
    if (expansionMode) return `Zone: ${expansionDraw.ring.length} pts. Double-click to close zone.`;
    if (placingOlt)  return 'Click the map to place the OLT.';
    if (tab === 'manual' && mode === 'place_node') return `Click map to place ${pendingNodeType.replace('_',' ')}`;
    if (tab === 'manual' && mode === 'draw_segment') return 'Click map to add vertices. Double-click to finish.';
    return graph ? `${graph.stats.node_count} nodes · ${graph.stats.segment_count} segments · ${graph.stats.drop_count} drops` : 'Ready';
  }, [polygonMode, polyDraw.ring.length, expansionMode, expansionDraw.ring.length, placingOlt, tab, mode, pendingNodeType, graph]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: terrPlan ? '1fr 400px' : '1fr 320px', gap: 16, height: 'calc(100vh - var(--topbar-h) - 48px)' }}>
      <div className="map-wrap" style={{ height: '100%', position: 'relative' }}>
        <div ref={containerRef} style={{ height: '100%', width: '100%' }} />
        {/*
          BEAD Awards overlay panel hidden 2026-05-07 — pulled raw award
          shapes from GeoHub (TX Aristotle awards by default), which
          conflicted with the operator-only narrative for the Ready.net
          demo. The data feed + map layer wiring is intact below; only
          the floating top-right control is suppressed. Re-enable by
          flipping the conditional, or wire to a per-tenant feature flag
          when we onboard a multi-state ISP that wants the overlay.
        */}
        {false && (
          <div style={{
            position: 'absolute', top: 12, right: 12, zIndex: 10,
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)', padding: '8px 10px',
            fontSize: 12, fontFamily: 'system-ui',
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={layerVis.bead_awards}
                onChange={(e) => setLayerVis(v => ({ ...v, bead_awards: e.target.checked }))}
              />
              <span style={{ color: '#b45309' }}>🏆 BEAD Awards</span>
            </label>
            {layerVis.bead_awards && (
              <div style={{ marginTop: 6, display: 'flex', gap: 4 }}>
                <select
                  value={beadState}
                  onChange={(e) => setBeadState(e.target.value)}
                  style={{ fontSize: 11, padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: 3 }}
                >
                  <option value="TX">TX</option>
                  <option value="WY">WY</option>
                  <option value="CO">CO</option>
                  <option value="NE">NE</option>
                </select>
                <input
                  type="text"
                  value={beadProvider}
                  onChange={(e) => setBeadProvider(e.target.value)}
                  placeholder="Provider"
                  style={{ width: 120, fontSize: 11, padding: '2px 4px', border: '1px solid #cbd5e1', borderRadius: 3 }}
                />
              </div>
            )}
            {layerVis.bead_awards && beadOverlayQuery.data?.summary && (
              <div style={{ marginTop: 6, fontSize: 10, color: '#64748b', maxWidth: 200 }}>
                {beadOverlayQuery.data.summary.h3_cells} cells ·{' '}
                {beadOverlayQuery.data.summary.total_bsls.toLocaleString()} BSLs
              </div>
            )}
          </div>
        )}
        <div className="map-statbar">{statText} · <span style={{ opacity: 0.5 }}>{BUILD_STAMP}</span></div>
        {banner && (
          <div className={`map-banner map-banner--${banner.kind}`}>
            <span>{banner.text}</span>
            <button onClick={() => setBanner(null)} aria-label="Dismiss">×</button>
          </div>
        )}
        {/* Expansion Studies FAB + zone list */}
        <div className="expansion-fab">

          {/* Place head-end button (shown when no head-end exists) */}
          {terrPlan && !(terrPlan as any).headend_geojson && !headendPlacingMode && (
            <button
              className="btn btn--primary"
              onClick={() => setHeadendPlacingMode(true)}
              title="Click the map to drop the head-end location."
              style={{ fontSize: 13, background: '#8b5cf6', borderColor: '#7c3aed' }}
            >
              ◆ Place head-end
            </button>
          )}
          {headendPlacingMode && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 8, background: '#0b1220cc', borderRadius: 6, color: '#fff', maxWidth: 280 }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>Click the map to place the head-end</div>
              {headendSuggestion && (
                <button
                  onClick={async () => {
                    if (!activePlan?.id || !headendSuggestion) return;
                    try {
                      await api.patch(`/territory/plans/${activePlan.id}/headend`, { point: [headendSuggestion.lng, headendSuggestion.lat] });
                      setHeadendPlacingMode(false);
                      setHeadendSuggestion(null);
                      qc.invalidateQueries({ queryKey: ['territory-plan', activePlan.id] });
                      toast.success('Used suggested head-end');
                    } catch (err: any) { toast.error(`Place failed: ${err?.message ?? err}`); }
                  }}
                  style={{ fontSize: 11, padding: '4px 8px', background: '#06b6d4', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                >
                  Use suggested ({headendSuggestion.distance_km} km away)
                </button>
              )}
              <button onClick={() => { setHeadendPlacingMode(false); setHeadendSuggestion(null); }} style={{ fontSize: 11, padding: '4px 8px', background: 'transparent', color: '#fff', border: '1px solid #334', borderRadius: 4, cursor: 'pointer' }}>Cancel</button>
            </div>
          )}
          {terrPlan && (terrPlan as any).headend_geojson && !headendPlacingMode && (
            <button
              className="btn"
              onClick={() => setHeadendPlacingMode(true)}
              title="Move the head-end"
              style={{ fontSize: 11 }}
            >
              ◆ Move head-end
            </button>
          )}
          {/* BEAD overlay toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '4px 8px', borderRadius: 6, background: '#0b1220cc', cursor: 'pointer', color: '#fff', userSelect: 'none', width: 'fit-content' }}>
            <input type="checkbox" checked={showVbProjects} onChange={(e) => setShowVbProjects(e.target.checked)} style={{ margin: 0, accentColor: '#8b5cf6' }} />
            <span style={{ fontWeight: 600 }}>BEAD</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '4px 8px', borderRadius: 6, background: '#0b1220cc', cursor: 'pointer', color: '#fff', userSelect: 'none', width: 'fit-content' }}>
            <input type="checkbox" checked={showRdofLocations} onChange={(e) => setShowRdofLocations(e.target.checked)} style={{ margin: 0, accentColor: '#06b6d4' }} />
            <span style={{ fontWeight: 600 }}>RDOF</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '4px 8px', borderRadius: 6, background: '#0b1220cc', cursor: 'pointer', color: '#fff', userSelect: 'none', width: 'fit-content' }}>
            <input type="checkbox" checked={showTowers} onChange={(e) => setShowTowers(e.target.checked)} style={{ margin: 0, accentColor: '#ea580c' }} />
            <span style={{ fontWeight: 600 }}>Towers</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '4px 8px', borderRadius: 6, background: '#0b1220cc', cursor: 'pointer', color: '#fff', userSelect: 'none', width: 'fit-content' }}>
            <input type="checkbox" checked={showBeadProjects} onChange={(e) => setShowBeadProjects(e.target.checked)} style={{ margin: 0, accentColor: '#f59e0b' }} />
            <span style={{ fontWeight: 600 }}>BEAD Project Areas</span>
          </label>
          {activePlan?.id && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '4px 8px', borderRadius: 6, background: '#0b1220cc', cursor: 'pointer', color: '#fff', userSelect: 'none', width: 'fit-content' }}
                   title="SSURGO soil polygons clipped to this territory, colored by construction method">
              <input type="checkbox" checked={showSoil} onChange={(e) => setShowSoil(e.target.checked)} style={{ margin: 0, accentColor: '#22c55e' }} />
              <span style={{ fontWeight: 600 }}>Soil overlay</span>
              {soilLoading && <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 4 }}>loading…</span>}
              {showSoil && !soilLoading && (
                <span style={{ display: 'inline-flex', gap: 4, marginLeft: 6 }}>
                  <span title="Plow" style={{ width: 8, height: 8, borderRadius: 2, background: '#3b82f6' }} />
                  <span title="Trench" style={{ width: 8, height: 8, borderRadius: 2, background: '#22c55e' }} />
                  <span title="Rock saw" style={{ width: 8, height: 8, borderRadius: 2, background: '#f59e0b' }} />
                  <span title="Directional bore" style={{ width: 8, height: 8, borderRadius: 2, background: '#ef4444' }} />
                </span>
              )}
            </label>
          )}

          {/* 2026-05-27: gates the 72-OLT Pulaski plant graph + buildings overlay
              that used to render by default and confuse BEAD compliance users. */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '4px 8px', borderRadius: 6, background: '#0b1220cc', cursor: 'pointer', color: '#fff', userSelect: 'none', width: 'fit-content' }}>
            <input type="checkbox" checked={layerVis.plant} onChange={(e) => setLayerVis(v => ({...v, plant: e.target.checked}))} style={{ margin: 0, accentColor: '#f59e0b' }} />
            <span style={{ fontWeight: 600 }}>Plant infrastructure</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '4px 8px', borderRadius: 6, background: '#0b1220cc', cursor: 'pointer', color: '#fff', userSelect: 'none', width: 'fit-content' }}>
            <input type="checkbox" checked={layerVis.buildings} onChange={(e) => setLayerVis(v => ({...v, buildings: e.target.checked}))} style={{ margin: 0, accentColor: '#a855f7' }} />
            <span style={{ fontWeight: 600 }}>Building footprints</span>
          </label>

          {/* Clear plant data — start-fresh button visible when project has
              existing plant but no territory yet. One click wipes every node,
              segment, and drop so the territory flow starts on a clean map. */}
          {selectedProjectId && !terrPlan && !terrDrawMode && graph && graph.stats.node_count > 0 && plant && (
            <button
              className="btn"
              onClick={async () => {
                if (!confirm(`Wipe all ${graph.stats.node_count} nodes, ${graph.stats.segment_count} segments, and ${graph.stats.drop_count} drops from this project? This cannot be undone.`)) return;
                try {
                  await api.post(`/plants/${plant.id}/clear`, {});
                  // Invalidate every project-scoped cache so stale data doesn't
                  // linger in the BOM panel, passings dots, graph, etc.
                  qc.invalidateQueries({ queryKey: ['plant-graph', plant.id] });
                  qc.invalidateQueries({ queryKey: ['plant-passings', plant.id] });
                  qc.invalidateQueries({ queryKey: ['bom', plant.id] });
                  qc.invalidateQueries({ queryKey: ['bead-eligible', selectedProjectId] });
                  qc.invalidateQueries({ queryKey: ['buildings'] });
                  // Wipe the map sources directly so the visual updates instantly
                  const map = mapRef.current;
                  if (map) {
                    const empty = { type: 'FeatureCollection', features: [] } as any;
                    for (const id of ['segments', 'nodes', 'drops', 'passings-buffer', 'passing-dots', 'olt-marker', 'polygon']) {
                      const src = map.getSource(id) as maplibregl.GeoJSONSource | undefined;
                      if (src) src.setData(empty);
                    }
                  }
                  setSelected(null);
                  clearDesign();
                  toast.success('Plant cleared — draw a territory to start.');
                } catch (e) {
                  toast.error('Clear failed: ' + (e as Error).message);
                }
              }}
              title={`Delete all ${graph.stats.node_count} nodes + ${graph.stats.segment_count} segments to start fresh for territory planning.`}
              style={{ fontSize: 12, background: '#7f1d1d', color: '#fff', border: '1px solid #991b1b' }}
            >
              ✕ Clear plant data ({graph.stats.node_count}N · {graph.stats.segment_count}S)
            </button>
          )}
          {/* Territory-first flow: draw territory polygon → auto-detect zones */}
          {selectedProjectId && !terrDrawMode && !expansionMode && !editMode && (
            <button
              className="btn btn--primary"
              onClick={() => { setTerrDrawMode(true); territoryDraw.clear(); setActiveZoneId(null); }}
              title={terrPlans.length > 0
                ? `Draw another territory polygon. Each draw creates a new scenario for comparison alongside existing plans.`
                : `Draw a territory polygon to detect zones and build a BEAD plan.`}
              style={{ fontSize: 13, background: '#8b5cf6', borderColor: '#7c3aed' }}
            >
              ▥ {terrPlans.length > 0 ? `Draw another territory` : `Draw territory`}
            </button>          )}
          {selectedProjectId && !terrDrawMode && !expansionMode && !editMode && (
            <>
              <input ref={importInputRef} type="file" accept=".kml,.kmz,.geojson,.json" style={{ display: 'none' }} onChange={handleImportBoundary} />
              <button
                className="btn"
                onClick={() => importInputRef.current?.click()}
                disabled={importingBoundary}
                title="Import a territory boundary from a Google My Maps / KML / KMZ / GeoJSON file, then Analyze it."
                style={{ fontSize: 13 }}
              >
                {importingBoundary ? '⏳ Importing…' : '⬆ Import KML/KMZ'}
              </button>
            </>
          )}
          {importedLocations.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 8px', fontSize: 12 }}>
              <span>📍 {importedLocations.length} locations imported</span>
              <button className="btn" style={{ fontSize: 11, padding: '2px 6px' }} onClick={() => { setImportedLocations([]); try { localStorage.removeItem('ff.importedLocations'); } catch { /* */ } }}>Clear</button>
            </div>
          )}
          {importedLocations.length > 1 && (
            <button className="btn btn--primary" onClick={() => setRingMode(ringMode === 'pick' ? 'idle' : 'pick')} style={{ fontSize: 13, background: '#f59e0b', borderColor: '#d97706' }}>
              {ringMode === 'pick' ? '✕ Cancel — click the hub on the map' : '🔗 Design fiber ring'}
            </button>
          )}
          {importedLocations.length > 1 && (
            <button className="btn" onClick={() => { setSelectMode(!selectMode); if (selectMode) setSelectedKeys([]); }} style={{ fontSize: 13, background: selectMode ? '#dbeafe' : undefined, borderColor: selectMode ? '#3b82f6' : undefined }}>
              {selectMode ? `✓ Selecting (${selectedKeys.length}) — tap pins, then Ring` : '☐ Select sites'}
            </button>
          )}
          {selectMode && selectedKeys.length >= 3 && (
            <button className="btn btn--primary" onClick={() => { const sel = selectedKeys.map((i) => importedLocations[i]).filter(Boolean); const d = computeRing(sel); if (d) { finalizeRing(d); setSelectMode(false); toast.success(`Ring of ${sel.length} sites: ${d.miles.toFixed(1)} fiber-mi`); } }} style={{ fontSize: 13, background: '#f59e0b', borderColor: '#d97706' }}>
              🔗 Ring selected ({selectedKeys.length})
            </button>
          )}
          {(ringDesign || routingProgress) && (
            <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', fontSize: 12, maxWidth: 280, boxShadow: '0 1px 4px rgba(0,0,0,0.12)' }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>🔗 Protected fiber ring</div>
              {ringDesign?.routed ? (
                <>
                  <div style={{ color: 'var(--text-muted)' }}>{ringDesign.sites} sites · dual-fed loop</div>
                  <div style={{ margin: '6px 0' }}><b style={{ fontSize: 18, color: '#b45309' }}>{ringDesign.miles.toFixed(1)}</b> fiber-mi <span style={{ color: 'var(--text-subtle,#94a3b8)', fontSize: 10 }}>(actual road miles)</span></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '6px 0' }}>
                    <span>$</span>
                    <input type="number" value={ringCostPerMile} onChange={(e) => setRingCostPerMile(Number(e.target.value) || 0)} style={{ width: 90, fontSize: 12, padding: '2px 4px' }} />
                    <span style={{ color: 'var(--text-muted)' }}>/ mile</span>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#065f46' }}>${Math.round(ringDesign.miles * ringCostPerMile).toLocaleString()}</div>
                  <div style={{ fontSize: 10, color: '#15803d', marginTop: 2 }}>🛣 Road-routed · buildable path</div>
                  <button className="btn" style={{ fontSize: 11, padding: '2px 8px', marginTop: 6 }} onClick={() => { setRingDesign(null); setRoutingProgress(null); ringRunRef.current++; }}>Clear ring</button>
                </>
              ) : (
                <>
                  <div style={{ color: 'var(--text-muted)' }}>{ringDesign?.sites ?? routingProgress?.total} sites · dual-fed loop</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' }}>
                    <span style={{ fontSize: 16 }}>⏳</span>
                    <b style={{ color: '#1d4ed8' }}>Routing along roads… {routingProgress ? `${routingProgress.done}/${routingProgress.total}` : ''} legs</b>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-subtle,#94a3b8)' }}>Building the buildable path — the ring appears only when it's correct.</div>
                  <button className="btn" style={{ fontSize: 11, padding: '2px 8px', marginTop: 6 }} onClick={() => { setRingDesign(null); setRoutingProgress(null); ringRunRef.current++; }}>Cancel</button>
                </>
              )}
            </div>
          )}
          {terrDrawMode && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn--primary" onClick={() => territoryDraw.finish()} disabled={territoryDraw.ring.length < 3} style={{ fontSize: 13, background: '#8b5cf6', borderColor: '#7c3aed' }}>
                ✓ Finish territory ({territoryDraw.ring.length} pts)
              </button>
              <button className="btn" onClick={() => { setTerrDrawMode(false); territoryDraw.clear(); }} style={{ fontSize: 13 }}>
                Cancel
              </button>
            </div>
          )}

          {activeZoneId && !editMode && !expansionMode && (
            <button
              className="btn"
              onClick={() => setEditMode(true)}
              style={{ background: '#fef3c7', color: '#92400e', borderColor: '#f59e0b' }}
              title="Drag vertices to reshape the active zone boundary"
            >
              ✎ Edit boundary
            </button>
          )}

          {editMode && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn--primary" onClick={() => { if (polyEdit.ring.length >= 4) saveEditMut.mutate(polyEdit.ring); }} disabled={polyEdit.ring.length < 4 || saveEditMut.isPending}>
                  {saveEditMut.isPending ? 'Saving…' : '✓ Save shape'}
                </button>
                <button className="btn" onClick={() => polyEdit.reset()}>Reset</button>
                <button className="btn" onClick={() => setEditMode(false)}>Cancel</button>
              </div>
              {previewPassings !== null && (
                <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: '6px 10px', fontSize: 11, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }} title="Live preview — passings">
                  <span style={{ color: 'var(--text-muted)' }}>Current shape: </span>
                  <b style={{ color: '#0284c7', fontSize: 14 }}>{previewPassings.toLocaleString()}</b>
                  <span style={{ color: 'var(--text-muted)' }}> passings</span>
                  <div style={{ fontSize: 9, color: 'var(--text-subtle, #94a3b8)', marginTop: 2 }}>Pricing updates after Save + Re-analyze</div>
                </div>
              )}
            </div>
          )}
          {(savedNeedsReanalyze || analyzeMut.isPending || (staleSince !== null) || activeZone?.status === 'draft') && !editMode && activeZoneId && (() => {
            const analyzing = analyzeMut.isPending || (staleSince !== null && !savedNeedsReanalyze);
            const color = analyzing ? '#1d4ed8' : '#92400e';
            const bg = analyzing ? '#dbeafe' : '#fef3c7';
            const border = analyzing ? '#3b82f6' : '#f59e0b';
            const isFreshDraft = activeZone?.status === 'draft' && !analyzing && !savedNeedsReanalyze && staleSince === null;
            return (
              <div style={{ background: isFreshDraft ? '#dcfce7' : bg, border: `2px solid ${isFreshDraft ? '#22c55e' : border}`, borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, maxWidth: 380 }}>
                <span style={{ fontSize: 16 }}>{analyzing ? '⏳' : isFreshDraft ? '✨' : '⚠'}</span>
                <div style={{ fontSize: 11, color: isFreshDraft ? '#166534' : color, flex: 1 }}>
                  {analyzing
                    ? <><b>Analyzing…</b> Auto-refreshes when done ({Math.max(0, Math.round(((staleSince ? Date.now() - staleSince : 0)) / 1000))}s elapsed)</>
                    : isFreshDraft
                      ? <><b>Zone drafted.</b> Click <i>Analyze</i> to compute passings, design the extension, and price the build.</>
                      : 'Shape updated — pricing + design are stale.'}
                </div>
                {!analyzing && (
                  <>
                    <button
                      className="btn btn--primary"
                      onClick={() => analyzeMut.mutate()}
                      disabled={analyzeMut.isPending}
                      style={{ background: isFreshDraft ? '#10b981' : '#f59e0b', borderColor: isFreshDraft ? '#059669' : '#d97706', padding: '4px 12px', fontSize: 12, fontWeight: 700 }}
                    >
                      {isFreshDraft ? 'Analyze →' : 'Re-analyze now'}
                    </button>
                    {!isFreshDraft && (
                      <button className="btn" onClick={() => { setSavedNeedsReanalyze(false); setStaleSince(null); }} style={{ padding: '4px 8px', fontSize: 11 }}>Later</button>
                    )}
                  </>
                )}
              </div>
            );
          })()}
          {!expansionMode && !activeZoneId && terrPlan && (
            <button
              className="btn"
              onClick={() => {
                if (!plant) { toast.warning('Plant still loading'); return; }
                setActiveZoneId(null);
                setExpansionMode(true);
                expansionDraw.clear();
                // Clear leftover per-zone state from the prior zone so the
                // fresh-draft analyze panel renders cleanly.
                setEditMode(false);
                setSavedNeedsReanalyze(false);
                setStaleSince(null);
                setAnalyzePollStart(null);
                toast.info('Click to drop zone vertices. Double-click to close the zone.');
              }}
              title="Draw a zone. Capture adjacent served neighborhoods or an off-polygon zone for private-capex analysis."
              style={{ fontSize: 13 }}
            >
              ⊕ Draw new project
            </button>
          )}
          {showSoil && activePlan?.id && (
            <div style={{
              background: '#0b1220cc', color: '#fff', borderRadius: 6,
              padding: '8px 10px', fontSize: 11, lineHeight: 1.4,
              width: 'fit-content', minWidth: 180,
            }}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: '#cbd5e1',
                textTransform: 'uppercase', letterSpacing: '.06em',
                marginBottom: 6,
              }}>Soil overlay legend</div>
              {[
                { c: '#3b82f6', label: 'Plow', sub: 'good drainage, deep soil, low slope' },
                { c: '#22c55e', label: 'Trench', sub: 'poor drainage / steep / group D' },
                { c: '#f59e0b', label: 'Rock saw', sub: 'shallow bedrock (≤ 18 in)' },
                { c: '#ef4444', label: 'Directional bore', sub: 'hydric ≥ 50% (likely wetland)' },
              ].map((row, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '2px 0' }}>
                  <span style={{
                    width: 12, height: 12, borderRadius: 3, background: row.c,
                    flexShrink: 0, marginTop: 2, border: '1px solid rgba(255,255,255,0.25)',
                  }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{row.label}</div>
                    <div style={{ color: '#94a3b8', fontSize: 10 }}>{row.sub}</div>
                  </div>
                </div>
              ))}
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #1e293b', color: '#94a3b8', fontSize: 10 }}>
                Click any polygon for soil unit detail.
              </div>
            </div>
          )}
          {expansionMode && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="btn btn--primary"
                onClick={() => expansionDraw.finish()}
                disabled={expansionDraw.ring.length < 3}
                style={{ fontSize: 13 }}
              >
                ✓ Close zone ({expansionDraw.ring.length} pts)
              </button>
              <button
                className="btn"
                onClick={() => { setExpansionMode(false); expansionDraw.clear(); }}
                style={{ fontSize: 13 }}
              >
                Cancel
              </button>
            </div>
          )}
          {/* Saved zones list (click to re-activate) */}
          {/* Recent failures panel — audit log of deleted failed/rejected/zombie zones */}
          {selectedProjectId && zoneFailures.length > 0 && (
            <div style={{
              background: '#fff', border: '1px solid #fecaca', borderRadius: 6,
              padding: 0, maxWidth: 360, fontSize: 12,
              boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            }}>
              <button
                onClick={() => setFailuresOpen(o => !o)}
                style={{
                  width: '100%', padding: '8px 12px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: '#fef2f2', color: '#991b1b',
                  border: 0, cursor: 'pointer', fontWeight: 600, fontSize: 12,
                  borderRadius: 6,
                }}>
                <span>⚠ Recent failures ({zoneFailures.length})</span>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!selectedProjectId) return;
                      if (!window.confirm(`Clear all ${zoneFailures.length} failure record(s) for this project? This deletes the audit log entries only — it doesn't undo or re-run anything.`)) return;
                      clearFailuresMut.mutate(selectedProjectId);
                    }}
                    style={{ fontSize: 10, padding: '2px 8px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                    title="Delete all failure records for this project"
                  >Clear all</button>
                  <span style={{ fontSize: 10, opacity: 0.7 }}>{failuresOpen ? 'hide ▼' : 'show ▶'}</span>
                </span>
              </button>
              {failuresOpen && (
                <div style={{ maxHeight: 280, overflowY: 'auto', padding: '4px 0' }}>
                  {zoneFailures.slice(0, 50).map((f) => {
                    const kindColor: Record<string, { bg: string; fg: string; label: string }> = {
                      rejected:        { bg: '#fef3c7', fg: '#92400e', label: 'rejected' },
                      analysis_failed: { bg: '#fee2e2', fg: '#991b1b', label: 'failed' },
                      zombie_killed:   { bg: '#e0e7ff', fg: '#3730a3', label: 'zombie' },
                      early_return:    { bg: '#f3f4f6', fg: '#374151', label: 'early-return' },
                    };
                    const k = kindColor[f.failure_kind] ?? kindColor.early_return;
                    // Derive a human-readable 'why' from partial_analysis when
                    // the reason field is empty/placeholder.
                    const pa = f.partial_analysis ?? {};
                    const pas = pa.passings ?? {};
                    const npv = typeof pa.npv === 'number' ? pa.npv : null;
                    let derivedWhy: string | null = null;
                    if (!f.reason || f.reason === 'no reason recorded') {
                      if (pas.total === 0) {
                        derivedWhy = 'No serviceable passings found inside the cluster polygon.';
                      } else if (pas.bead_eligible === 0 && f.zone_type !== 'private') {
                        derivedWhy = `No BEAD-eligible passings (${pas.total ?? 0} total · ${pas.served ?? 0} already served).`;
                      } else if (npv != null && npv < 0) {
                        derivedWhy = `Negative NPV ($${Math.round(npv).toLocaleString()}) — auto-design rejected as uneconomic.`;
                      } else if ((pa.nodes ?? []).length === 0) {
                        derivedWhy = 'Auto-design produced no nodes (insufficient passings or geometry too small).';
                      }
                    }
                    const canZoom = !!f.zone_geojson;
                    return (
                      <button
                        key={f.id}
                        onClick={() => {
                          const map = mapRef.current;
                          if (!map || !f.zone_geojson) return;
                          // Compute bbox from any geometry shape (Polygon/MultiPolygon/Line).
                          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                          const visit = (coord: any) => {
                            if (Array.isArray(coord) && typeof coord[0] === 'number' && typeof coord[1] === 'number') {
                              if (coord[0] < minX) minX = coord[0];
                              if (coord[1] < minY) minY = coord[1];
                              if (coord[0] > maxX) maxX = coord[0];
                              if (coord[1] > maxY) maxY = coord[1];
                            } else if (Array.isArray(coord)) {
                              coord.forEach(visit);
                            }
                          };
                          visit(f.zone_geojson.coordinates);
                          if (isFinite(minX)) {
                            map.fitBounds([[minX, minY], [maxX, maxY]] as any, { padding: 80, maxZoom: 16, duration: 600 });
                          }
                        }}
                        style={{
                          padding: '6px 12px',
                          display: 'flex', flexDirection: 'column', gap: 2,
                          width: '100%', textAlign: 'left',
                          background: 'transparent', border: 0,
                          borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: '#fee2e2',
                          cursor: canZoom ? 'pointer' : 'default',
                        }}
                        onMouseEnter={(e) => { if (canZoom) e.currentTarget.style.background = '#fef2f2'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        title={canZoom ? 'Click to zoom to this cluster on the map' : ''}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 600, color: '#0f172a', fontSize: 12 }}>
                            {canZoom && <span style={{ marginRight: 4, color: '#64748b' }}>⌖</span>}
                            {f.name}
                          </span>
                          <span style={{
                            background: k.bg, color: k.fg,
                            padding: '1px 6px', borderRadius: 999,
                            fontSize: 9, fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: '.04em',
                          }}>{k.label}</span>
                        </div>
                        <div style={{ fontSize: 10, color: '#64748b', lineHeight: 1.4 }}>
                          {f.zone_type ?? '—'} · {new Date(f.failed_at).toLocaleString()}
                        </div>
                        {f.reason && f.reason !== 'no reason recorded' && (
                          <div style={{ fontSize: 10, color: '#7f1d1d', fontFamily: 'ui-monospace, monospace' }}>
                            {f.reason}
                          </div>
                        )}
                        {derivedWhy && (
                          <div style={{ fontSize: 10, color: '#7f1d1d', lineHeight: 1.4 }}>
                            <strong>Why:</strong> {derivedWhy}
                          </div>
                        )}
                        {(pas.total != null || npv != null) && (
                          <div style={{ display: 'flex', gap: 10, fontSize: 10, color: '#94a3b8', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                            {pas.total != null && <span>passings <strong style={{ color: '#475569' }}>{pas.total.toLocaleString()}</strong></span>}
                            {pas.bead_eligible != null && <span>BEAD <strong style={{ color: '#475569' }}>{pas.bead_eligible.toLocaleString()}</strong></span>}
                            {pas.served != null && <span>served <strong style={{ color: '#475569' }}>{pas.served.toLocaleString()}</strong></span>}
                            {npv != null && <span>NPV <strong style={{ color: npv < 0 ? '#b91c1c' : '#047857' }}>${Math.round(npv).toLocaleString()}</strong></span>}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {!expansionMode && expansionZones.length > 0 && (
            <ZonePills
              zones={
                activeTerritoryPlanId && activeTerritoryPlanId !== 'all'
                  ? expansionZones.filter((z) => (z as any).territory_plan_id === activeTerritoryPlanId)
                  : expansionZones
              }
              activeId={activeZoneId}
              selectedIds={selectedZoneIds}
              onSelect={(id, shiftKey) => {
                if (shiftKey) {
                  setSelectedZoneIds((prev) => {
                    if (prev.includes(id)) return prev.filter((x) => x !== id);
                    return [...prev, id];
                  });
                } else {
                  setSelectedZoneIds((prev) => prev.includes(id) && prev[0] !== id
                    ? [id, ...prev.filter((x) => x !== id)]
                    : prev);
                  setActiveZoneId(id);
                }
              }}
              onClearMultiselect={() => setSelectedZoneIds([])}
              onMerge={() => mergeMut.mutate()}
              mergePending={mergeMut.isPending}
              planFilter={(() => {
                // Disabled per request 2026-05-27 — the All-plans pill was
                // cluttering the map. Side-panel territory list is the
                // primary plan switcher now. To restore, change `if (true)`
                // back to `if (plans.length <= 1)`.
                if (true) return null;
                const seen = new Set<string>();
                const plans: Array<{ id: string; name: string }> = [];
                for (const z of (expansionZones || []) as any[]) {
                  const pid = z.territory_plan_id;
                  const pname = z.territory_plan_name;
                  if (pid && !seen.has(pid)) { seen.add(pid); plans.push({ id: pid, name: pname || 'Unnamed plan' }); }
                }
                if (plans.length <= 1) return null;
                const currentName = activeTerritoryPlanId === 'all'
                  ? 'All plans'
                  : plans.find((p) => p.id === activeTerritoryPlanId)?.name || 'Pick plan';
                return (
                  <select
                    value={activeTerritoryPlanId || 'all'}
                    onChange={(e) => {
                      const v = e.target.value;
                      setActiveTerritoryPlanId(v === 'all' ? 'all' : v);
                      // Also drive the right-side Territory card so the
                      // $/capex/payback numbers actually change.
                      setSelectedPlanId(v === 'all' ? null : v);
                      // Pan/fit the map to the chosen plan's polygon,
                      // if we can derive its bounds from its clusters.
                      if (v !== 'all') {
                        const map = mapRef.current;
                        const zonesForPlan = (expansionZones || []).filter((z: any) => z.territory_plan_id === v);
                        const coords: number[][] = [];
                        for (const z of zonesForPlan) {
                          const g = (z as any).zone_geojson;
                          if (!g) continue;
                          const rings = g.type === 'Polygon' ? g.coordinates
                            : g.type === 'MultiPolygon' ? g.coordinates.flat() : [];
                          for (const ring of rings) for (const pt of ring) coords.push(pt);
                        }
                        if (map && coords.length) {
                          let minX = coords[0][0], minY = coords[0][1], maxX = coords[0][0], maxY = coords[0][1];
                          for (const [x, y] of coords) { if (x<minX)minX=x; if (x>maxX)maxX=x; if (y<minY)minY=y; if (y>maxY)maxY=y; }
                          try { map.fitBounds([[minX, minY], [maxX, maxY]], { padding: 60, duration: 600 }); } catch {}
                        }
                      }
                    }}
                    title="Filter clusters by Territory"
                    style={{
                      padding: '3px 8px', borderRadius: 12, fontSize: 11,
                      border: '1px solid var(--border-strong)', background: '#fff', color: '#111827', cursor: 'pointer',
                      maxWidth: 180, fontWeight: 600,
                    }}
                  >
                    <option value="all">All plans ({plans.length} · {(expansionZones || []).length} clusters)</option>
                    {plans.map((p) => {
                      const tp = (terrPlans as any[]).find((x) => x.id === p.id);
                      const st = tp?.state ? ` · ${tp.state}` : '';
                      const pas = tp?.summary?.total_passings;
                      const pasLabel = typeof pas === 'number' ? ` · ${pas.toLocaleString()} passings` : '';
                      const d = tp?.created_at ? new Date(tp.created_at) : null;
                      const dLabel = d ? ` · ${d.toISOString().slice(0, 10)}` : '';
                      return (
                        <option key={p.id} value={p.id}>
                          {p.name}{st}{pasLabel}{dLabel}
                        </option>
                      );
                    })}
                  </select>
                );
              })()}
            />
          )}
        </div>
      </div>

      {selected ? (
        <InspectorPanel
          selected={selected}
          onDelete={handleDelete}
          onGenerateDrops={handleGenerateDrops}
          generatingDrops={generatingDrops}
          lastDropResult={lastDropResult}
        />
      ) : activeZone ? (
        <div className="card" style={{ height: '100%', overflow: 'auto' }}>
          <div className="card__body" style={{ padding: 12 }}>
            <ExpansionStudyPanel
              zone={activeZone}
              onClose={() => setActiveZoneId(null)}
              onCommitted={() => {
                refetchGraph();
                refetchBom();
                setActiveZoneId(null);
              }}
              onRejected={() => setActiveZoneId(null)}
              onBackhaulSelect={(type, hops) => {
                setSelectedBackhaulType(type);
                setWirelessHops(hops ?? null);
              }}
            />
          </div>
        </div>
      ) : terrPlan ? (
        <div className="card" style={{ height: '100%', overflow: 'auto' }}>
          <div className="card__header">Territory</div>
          {/* Scenarios: list all plans for this project; switch/clone/expand */}
          {terrPlans && terrPlans.length > 0 && (
            <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2, #f8fafc)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: '#111827' }}>Scenarios ({terrPlans.filter((p: any) => p.status !== 'committed' || p.id === selectedPlanId).length})</div>
                {compareSelection.size >= 1 && (
                  <button
                    onClick={() => {
                      const ids = Array.from(compareSelection).join(',');
                      window.open(`/reports?planIds=${ids}&margin=${terrGrossMargin.toFixed(3)}`, '_blank');
                    }}
                    title="Open side-by-side business case comparison for selected scenarios"
                    style={{ fontSize: 10, padding: '2px 8px', border: '1px solid #059669', background: '#059669', color: '#fff', borderRadius: 4, cursor: 'pointer', fontWeight: 600, marginRight: 6 }}
                  >
                    Compare ({compareSelection.size})
                  </button>
                )}
                {(terrPlans ?? []).filter((p: any) => p.status !== 'committed').length > 0 && (
                  <button
                    disabled={clearAllScenariosMut.isPending}
                    onClick={() => {
                      const n = (terrPlans ?? []).filter((p: any) => p.status !== 'committed').length;
                      if (n === 0) { toast.info('No scenarios to clear'); return; }
                      if (window.confirm(`Delete ALL ${n} non-committed scenario${n === 1 ? '' : 's'} in this project?\n\nCommitted plans of record are preserved. This cannot be undone.`)) {
                        clearAllScenariosMut.mutate();
                      }
                    }}
                    title="Delete every scenario that is not committed. Committed plans are locked and skipped."
                    style={{ fontSize: 10, padding: '2px 8px', border: '1px solid #b91c1c', background: '#fff', color: '#b91c1c', borderRadius: 4, cursor: 'pointer', fontWeight: 600, marginRight: 6 }}
                  >
                    Clear all
                  </button>
                )}
                <button
                  disabled={clearDraftsMut.isPending}
                  onClick={() => { if (window.confirm('Delete orphan draft zones (status=draft, no territory plan)? Committed/analyzed zones are preserved.')) clearDraftsMut.mutate(); }}
                  title="Wipe any leftover draft zones that aren't part of a territory plan"
                  style={{ fontSize: 10, padding: '2px 8px', border: '1px solid #64748b', background: '#fff', color: '#334155', borderRadius: 4, cursor: 'pointer', fontWeight: 600, marginRight: 6 }}
                >
                  Clear drafts
                </button>
                <button
                  disabled={!activePlan?.id || cloneTerrMut.isPending}
                  onClick={() => activePlan?.id && cloneTerrMut.mutate(activePlan.id)}
                  title="Create a new scenario by cloning the active plan's polygon + assumptions"
                  style={{ fontSize: 10, padding: '2px 8px', border: '1px solid #6366f1', background: '#6366f1', color: '#fff', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                >
                  + Clone scenario
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto' }}>
                {terrPlans.filter((p: any) => p.status !== 'committed' || p.id === selectedPlanId).map((p: any) => {
                  const isActive = p.id === activePlan?.id;
                  // 2026-06-01: prefer engineered_capex_full (persisted at analyze)
                  // so the Scenarios row matches the right-rail BUILD COST card.
                  // That field = pricing-model grand_total = cable + labor +
                  // electronics + per-mile labor + adders. Falls back to
                  // total_cluster_capex + backbone for plans analyzed before #58.
                  const engFull = Number(p.summary?.engineered_capex_full ?? 0);
                  const clusterCap = Number(p.summary?.total_cluster_capex ?? 0);
                  const backboneCap = Number(p.summary?.total_backbone_capex ?? 0);
                  const cap = engFull > 100_000
                    ? engFull
                    : ((clusterCap + backboneCap) > 0
                        ? clusterCap + backboneCap
                        : Number(p.summary?.total_capex ?? 0));
                  const mrr = Number(p.summary?.total_mrr ?? 0);
                  const passings = Number(p.summary?.total_passings ?? 0);
                  const beadCov = Number(p.summary?.bead_in_territory ?? 0) - Number(p.summary?.bead_uncovered ?? 0);
                  const beadTot = Number(p.summary?.bead_in_territory ?? 0);
                  const fmtM = (n: number) => n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `$${(n / 1e3).toFixed(0)}K` : `$${n}`;
                  const status = String(p.status ?? 'unknown');
                  return (
                    <div
                      key={p.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '6px 8px', borderRadius: 4,
                        border: isActive ? '2px solid #6366f1' : '1px solid var(--border)',
                        background: isActive ? '#eef2ff' : '#fff',
                        color: '#111827', fontWeight: isActive ? 700 : 500, fontSize: 11,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={compareSelection.has(p.id)}
                        onChange={(e) => {
                          setCompareSelection((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(p.id); else next.delete(p.id);
                            return next;
                          });
                        }}
                        onClick={(e) => e.stopPropagation()}
                        title="Select for side-by-side comparison"
                        style={{ cursor: 'pointer', accentColor: '#6366f1' }}
                      />
                      <div
                        onClick={() => setSelectedPlanId(p.id)}
                        style={{ display: 'flex', flex: 1, minWidth: 0, cursor: 'pointer' }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                            <span
                              role="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const newName = window.prompt('Rename scenario:', p.name);
                                if (newName && newName.trim() && newName !== p.name) {
                                  renamePlanMut.mutate({ planId: p.id, name: newName.trim() });
                                }
                              }}
                              title="Rename"
                              style={{ cursor: 'pointer', fontSize: 10, color: '#64748b', padding: '0 4px' }}
                            >✎</span>
                            {p.status === 'complete' && (
                              <span
                                role="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Commit "${p.name}" to Plans of Record? (You can commit multiple scenarios — they'll all be archived for later comparison.)`)) {
                                    commitPlanMut.mutate(p.id);
                                  }
                                }}
                                title="Commit as plan of record — locks this scenario in"
                                style={{ cursor: 'pointer', fontSize: 10, color: '#059669', padding: '0 4px', fontWeight: 700 }}
                              >✓ commit</span>
                            )}
                            {p.status === 'committed' && (
                              <span
                                role="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Uncommit "${p.name}"? It will go back to 'complete' and lose its protected status.`)) {
                                    uncommitPlanMut.mutate(p.id);
                                  }
                                }}
                                title="Uncommit"
                                style={{ cursor: 'pointer', fontSize: 10, color: '#b45309', padding: '0 4px', fontWeight: 600 }}
                              >uncommit</span>
                            )}
                            {(p.status === 'complete' || p.status === 'committed') && (
                              <span
                                role="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Promote "${p.name}" design into As-Built? Copies the built fiber into the as-built record and auto-detects permits (no Vetro needed).`)) {
                                    promoteAsBuiltMut.mutate(p.id);
                                  }
                                }}
                                title="Promote this plan's design straight into As-Built + auto-detect permits (no Vetro)"
                                style={{ cursor: 'pointer', fontSize: 10, color: '#7c3aed', padding: '0 4px', fontWeight: 700 }}
                              >→ as-built</span>
                            )}
                            {/* 2026-05-30 inline delete for non-committed plans
                                (failed/rejected/pending/complete). Committed
                                plans still go through the uncommit flow first. */}
                            {p.status !== 'committed' && (
                              <span
                                role="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const isJunk = p.status === 'failed' || p.status === 'rejected';
                                  const msg = isJunk
                                    ? `Delete "${p.name}" (${p.status})? This was never completed — nothing of value will be lost.`
                                    : `Delete "${p.name}"? All zones, segments, and analysis for this plan will be removed. This cannot be undone.`;
                                  if (window.confirm(msg)) deleteTerrMut.mutate(p.id);
                                }}
                                title={`Delete this plan (${p.status})`}
                                style={{ cursor: 'pointer', fontSize: 12, color: '#dc2626', padding: '0 4px', fontWeight: 700, lineHeight: 1 }}
                              >✕</span>
                            )}
                          </div>
                          <div style={{ fontSize: 9, color: '#6b7280', fontWeight: 400, display: 'flex', alignItems: 'center', gap: 4 }}>
                            {p.status === 'committed' && (
                              <span style={{ padding: '1px 4px', background: '#059669', color: '#fff', borderRadius: 2, fontSize: 8, fontWeight: 700, letterSpacing: '.04em' }}>COMMITTED</span>
                            )}
                            <span>{status} · {passings.toLocaleString()} passings · BEAD {beadCov}/{beadTot}</span>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', marginLeft: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#111827' }}>{fmtM(cap)}</div>
                          <div style={{ fontSize: 9, color: '#6b7280' }}>{fmtM(mrr)}/mo</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {activePlan?.status === 'complete' && !territoryEditMode && (
                <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: 10, color: '#6b7280' }}>Edit polygon:</span>
                  {(() => {
                    const committed = activePlan?.status === 'committed';
                    const polygonLoaded = !!(terrPlan && (terrPlan as any).id === activePlan?.id && (terrPlan as any).territory_geojson);
                    const disabled = committed || !polygonLoaded;
                    const title = committed
                      ? 'Uncommit this plan first to edit its polygon'
                      : !polygonLoaded ? 'Waiting for polygon data to load…' : 'Drag vertices of the active polygon';
                    return (
                      <button
                        onClick={() => { if (!disabled) setTerritoryEditMode(true); }}
                        disabled={disabled}
                        title={title}
                        style={{ fontSize: 10, padding: '2px 6px', border: '1px solid #8b5cf6', background: disabled ? '#f3f4f6' : '#fff', color: disabled ? '#9ca3af' : '#6d28d9', borderRadius: 4, cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 600 }}
                      >
                        ✎ Stretch vertices
                      </button>
                    );
                  })()}
                </div>
              )}
              {activePlan?.status === 'complete' && territoryEditMode && (
                <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap', padding: 6, background: '#ede9fe', border: '1px solid #8b5cf6', borderRadius: 4 }}>
                  <div style={{ fontSize: 10, color: '#6d28d9', fontWeight: 700, width: '100%' }}>
                    {terrPolyEdit.ring.length < 3 && terrEditInitialRing.length < 3
                      ? 'LOADING POLYGON DATA… (purple handles will appear once the plan detail is fetched)'
                      : 'EDITING POLYGON — drag the purple handles on the map'}
                  </div>
                  <button
                    disabled={saveTerritoryEditMut.isPending || terrPolyEdit.ring.length < 4}
                    onClick={() => { if (window.confirm('Save edited polygon as a NEW scenario? Original plan is preserved.')) saveTerritoryEditMut.mutate({ asScenario: true }); }}
                    style={{ fontSize: 10, padding: '3px 8px', border: '1px solid #6366f1', background: '#6366f1', color: '#fff', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                  >
                    Save as scenario
                  </button>
                  <button
                    disabled={saveTerritoryEditMut.isPending || terrPolyEdit.ring.length < 4}
                    onClick={() => { if (window.confirm('Overwrite the active plan\'s polygon with your edits and re-analyze? The original analysis will be lost.')) saveTerritoryEditMut.mutate({ asScenario: false }); }}
                    style={{ fontSize: 10, padding: '3px 8px', border: '1px solid #b91c1c', background: '#fff', color: '#b91c1c', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                  >
                    Save in place
                  </button>
                  <button
                    onClick={() => { terrPolyEdit.reset(); setTerritoryEditMode(false); }}
                    style={{ fontSize: 10, padding: '3px 8px', border: '1px solid #64748b', background: '#fff', color: '#334155', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                  >
                    Cancel
                  </button>
                </div>
              )}
              {activePlan?.status === 'complete' && !territoryEditMode && (
                <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, color: '#6b7280', alignSelf: 'center' }}>Expand active polygon:</span>
                  {[1, 2, 5, 10].map((mi) => (
                    <button
                      key={mi}
                      disabled={expandPolygonMut.isPending}
                      onClick={() => { if (window.confirm(`Expand territory polygon by ${mi} mile${mi === 1 ? '' : 's'} outward and re-analyze? This overwrites the current analysis.`)) expandPolygonMut.mutate(mi); }}
                      style={{ fontSize: 10, padding: '2px 6px', border: '1px solid #8b5cf6', background: '#fff', color: '#6d28d9', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
                    >
                      +{mi}mi
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="card__body" style={{ padding: 0 }}>
            <TerritoryBusinessCase
              key={terrPlan?.id ?? 'no-plan'}
              plan={terrPlan}
              isAnalyzing={terrAnalysisRunning || terrPlan?.status === 'analyzing'}
              progress={terrPlan?.progress ?? {}}
              grossMargin={terrGrossMargin}
              setGrossMargin={setTerrGrossMargin}
              buildIndices={terrBuildIndices}
              setBuildIndices={setTerrBuildIndices}
              showModes={terrShowModes}
              setShowModes={setTerrShowModes}
              onAnalyze={startTerrAnalysis}
              onDelete={() => activePlan?.id && deleteTerrMut.mutate(activePlan.id)}
              onGenerateReport={(extraQs?: string) => activePlan?.id && window.open(`/reports?planId=${activePlan.id}&margin=${terrGrossMargin.toFixed(3)}&build=${terrBuildIndices.join(',')}${extraQs ?? ''}`, '_blank')}
              onAbort={() => activePlan?.id && abortPlanMut.mutate(activePlan.id)}
              onZoneClick={(zid) => setActiveZoneId(zid)}
              onClearDrafts={() => clearDraftsMut.mutate()}
              clearDraftsPending={clearDraftsMut.isPending}
              onRegenerateBackbone={(override) => regenBackboneMut.mutate(override)}
              regeneratingBackbone={regenBackboneMut.isPending}
              hasHeadend={!!(terrPlan as any)?.headend_geojson}
            />
          </div>
        </div>
      ) : (
        <div className="card" style={{ height: '100%', overflow: 'auto' }}>
          <div className="card__header">Project economics</div>
          <div className="card__body">
            <BomPanel bom={bom ?? null} busy={!bom && !!plant && !!graph && graph.stats.segment_count > 0} />
          </div>
        </div>
      )}
      {mergeModal.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => !mergeMut.isPending && setMergeModal({ open: false, name: '' })}>
          <div style={{ background: 'var(--bg, #0b1220)', color: 'var(--text, #fff)', padding: 20, borderRadius: 8, width: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.6)', border: '1px solid var(--border, #334)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>Merge {selectedZoneIds.length} zones</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted, #94a3b8)', marginBottom: 14 }}>
              Union of all selected zone polygons with a 30 m buffer. Revenue + plant inherited from the primary (first-selected) zone.
            </div>
            <label style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted, #94a3b8)' }}>Save as</label>
            <input
              autoFocus
              value={mergeModal.name}
              onChange={(e) => setMergeModal((m) => ({ ...m, name: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter' && mergeModal.name.trim()) mergeMut.mutate(); if (e.key === 'Escape') setMergeModal({ open: false, name: '' }); }}
              style={{ width: '100%', padding: '8px 10px', marginTop: 4, marginBottom: 16, borderRadius: 6, border: '1px solid var(--border, #334)', background: 'var(--bg-subtle, #111827)', color: 'var(--text, #fff)', fontSize: 13 }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setMergeModal({ open: false, name: '' })} disabled={mergeMut.isPending} style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid var(--border, #334)', background: 'transparent', color: 'var(--text, #fff)', cursor: 'pointer' }}>Cancel</button>
              <button onClick={() => mergeMut.mutate()} disabled={!mergeModal.name.trim() || mergeMut.isPending} style={{ padding: '8px 14px', borderRadius: 6, background: '#8b5cf6', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                {mergeMut.isPending ? 'Merging…' : 'Merge + save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Compact zone picker — one pill shows the active zone, a chevron opens a
// searchable dropdown of all zones. Preserves shift+click multi-select and
// primary-promotion logic but fits in one row.
function ZonePills({
  zones, activeId, selectedIds, onSelect, onClearMultiselect, onMerge, mergePending,
  planFilter,
}: {
  zones: ExpansionZone[];
  activeId: string | null;
  selectedIds: string[];
  onSelect: (id: string, shiftKey: boolean) => void;
  onClearMultiselect: () => void;
  onMerge?: () => void;
  mergePending?: boolean;
  planFilter?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const visible = zones.filter((z) => z.status !== 'rejected');
  if (visible.length === 0) return null;

  const primaryId = selectedIds[0];
  const multiselecting = selectedIds.length > 1;
  const active = visible.find((z) => z.id === activeId);
  const sorted = [...visible].sort((a, b) => {
    // Passings desc from name like "Cluster 12 (42 passings)" if parseable, else by name
    const pa = parseInt((a.name.match(/\((\d+)/) || [])[1] || '0', 10);
    const pb = parseInt((b.name.match(/\((\d+)/) || [])[1] || '0', 10);
    return pb - pa;
  });
  const filtered = query.trim()
    ? sorted.filter((z) => z.name.toLowerCase().includes(query.toLowerCase()))
    : sorted;

  const chipStyleFor = (z: ExpansionZone) => {
    const committed = z.status === 'committed';
    const isSelected = selectedIds.includes(z.id);
    const isPrimary = primaryId === z.id && selectedIds.length > 1;
    const isActive = z.id === activeId;
    return {
      borderColor: isPrimary ? '#f59e0b' : isSelected ? '#8b5cf6' : isActive ? '#0ea5e9' : committed ? '#10b981' : 'var(--border-strong)',
      borderWidth: isPrimary ? 2 : 1,
      background: isPrimary ? '#fef3c7' : isSelected ? '#ede9fe' : isActive ? '#0ea5e9' : committed ? '#d1fae5' : '#fff',
      color: isPrimary ? '#92400e' : isSelected ? '#5b21b6' : isActive ? '#fff' : committed ? '#065f46' : 'var(--text)',
      prefix: isPrimary ? '👑 ' : isSelected ? '✚ ' : committed ? '✓ ' : '',
      fontWeight: isPrimary || isSelected || isActive || committed ? 600 : 400,
    } as any;
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', marginTop: 8, maxWidth: 420 }}>
      {/* The compact visible row: active pill + multi-select summary + dropdown trigger */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
        {planFilter}
        {active ? (() => {
          const st = chipStyleFor(active);
          return (
            <button
              onClick={(e) => onSelect(active.id, e.shiftKey || e.metaKey || e.ctrlKey)}
              style={{
                padding: '3px 8px', borderRadius: 12, fontSize: 11,
                border: `${st.borderWidth}px solid ${st.borderColor}`,
                background: st.background, color: st.color, fontWeight: st.fontWeight,
                cursor: 'pointer',
              }}
              title={`Active: ${active.name}`}
            >
              {st.prefix}{active.name}
            </button>
          );
        })() : (
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>No project selected</span>
        )}
        {/* Cluster-count pill hidden 2026-05-27 — was cluttering the map. */}
        {false && (
        <button
          onClick={() => setOpen((v) => !v)}
          style={{
            padding: '3px 8px', borderRadius: 12, fontSize: 11,
            border: '1px solid var(--border-strong)', background: '#fff', color: '#111827', cursor: 'pointer',
            fontWeight: 600,
          }}
          title="All zones"
        >
          {visible.length} clusters {open ? '▲' : '▾'}
        </button>
        )}
        {multiselecting && (
          <>
            <span style={{ fontSize: 10, color: '#5b21b6', fontWeight: 600, padding: '2px 6px', background: '#ede9fe', borderRadius: 10 }}>
              {selectedIds.length} selected
            </span>
            <button onClick={onClearMultiselect} style={{ fontSize: 10, padding: '1px 6px', border: '1px solid var(--border)', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>Clear</button>
          </>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0,
          width: 360, maxHeight: 420, overflow: 'hidden',
          background: '#fff', border: '1px solid var(--border)', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)', zIndex: 20,
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: 8, borderBottom: '1px solid var(--border)' }}>
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${visible.length} clusters…`}
              style={{ width: '100%', padding: '4px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 6, outline: 'none' }}
            />
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>
              Click to activate · shift+click to multi-select · click a selected one to promote to primary
            </div>
            {selectedIds.length >= 2 && onMerge && (
              <button
                onClick={onMerge}
                disabled={mergePending}
                style={{ marginTop: 6, width: '100%', padding: '6px 10px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: mergePending ? 'wait' : 'pointer' }}
              >
                {mergePending ? 'Merging…' : `⤷ Merge ${selectedIds.length} clusters into one`}
              </button>
            )}
          </div>
          <div style={{ overflowY: 'auto', flex: 1, padding: 6 }}>
            {filtered.length === 0 && (
              <div style={{ padding: 12, fontSize: 11, color: 'var(--text-muted)', textAlign: 'center' }}>No projects match "{query}"</div>
            )}
            {(() => {
              // Group by territory plan name; zones without a plan go under 'Manual / other'
              const groups = new Map<string, typeof filtered>();
              for (const z of filtered) {
                const key = (z as any).territory_plan_name || 'Manual / other';
                if (!groups.has(key)) groups.set(key, [] as any);
                groups.get(key)!.push(z);
              }
              const groupEntries = Array.from(groups.entries());
              return groupEntries.map(([planName, list]) => (
                <div key={planName}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-muted)', padding: '8px 4px 3px', borderBottom: '1px solid var(--border)', marginBottom: 2 }}>
                    {planName} <span style={{ fontWeight: 400, color: 'var(--text-subtle, #94a3b8)' }}>({list.length})</span>
                  </div>
                  {list.map((z) => {
                    const st = chipStyleFor(z);
                    return (
                      <button
                        key={z.id}
                        onClick={(e) => {
                          onSelect(z.id, e.shiftKey || e.metaKey || e.ctrlKey);
                          if (!(e.shiftKey || e.metaKey || e.ctrlKey)) setOpen(false);
                        }}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '5px 8px', margin: '2px 0', borderRadius: 8, fontSize: 11,
                          border: `${st.borderWidth}px solid ${st.borderColor}`,
                          background: st.background, color: st.color, fontWeight: st.fontWeight,
                          cursor: 'pointer',
                        }}
                        title={`${z.name} — ${z.status}`}
                      >
                        {st.prefix}{z.name}
                      </button>
                    );
                  })}
                </div>
              ));
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

// Add all app-specific sources + layers onto a loaded map style.
// Called on initial load and again after each basemap swap.
function addAppLayers(map: maplibregl.Map) {
  // Buildings
  if (!map.getSource('buildings')) {
    map.addSource('buildings', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'buildings-fill', type: 'fill', source: 'buildings',
      paint: {
        'fill-color': ['case', ['get', 'served'], '#10b981', '#facc15'] as any,
        'fill-opacity': 0.35,
      },
    });
    map.addLayer({
      id: 'buildings-stroke', type: 'line', source: 'buildings',
      paint: { 'line-color': '#334155', 'line-width': 0.6, 'line-opacity': 0.7 },
    });
  }
  // Passings buffer (translucent) + passing BSL dots
  if (!map.getSource('passings-buffer')) {
    map.addSource('passings-buffer', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    // Hidden 2026-05-27 — was painting a giant cyan stripe across every
    // BSL tied to the plant, ugly at any zoom. Source kept so any
    // downstream code that calls getSource('passings-buffer') still works.
    map.addLayer({
      id: 'passings-buffer-fill', type: 'fill', source: 'passings-buffer',
      paint: { 'fill-color': '#22d3ee', 'fill-opacity': 0 },
    });
    map.addLayer({
      id: 'passings-buffer-outline', type: 'line', source: 'passings-buffer',
      paint: { 'line-color': '#0891b2', 'line-width': 0, 'line-opacity': 0 },
    });
  }
  if (!map.getSource('passing-dots')) {
    map.addSource('passing-dots', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'passing-dots', type: 'circle', source: 'passing-dots',
      paint: {
        // Larger and brighter when BEAD-eligible
        'circle-radius': ['case', ['get', 'eligible'], 5, 3] as any,
        'circle-color': ['case',
          ['get', 'eligible'], '#10b981',     // green = BEAD eligible (Layer 1)
          '#f59e0b'                           // amber = opportunistic (Layer 2)
        ] as any,
        'circle-opacity': 0.9,
        'circle-stroke-color': ['case', ['get', 'eligible'], '#065f46', '#92400e'] as any,
        'circle-stroke-width': 1,
      },
    });
  }
  // Polygon draft (for design flow)
  if (!map.getSource('polygon')) {
    map.addSource('polygon', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'polygon-fill', type: 'fill', source: 'polygon',
      filter: ['==', '$type', 'Polygon'] as any,
      paint: { 'fill-color': '#8b5cf6', 'fill-opacity': 0.12 },
    });
    map.addLayer({
      id: 'polygon-outline', type: 'line', source: 'polygon',
      paint: { 'line-color': '#8b5cf6', 'line-width': 3 },
    });
    map.addLayer({
      id: 'polygon-pts', type: 'circle', source: 'polygon',
      filter: ['==', '$type', 'Point'] as any,
      paint: { 'circle-radius': 5, 'circle-color': '#8b5cf6', 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 },
    });
  }
  // OLT placement marker
  if (!map.getSource('olt-marker')) {
    map.addSource('olt-marker', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'olt-marker-halo', type: 'circle', source: 'olt-marker',
      paint: { 'circle-radius': 18, 'circle-color': '#f59e0b', 'circle-opacity': 0.25 },
    });
    map.addLayer({
      id: 'olt-marker', type: 'circle', source: 'olt-marker',
      paint: { 'circle-radius': 10, 'circle-color': '#f59e0b', 'circle-stroke-color': '#111827', 'circle-stroke-width': 3 },
    });
  }
  // Segments
  if (!map.getSource('segments')) {
    map.addSource('segments', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'segments-casing', type: 'line', source: 'segments',
      paint: { 'line-color': '#ffffff', 'line-width': 3, 'line-opacity': 0.15 },
      layout: { 'line-cap': 'round', 'line-join': 'round' },
    });
    map.addLayer({
      id: 'segments', type: 'line', source: 'segments',
      paint: segmentPaint() as any,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
    });
    map.addLayer({
      id: 'segments-selected', type: 'line', source: 'segments',
      paint: { 'line-color': '#0ea5e9', 'line-width': 8, 'line-opacity': 0.45 },
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      filter: ['==', ['id'], ''] as any,
    });
  }
  // Nodes
  if (!map.getSource('nodes')) {
    map.addSource('nodes', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'nodes', type: 'circle', source: 'nodes',
      paint: nodeCirclePaint() as any,
    });
    map.addLayer({
      id: 'nodes-labels', type: 'symbol', source: 'nodes',
      layout: {
        'text-field': ['get', 'auto_label'] as any,
        'text-size': 10,
        'text-anchor': 'left',
        'text-offset': [0.8, 0] as any,
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': '#0b1220',
        'text-halo-color': '#fff',
        'text-halo-width': 1.8,
      },
      minzoom: 14,
    });
    map.addLayer({
      id: 'nodes-selected', type: 'circle', source: 'nodes',
      paint: {
        'circle-radius': 12, 'circle-color': 'transparent',
        'circle-stroke-color': '#0ea5e9', 'circle-stroke-width': 3,
      },
      filter: ['==', ['id'], ''] as any,
    });
  }
  // Drops
  if (!map.getSource('drops')) {
    map.addSource('drops', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'drops', type: 'circle', source: 'drops',
      paint: {
        'circle-radius': 3, 'circle-color': '#06b6d4',
        'circle-stroke-color': '#fff', 'circle-stroke-width': 1,
      },
    });
  }
  // 2026-05-27: dedicated sources for the committed-plan full snapshot.
  // These render the saved fiber design of the active plan and are NOT
  // gated by the layerVis.plant toggle (which is for the existing plant
  // graph, a different concept). Source IDs intentionally distinct so
  // they don't conflict with the per-zone activeZoneDesign sources.
  if (!map.getSource('plan-segments')) {
    map.addSource('plan-segments', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'plan-segments-casing', type: 'line', source: 'plan-segments',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#000', 'line-opacity': 0.35, 'line-width': 3.5 },
    });
    map.addLayer({
      id: 'plan-segments', type: 'line', source: 'plan-segments',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: segmentPaint() as any,
    });
  }
  if (!map.getSource('plan-nodes')) {
    map.addSource('plan-nodes', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'plan-nodes', type: 'circle', source: 'plan-nodes',
      paint: nodeCirclePaint() as any,
    });
  }
  if (!map.getSource('plan-drops')) {
    map.addSource('plan-drops', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'plan-drops', type: 'circle', source: 'plan-drops',
      paint: {
        'circle-radius': 2.5, 'circle-color': '#06b6d4',
        'circle-stroke-color': '#fff', 'circle-stroke-width': 0.8,
        'circle-opacity': 0.85,
      },
    });
  }
  // Draft segment
  if (!map.getSource('draft')) {
    map.addSource('draft', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'draft-line', type: 'line', source: 'draft',
      filter: ['==', '$type', 'LineString'] as any,
      paint: { 'line-color': '#0ea5e9', 'line-width': 3, 'line-dasharray': [1, 1.4] },
    });
    map.addLayer({
      id: 'draft-pts', type: 'circle', source: 'draft',
      filter: ['==', '$type', 'Point'] as any,
      paint: { 'circle-radius': 4, 'circle-color': '#0ea5e9', 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 },
    });
  }
  // NOTE: a legacy towers source ('towers') + layers used to be added here. It was
  // created empty and never fed, but it claimed the layer id 'towers-dot' first — so
  // the REAL FCC ASR layer below (source 'towers-source', fed by the bbox fetch effect)
  // threw "Layer towers-dot already exists" and never rendered. Towers silently never
  // appeared. The dead layer is removed so the live one owns the id.
  // Head-end marker (one per territory)
  if (!map.getSource('headend-src')) {
    map.addSource('headend-src', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'headend-glow', type: 'circle', source: 'headend-src',
      paint: { 'circle-radius': 28, 'circle-color': '#f59e0b', 'circle-opacity': 0.35, 'circle-blur': 0.7 },
    });
    map.addLayer({
      id: 'headend-ring', type: 'circle', source: 'headend-src',
      paint: { 'circle-radius': 14, 'circle-color': 'rgba(0,0,0,0)', 'circle-stroke-color': '#f59e0b', 'circle-stroke-width': 3 },
    });
    map.addLayer({
      id: 'headend-dot', type: 'circle', source: 'headend-src',
      paint: { 'circle-radius': 9, 'circle-color': '#f59e0b', 'circle-stroke-color': '#fff', 'circle-stroke-width': 2.5 },
    });
    map.addLayer({
      id: 'headend-label', type: 'symbol', source: 'headend-src',
      layout: { 'text-field': 'HEAD-END', 'text-size': 11, 'text-offset': [0, -2] as any, 'text-allow-overlap': true, 'text-anchor': 'center' as any, 'text-font': ['Noto Sans Bold'] as any },
      paint: { 'text-color': '#fff', 'text-halo-color': '#0b1220', 'text-halo-width': 2.5 },
    });
  }
  // Gateway tower markers — one per cluster, where backhaul lands on the tower base
  if (!map.getSource('gateway-src')) {
    map.addSource('gateway-src', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'gateway-dot', type: 'circle', source: 'gateway-src',
      paint: { 'circle-radius': 6, 'circle-color': '#facc15', 'circle-stroke-color': '#713f12', 'circle-stroke-width': 2 },
    });
    map.addLayer({
      id: 'gateway-label', type: 'symbol', source: 'gateway-src',
      layout: { 'text-field': ['concat', '📡 ', ['get', 'city']] as any, 'text-size': 9, 'text-offset': [0, 1.1] as any, 'text-allow-overlap': false, 'text-anchor': 'top' as any },
      paint: { 'text-color': '#fff', 'text-halo-color': '#713f12', 'text-halo-width': 1.8 },
    });
  }
  // POP markers (one per cluster when pop_point is set)
  if (!map.getSource('pops-src')) {
    map.addSource('pops-src', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'pops-dot', type: 'circle', source: 'pops-src',
      paint: { 'circle-radius': 7, 'circle-color': '#06b6d4', 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 },
    });
    map.addLayer({
      id: 'pops-label', type: 'symbol', source: 'pops-src',
      layout: { 'text-field': ['concat', 'POP · ', ['get', 'name']] as any, 'text-size': 10, 'text-offset': [0, 1.2] as any, 'text-allow-overlap': false, 'text-anchor': 'top' as any },
      paint: { 'text-color': '#fff', 'text-halo-color': '#0b1220', 'text-halo-width': 2 },
    });
  }
  // BEAD awarded BSL dots (visible when the operator BEAD toggle is on)
  if (!map.getSource('bead-bsl-source')) {
    map.addSource('bead-bsl-source', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'bead-bsl-dot', type: 'circle', source: 'bead-bsl-source',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 1.5, 10, 2.5, 14, 4],
        'circle-color': '#22c55e',
        'circle-stroke-color': '#064e3b',
        'circle-stroke-width': 0.5,
        'circle-opacity': 0.85,
      },
    });
  }
  // FCC ASR towers (visible when Towers checkbox is on). Orange so visually
  // distinct from BEAD green + RDOF cyan dots. Larger marker because towers
  // are physical infrastructure not location points.
  if (!map.getSource('towers-source')) {
    map.addSource('towers-source', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'towers-dot', type: 'circle', source: 'towers-source',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 3, 10, 5, 14, 7],
        'circle-color': '#ea580c',
        'circle-stroke-color': '#7c2d12',
        'circle-stroke-width': 1.5,
        'circle-opacity': 0.9,
      },
      layout: { visibility: 'none' },
    });
    map.addLayer({
      id: 'towers-label', type: 'symbol', source: 'towers-source',
      layout: {
        'text-field': ['concat', ['to-string', ['get', 'overall_ft']], 'ft'],
        'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
        'text-size': 10,
        'text-offset': [0, 1.4],
        'text-anchor': 'top',
        'text-optional': true,
        visibility: 'none',
      },
      paint: {
        'text-color': '#7c2d12',
        'text-halo-color': '#fff7ed',
        'text-halo-width': 1.5,
      },
      minzoom: 11,
    });
  }
  // Aristotle RDOF location dots (visible when the operator RDOF toggle is on).
  // Different color (cyan) so they're visually distinct from BEAD green dots.
  if (!map.getSource('rdof-bsl-source')) {
    map.addSource('rdof-bsl-source', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'rdof-bsl-dot', type: 'circle', source: 'rdof-bsl-source',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 1.5, 10, 2.5, 14, 4],
        'circle-color': '#06b6d4',
        'circle-stroke-color': '#155e75',
        'circle-stroke-width': 0.5,
        'circle-opacity': 0.85,
      },
      layout: { visibility: 'none' },
    });
  }
  // the operator BEAD project polygon overlay removed on 2026-04-20 — we
  // now have real BSL points for WY/NE (and CO pending geometry load)
  // rendered via the bead-bsl-dot layer below. The the operator BEAD
  // checkbox in the map corner still toggles those dots.
  // Territory polygon (analyzed plan's outline)
  if (!map.getSource('territory-poly')) {
    map.addSource('territory-poly', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({ id: 'territory-poly-fill', type: 'fill', source: 'territory-poly', paint: { 'fill-color': '#8b5cf6', 'fill-opacity': 0.06 } });
    map.addLayer({ id: 'territory-poly-outline', type: 'line', source: 'territory-poly', paint: { 'line-color': '#8b5cf6', 'line-width': 2, 'line-dasharray': [4, 2] } });
  }
  // Cluster centroid markers (from territory plan)
  if (!map.getSource('clusters')) {
    map.addSource('clusters', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'clusters-circle', type: 'circle', source: 'clusters',
      paint: {
        'circle-radius': ['case', ['boolean', ['get', 'selected'], false], 14, 10] as any,
        'circle-color': ['case', ['boolean', ['get', 'selected'], false], '#8b5cf6', '#64748b'] as any,
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 2,
        'circle-opacity': 0.85,
      },
    });
    map.addLayer({
      id: 'clusters-label', type: 'symbol', source: 'clusters',
      layout: { 'text-field': ['get', 'label'] as any, 'text-size': 11, 'text-allow-overlap': true, 'text-ignore-placement': true },
      paint: { 'text-color': '#fff', 'text-halo-color': '#0b1220', 'text-halo-width': 2 },
    });
  }
  // Backbone hybrid routes (colored by mode: fiber/wireless/ride-along)
  if (!map.getSource('backbone')) {
    map.addSource('backbone', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({ id: 'backbone-casing', type: 'line', source: 'backbone',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': '#000',
        'line-opacity': 0.5,
        'line-width': ['interpolate', ['linear'], ['coalesce', ['get', 'load'], 1], 1, 10, 3, 14, 6, 20] as any,
      }
    });
    map.addLayer({ id: 'backbone-line', type: 'line', source: 'backbone',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': ['match', ['get', 'mode'],
          'fiber_build',  '#f5ff00',
          'wireless_ptp', '#06b6d4',
          'ride_along',   '#a3e635',
          'unknown',      '#94a3b8',
          '#f5ff00'] as any,
        // Dominant yellow: load=1 → 7 px, load=6+ → 16 px
        'line-width': ['interpolate', ['linear'], ['coalesce', ['get', 'load'], 1], 1, 7, 3, 11, 6, 16] as any,
        'line-opacity': 1,
      }
    });
  }
  // Combined FTTH distribution fiber across selected clusters
  if (!map.getSource('ftth')) {
    map.addSource('ftth', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({ id: 'ftth-line', type: 'line', source: 'ftth', paint: {
      'line-color': ['match', ['get', 'type'], 'backbone', '#a855f7', 'distribution', '#06b6d4', 'drop', '#facc15', '#06b6d4'] as any,
      'line-width': ['match', ['get', 'type'], 'backbone', 2.5, 'distribution', 1.6, 'drop', 1.1, 1.4] as any,
      'line-opacity': 0.9,
    }});
  }
  // Territory draft (in-progress territory polygon draw)
  if (!map.getSource('territory-draft')) {
    map.addSource('territory-draft', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({ id: 'territory-draft-fill', type: 'fill', source: 'territory-draft', filter: ['==', '$type', 'Polygon'] as any, paint: { 'fill-color': '#06b6d4', 'fill-opacity': 0.22 } });
    map.addLayer({ id: 'territory-draft-line', type: 'line', source: 'territory-draft', filter: ['==', '$type', 'LineString'] as any, paint: { 'line-color': '#06b6d4', 'line-width': 4 } });
    map.addLayer({ id: 'territory-draft-pts', type: 'circle', source: 'territory-draft', filter: ['==', '$type', 'Point'] as any, paint: { 'circle-radius': 6, 'circle-color': '#06b6d4', 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 } });
  }

  // Territory polygon EDIT overlay: preview + draggable vertex handles.
  // Purple palette to distinguish from the amber zone-edit overlay.
  if (!map.getSource('territory-edit')) {
    map.addSource('territory-edit', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'territory-edit-fill', type: 'fill', source: 'territory-edit',
      filter: ['==', '$type', 'Polygon'] as any,
      paint: { 'fill-color': '#8b5cf6', 'fill-opacity': 0.10 },
    });
    map.addLayer({
      id: 'territory-edit-outline', type: 'line', source: 'territory-edit',
      filter: ['==', '$type', 'Polygon'] as any,
      paint: { 'line-color': '#8b5cf6', 'line-width': 3 },
    });
    map.addLayer({
      id: 'territory-edit-verts', type: 'circle', source: 'territory-edit',
      filter: ['==', '$type', 'Point'] as any,
      paint: {
        'circle-radius': 10,
        'circle-color': '#fff',
        'circle-stroke-color': '#8b5cf6',
        'circle-stroke-width': 4,
      },
    });
  }

  // Zone-boundary EDIT overlay: polygon preview + draggable vertex handles.
  if (!map.getSource('expansion-edit')) {
    map.addSource('expansion-edit', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'expansion-edit-fill', type: 'fill', source: 'expansion-edit',
      filter: ['==', '$type', 'Polygon'] as any,
      paint: { 'fill-color': '#f59e0b', 'fill-opacity': 0.12 },
    });
    map.addLayer({
      id: 'expansion-edit-outline', type: 'line', source: 'expansion-edit',
      filter: ['==', '$type', 'Polygon'] as any,
      paint: { 'line-color': '#f59e0b', 'line-width': 3 },
    });
    map.addLayer({
      id: 'expansion-edit-verts', type: 'circle', source: 'expansion-edit',
      filter: ['==', '$type', 'Point'] as any,
      paint: {
        'circle-radius': 8,
        'circle-color': '#fff',
        'circle-stroke-color': '#f59e0b',
        'circle-stroke-width': 3,
      },
    });
  }

  // In-progress expansion-study draft: isolated source so the draw never
  // clobbers the saved active-zone polygon.
  if (!map.getSource('expansion-draft')) {
    map.addSource('expansion-draft', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'expansion-draft-fill', type: 'fill', source: 'expansion-draft',
      filter: ['==', '$type', 'Polygon'] as any,
      paint: { 'fill-color': '#8b5cf6', 'fill-opacity': 0.14 },
    });
    map.addLayer({
      id: 'expansion-draft-line', type: 'line', source: 'expansion-draft',
      filter: ['==', '$type', 'LineString'] as any,
      paint: { 'line-color': '#8b5cf6', 'line-width': 3, 'line-dasharray': [4, 2] },
    });
    map.addLayer({
      id: 'expansion-draft-pts', type: 'circle', source: 'expansion-draft',
      filter: ['==', '$type', 'Point'] as any,
      paint: { 'circle-radius': 5, 'circle-color': '#8b5cf6', 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 },
    });
  }

  // ALL expansion zones (inactive ones dimmed) so the user can click any to
  // activate it. Sits below the active-zone layers.
  if (!map.getSource('expansion-all-zones')) {
    map.addSource('expansion-all-zones', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'expansion-all-zones-fill', type: 'fill', source: 'expansion-all-zones',
      paint: {
        'fill-color': '#a855f7',
        'fill-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.22, 0.10] as any,
      },
    });
    map.addLayer({
      id: 'expansion-all-zones-outline', type: 'line', source: 'expansion-all-zones',
      paint: {
        'line-color': '#a855f7',
        'line-width': ['case', ['boolean', ['feature-state', 'hover'], false], 2.5, 1.5] as any,
        'line-dasharray': [2, 2],
        'line-opacity': 0.8,
      },
    });
    map.addLayer({
      id: 'expansion-all-zones-label', type: 'symbol', source: 'expansion-all-zones',
      layout: {
        'text-field': ['get', 'name'] as any,
        'text-size': 10,
        'text-anchor': 'center' as any,
      },
      paint: {
        'text-color': '#fff',
        'text-halo-color': '#0b1220',
        'text-halo-width': 2,
      },
    });
  }

  // Zone polygon (in-progress or completed).
  if (!map.getSource('expansion-polygon')) {
    map.addSource('expansion-polygon', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'expansion-polygon-fill', type: 'fill', source: 'expansion-polygon',
      filter: ['==', '$type', 'Polygon'] as any,
      paint: { 'fill-color': '#0ea5e9', 'fill-opacity': 0.10 },
    });
    map.addLayer({
      id: 'expansion-polygon-outline', type: 'line', source: 'expansion-polygon',
      paint: { 'line-color': '#0ea5e9', 'line-width': 3, 'line-dasharray': [3, 2] },
    });
    map.addLayer({
      id: 'expansion-polygon-pts', type: 'circle', source: 'expansion-polygon',
      filter: ['==', '$type', 'Point'] as any,
      paint: { 'circle-radius': 5, 'circle-color': '#0ea5e9', 'circle-stroke-color': '#fff', 'circle-stroke-width': 2 },
    });
  }
  // Extension plant segments (drawn by zone analyze). Dashed to distinguish from committed plant.
  if (!map.getSource('expansion-segments')) {
    map.addSource('expansion-segments', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'expansion-segments-casing', type: 'line', source: 'expansion-segments',
      paint: { 'line-color': '#ffffff', 'line-width': 6, 'line-opacity': 0.5 },
      layout: { 'line-cap': 'round', 'line-join': 'round' },
    });
    map.addLayer({
      id: 'expansion-segments', type: 'line', source: 'expansion-segments',
      paint: {
        'line-color': '#0ea5e9',
        'line-width': 3,
        'line-dasharray': [2, 1.5],
        'line-opacity': 0.95,
      } as any,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
    });
  }
  // Extension plant nodes (vaults, closures, etc.) for the active zone.
  if (!map.getSource('expansion-nodes')) {
    map.addSource('expansion-nodes', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'expansion-nodes', type: 'circle', source: 'expansion-nodes',
      paint: {
        'circle-radius': 4,
        'circle-color': '#0ea5e9',
        'circle-stroke-color': '#fff',
        'circle-stroke-width': 1.5,
      },
    });
  }
  // Zone passings split by served status.
  //   green  = served / private-$ opportunity
  //   blue   = BEAD-eligible
  //   amber  = unknown / addressable
  // along_route_* variants render smaller so the zone reads as primary.
  if (!map.getSource('expansion-dots')) {
    map.addSource('expansion-dots', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } } as any);
    map.addLayer({
      id: 'expansion-dots', type: 'circle', source: 'expansion-dots',
      paint: {
        'circle-radius': ['match', ['get', 'source'],
          'along_route', 3,
          /* default in-zone */ 4,
        ] as any,
        'circle-color': ['match', ['get', 'served_status'],
          'served', '#10b981',
          'along_route_served', '#10b981',
          'bead_eligible', '#0ea5e9',
          'along_route_bead_eligible', '#0ea5e9',
          'unknown', '#f59e0b',
          'along_route_unknown', '#f59e0b',
          /* default */ '#f59e0b',
        ] as any,
        'circle-opacity': ['match', ['get', 'source'],
          'along_route', 0.7,
          /* default in-zone */ 0.92,
        ] as any,
        'circle-stroke-color': '#0b1220',
        'circle-stroke-width': 1,
      },
    });
  }
}
